import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as THREE from 'three';
import {
  X, ChevronRight, ChevronLeft, TrendingUp, Search, ShoppingCart, Zap, Hash,
  Shield, Brain, CheckCircle, Layers, BarChart2, Calendar, Target,
  IndianRupee, Wallet, Activity, PieChart, Edit, Award, Star,
  ScanLine, FileText, History, Map, Globe, FlaskConical, BookOpen,
  Users, Calculator, MessageSquare, Clock,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { GUIDE_NODES, GUIDE_ZONES } from '../data/guideNodes';

// ─── Icon map ─────────────────────────────────────────────────────────────────
const ICON_MAP = {
  TrendingUp, Search, ShoppingCart, Zap, Hash, Shield, Brain, CheckCircle,
  Layers, BarChart2, Calendar, Target, IndianRupee, Wallet, Activity,
  PieChart, Edit, Award, Star, ScanLine, FileText, History, Map, Globe,
  FlaskConical, BookOpen, Users, Calculator, MessageSquare, Clock,
};

// ─── Sphere math (shared) ─────────────────────────────────────────────────────
function latLonToVec3(lat, lon, radius) {
  const phi   = ((90 - lat) * Math.PI) / 180;
  const theta = ((lon + 180) * Math.PI) / 180;
  return new THREE.Vector3(
    -radius * Math.sin(phi) * Math.cos(theta),
     radius * Math.cos(phi),
     radius * Math.sin(phi) * Math.sin(theta),
  );
}

// ─── Three.js Orb ─────────────────────────────────────────────────────────────
const ORB_R = 1.45;   // Three.js units (camera at z=4.5 gives ~360px apparent size)
const MERIDIAN_ANGLES = [0, 30, 60, 90, 120, 150];
const ZONE_BOUNDS     = [-108, -36, 36, 108, 180];

function makeCircle(radius, segments = 128) {
  const pts = [];
  for (let i = 0; i <= segments; i++) {
    const a = (i / segments) * Math.PI * 2;
    pts.push(new THREE.Vector3(Math.cos(a) * radius, Math.sin(a) * radius, 0));
  }
  const geo = new THREE.BufferGeometry().setFromPoints(pts);
  return geo;
}

function GuideOrb({ onSelect, activeNodeId }) {
  const canvasRef   = useRef(null);
  const stateRef    = useRef({ rotY: 0.35, rotX: -0.17, dragging: false, lastX: 0, lastY: 0, velX: 0, velY: 0 });
  const rendererRef = useRef(null);
  const sceneRef    = useRef(null);
  const groupRef    = useRef(null);
  const cameraRef   = useRef(null);
  const rafRef      = useRef(null);
  const labelDataRef = useRef([]);   // { node, div, vec3 }
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  // ── Build scene once ──
  useEffect(() => {
    const canvas = canvasRef.current;
    const SIZE = 360;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(SIZE, SIZE);
    renderer.setClearColor(0x000000, 0);
    rendererRef.current = renderer;

    const scene = new THREE.Scene();
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    camera.position.z = 4.5;
    cameraRef.current = camera;

    const group = new THREE.Group();
    scene.add(group);
    groupRef.current = group;

    // Wire colour — single indigo for all meridians
    const wireMat = new THREE.LineBasicMaterial({ color: 0x6366f1, transparent: true, opacity: 0.6 });
    const zoneMat = new THREE.LineBasicMaterial({ color: 0x6366f1, transparent: true, opacity: 0.6 });

    // Meridian grid lines
    MERIDIAN_ANGLES.forEach(deg => {
      const geo  = makeCircle(ORB_R);
      const line = new THREE.LineLoop(geo, wireMat);
      line.rotation.y = (deg * Math.PI) / 180;
      group.add(line);
    });

    // Zone boundary meridians
    ZONE_BOUNDS.forEach(deg => {
      const geo  = makeCircle(ORB_R);
      const line = new THREE.LineLoop(geo, zoneMat);
      line.rotation.y = (deg * Math.PI) / 180;
      group.add(line);
    });

    // Dark inner sphere
    const innerGeo = new THREE.SphereGeometry(ORB_R * 0.995, 64, 64);
    const innerMat = new THREE.MeshPhongMaterial({
      color: 0x030310,
      transparent: false,
      shininess: 80,
      specular: new THREE.Color(0x3333aa),
      side: THREE.FrontSide,
    });
    const inner = new THREE.Mesh(innerGeo, innerMat);
    group.add(inner);

    // Rim light
    const rimLight = new THREE.DirectionalLight(0x6366f1, 3);
    rimLight.position.set(-2, 2, -3);
    scene.add(rimLight);

    const ambLight = new THREE.AmbientLight(0x1a1a3a, 2);
    scene.add(ambLight);

    const frontLight = new THREE.PointLight(0x4444cc, 1.5, 10);
    frontLight.position.set(1, 1, 4);
    scene.add(frontLight);

    // ── Orbiting particles ──
    const ORBIT_COUNT = 38;
    const orbitColors = [0x6366f1, 0x16C784, 0xf97316, 0x06b6d4, 0x8b5cf6, 0xf59e0b, 0xffffff];
    const particles = [];

    for (let i = 0; i < ORBIT_COUNT; i++) {
      const color = orbitColors[i % orbitColors.length];
      const size  = 0.008 + Math.random() * 0.012;
      const geo   = new THREE.SphereGeometry(size, 6, 6);
      const mat   = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.4 + Math.random() * 0.35 });
      const mesh  = new THREE.Mesh(geo, mat);

      // Each particle orbits on a tilted elliptical ring
      const orbitRadius  = ORB_R * (1.25 + Math.random() * 0.9);
      const orbitTiltX   = (Math.random() - 0.5) * Math.PI;
      const orbitTiltZ   = (Math.random() - 0.5) * Math.PI;
      const speed        = (0.0008 + Math.random() * 0.0015) * (Math.random() < 0.5 ? 1 : -1);
      const phase        = Math.random() * Math.PI * 2;
      const eccentricity = 0.2 + Math.random() * 0.5;              // ellipse stretch

      scene.add(mesh);
      particles.push({ mesh, orbitRadius, orbitTiltX, orbitTiltZ, speed, phase, eccentricity });
    }

    // Store particles on renderer so animate loop can access them
    rendererRef._particles = particles;

    return () => {
      renderer.dispose();
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // ── Render loop + HTML label projection ──
  useEffect(() => {
    const renderer = rendererRef.current;
    const scene    = sceneRef.current;
    const group    = groupRef.current;
    const camera   = cameraRef.current;
    if (!renderer || !scene || !group || !camera) return;

    const s = stateRef.current;

    function animate() {
      rafRef.current = requestAnimationFrame(animate);

      // Auto-rotate when idle
      if (!s.dragging) {
        s.velX *= 0.95;
        s.velY *= 0.95;
        s.rotY += s.velX + 0.003;
        s.rotX += s.velY;
        s.rotX = Math.max(-0.7, Math.min(0.7, s.rotX));
      }

      group.rotation.y = s.rotY;
      group.rotation.x = s.rotX;

      // Animate orbiting particles
      const particles = rendererRef._particles;
      if (particles) {
        const t = performance.now() * 0.001;
        particles.forEach(p => {
          const angle = p.phase + t * p.speed * 60;
          // Elliptical orbit in local XZ plane
          const x = Math.cos(angle) * p.orbitRadius;
          const z = Math.sin(angle) * p.orbitRadius * (1 - p.eccentricity * 0.5);
          const y = 0;
          // Apply tilt rotations
          const cosX = Math.cos(p.orbitTiltX), sinX = Math.sin(p.orbitTiltX);
          const cosZ = Math.cos(p.orbitTiltZ), sinZ = Math.sin(p.orbitTiltZ);
          // Rotate around X axis
          const y1 = y * cosX - z * sinX;
          const z1 = y * sinX + z * cosX;
          // Rotate around Z axis
          const x2 = x * cosZ - y1 * sinZ;
          const y2 = x * sinZ + y1 * cosZ;
          p.mesh.position.set(x2, y2, z1);
          // Pulse opacity
          p.mesh.material.opacity = 0.4 + 0.4 * Math.sin(t * 1.5 + p.phase);
        });
      }

      renderer.render(scene, camera);

      // Update group matrix so we can use it for label projection
      group.updateMatrixWorld(true);

      // Project node world positions → screen space → position HTML labels
      labelDataRef.current.forEach(({ vec3, div, nodeId }) => {
        // Apply the group's actual world matrix — exact same transform as the geometry
        const worldPos = vec3.clone().applyMatrix4(group.matrixWorld);
        const proj = worldPos.clone().project(camera);
        const SIZE = 360;
        const x = (proj.x *  0.5 + 0.5) * SIZE;
        const y = (proj.y * -0.5 + 0.5) * SIZE;

        const depth = worldPos.z;
        const front = depth >= 0;
        const opacity = front ? Math.max(0.25, depth / ORB_R) : Math.max(0.08, 0.15 + (depth / ORB_R) * 0.1);

        div.style.left    = `${x}px`;
        div.style.top     = `${y}px`;
        div.style.opacity = opacity;
        div.style.zIndex  = front ? Math.round(depth * 100 + 150) : 1;
        // disable clicks on back-hemisphere nodes via the inner circle
        const inner = div.querySelector('[data-nodeid]');
        if (inner) inner.style.pointerEvents = front ? 'auto' : 'none';
      });
    }

    animate();
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // ── Build HTML label overlays for nodes ──
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Remove old labels
    labelDataRef.current.forEach(({ div }) => div.remove());
    labelDataRef.current = [];

    GUIDE_NODES.forEach(node => {
      const vec3 = latLonToVec3(node.position.lat, node.position.lon, ORB_R);

      const div = document.createElement('div');
      div.style.cssText = `
        position: absolute;
        transform: translate(-50%, -50%);
        pointer-events: none;
        cursor: pointer;
        transition: opacity 0.15s;
      `;

      const isActive = node.id === activeNodeId;
      const w = isActive ? 56 : 46;
      const c = node.color;
      div.innerHTML = `
        <div data-nodeid="${node.id}" style="
          width:${w}px; height:${w}px; border-radius:50%;
          background: ${isActive ? c : `${c}26`};
          border: 2px solid ${c}${isActive ? '' : 'bb'};
          box-shadow: ${isActive ? `0 0 20px ${c}cc` : `0 0 8px ${c}55`};
          display:flex; align-items:center; justify-content:center;
          transition: all 0.2s;
          pointer-events: auto;
          cursor: pointer;
        ">
          <span style="
            font-size:7px; font-weight:800; line-height:1.2; text-align:center;
            white-space:pre; letter-spacing:-0.02em; color:#fff;
            pointer-events:none;
          ">${node.label}</span>
        </div>
      `;

      div.querySelector('[data-nodeid]').addEventListener('click', () => onSelect(node.id));
      container.appendChild(div);
      labelDataRef.current.push({ vec3, div, nodeId: node.id });
    });

    // Zone labels
    GUIDE_ZONES.forEach(zone => {
      const vec3 = latLonToVec3(zone.lat, zone.lon, ORB_R * 1.05);

      const div = document.createElement('div');
      div.style.cssText = `
        position: absolute;
        transform: translate(-50%, -50%);
        pointer-events: none;
        transition: opacity 0.15s;
      `;
      div.innerHTML = `
        <span style="
          font-size:8px; font-weight:800; color:#fff; letter-spacing:0.08em;
          text-transform:uppercase; white-space:nowrap;
          text-shadow: 0 0 8px rgba(99,102,241,1), 0 0 2px #000;
        ">${zone.label}</span>
      `;
      container.appendChild(div);
      labelDataRef.current.push({ vec3, div, nodeId: null });
    });

    return () => {
      labelDataRef.current.forEach(({ div }) => div.remove());
      labelDataRef.current = [];
    };
  }, [activeNodeId, onSelect]);

  // ── Pointer drag ──
  const onPointerDown = useCallback((e) => {
    stateRef.current.dragging = true;
    stateRef.current.lastX = e.clientX;
    stateRef.current.lastY = e.clientY;
    stateRef.current.velX  = 0;
    stateRef.current.velY  = 0;
    containerRef.current?.setPointerCapture?.(e.pointerId);
    setIsDragging(true);
  }, []);

  const onPointerMove = useCallback((e) => {
    const s = stateRef.current;
    if (!s.dragging) return;
    const dx = e.clientX - s.lastX;
    const dy = e.clientY - s.lastY;
    s.velX  = dx * 0.008;
    s.velY  = dy * 0.008;
    s.rotY += dx * 0.008;
    s.rotX  = Math.max(-0.7, Math.min(0.7, s.rotX + dy * 0.008));
    s.lastX = e.clientX;
    s.lastY = e.clientY;
  }, []);

  const onPointerUp = useCallback(() => {
    stateRef.current.dragging = false;
    setIsDragging(false);
  }, []);

  return (
    <div
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
      style={{
        width: 360, height: 360,
        position: 'relative',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        touchAction: 'none',
        flexShrink: 0,
      }}
    >
      {/* Canvas — the Three.js scene, glow via filter so it belongs to the sphere */}
      <canvas ref={canvasRef} style={{
        position: 'absolute', top: 0, left: 0,
        filter: 'drop-shadow(0 0 18px rgba(99,102,241,0.55))',
        borderRadius: '50%',
      }} />

      {/* HTML label layer — clipped to orb circle so nodes can't spill outside */}
      <div
        ref={containerRef}
        style={{
          position: 'absolute', top: 0, left: 0,
          width: 360, height: 360,
          borderRadius: '50%',
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
      />

      {/* Outer glass overlay — screen-space gloss only, no hard CSS ring */}
      <div style={{
        position: 'absolute', top: 0, left: 0,
        width: 360, height: 360,
        borderRadius: '50%',
        background: 'radial-gradient(circle at 38% 35%, rgba(255,255,255,0.07) 0%, rgba(99,102,241,0.08) 40%, transparent 65%)',
        pointerEvents: 'none',
      }} />
    </div>
  );
}

// ─── Step UI mini-mockup renderer ────────────────────────────────────────────
const REASON_EXPLAINERS = {
  Technical:   { color: '#6366f1', icon: '📐', example: 'Breakout above resistance on 15m chart. Entry is level-based, not emotional — exactly what discipline demands.' },
  Scanner:     { color: '#06b6d4', icon: '🔍', example: 'A Volume Surge or EMA crossover scanner alert triggered this. You\'re following a systematic signal.' },
  Fundamental: { color: '#16C784', icon: '📊', example: 'Strong earnings or sector tailwind. You\'re trading with business logic and data behind you.' },
  Sentiment:   { color: '#f59e0b', icon: '🌊', example: 'FII buying or bullish market breadth supports this direction. You\'re aligned with macro flow.' },
  Tip:         { color: '#f97316', icon: '💬', example: 'Someone suggested this trade. Only valid if you\'ve verified the setup yourself — never blindly follow.' },
  Impulse:     { color: '#ef4444', icon: '⚠️', example: 'No real reason — just a gut feeling or FOMO. Selecting this flags the trade as undisciplined, lowers your Discipline Score, and appears in your AI coach review. Honest, but costly.' },
  Strategy:    { color: '#8b5cf6', icon: '♟️', example: 'Part of a defined trading strategy or playbook. Entry, exit and sizing are all pre-planned.' },
};

function StepUI({ ui, color }) {
  const [selectedReason, setSelectedReason] = React.useState(null);
  const [selectedSide, setSelectedSide] = React.useState(null);
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  const [selectedMode, setSelectedMode] = React.useState(null);
  const [selectedField, setSelectedField] = React.useState(null);
  const [selectedTag, setSelectedTag] = React.useState(null);
  const [selectedStock, setSelectedStock] = React.useState(false);
  const [selCond, setSelCond] = React.useState(null);
  const [selMetric, setSelMetric] = React.useState(null);
  const [selAlgo, setSelAlgo] = React.useState(null);

  const INDIGO   = '#6366f1';
  const GREEN    = '#16C784';
  const RED      = '#ef4444';
  const PURPLE   = '#7c3aed';
  const AMBER    = '#f59e0b';
  const border   = 'rgba(255,255,255,0.10)';
  const text     = '#eceef5';
  const sub      = 'rgba(255,255,255,0.40)';
  const cardBg   = '#13141f';
  const inputBg  = '#1a1b2e';

  const Explainer = ({ color: c, icon, title, body, badge }) => (
    <div style={{ borderRadius: 10, padding: '10px 13px', background: `${c}12`, border: `1.5px solid ${c}35`, marginTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
        <span style={{ fontSize: 14 }}>{icon}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: c }}>{title}</span>
        {badge && <span style={{ fontSize: 9, fontWeight: 700, color: c, background: `${c}20`, borderRadius: 4, padding: '1px 5px' }}>{badge}</span>}
      </div>
      <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.70)', margin: 0, lineHeight: 1.55 }}>{body}</p>
    </div>
  );

  const Card = ({ children, style }) => (
    <div style={{ background: cardBg, borderRadius: 16, border: `1px solid ${border}`, padding: '14px 16px', width: '100%', ...style }}>{children}</div>
  );

  const HintLabel = ({ text: t }) => (
    <div style={{ fontSize: 9, color: sub, fontWeight: 500, marginBottom: 7, letterSpacing: '0.03em' }}>{t}</div>
  );

  // --- SEARCH ---
  if (ui.type === 'search') {
    const info = selectedStock ? {
      icon: '📈', title: 'Stock Result', color: INDIGO,
      body: `LTP (Last Traded Price) is ₹${ui.price.replace('₹','')} — the most recent transaction price. The ${ui.change} shows today's % change from yesterday's close. Tap the row to open the order ticket.`,
    } : null;
    return (
      <Card>
        <HintLabel text="tap the result to learn what each field means" />
        <div style={{ flex: 1, background: inputBg, borderRadius: 10, padding: '9px 12px', border: `1px solid ${border}`, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <Search size={13} color={sub} />
          <span style={{ fontSize: 13, color: INDIGO, fontWeight: 700 }}>{ui.stock}</span>
        </div>
        <div
          onClick={() => setSelectedStock(s => !s)}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: selectedStock ? `${INDIGO}20` : `${INDIGO}10`, borderRadius: 10, border: `1.5px solid ${selectedStock ? INDIGO : `${INDIGO}30`}`, cursor: 'pointer', transition: 'all 0.18s', userSelect: 'none' }}
        >
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: text }}>{ui.stock}</div>
            <div style={{ fontSize: 10, color: sub }}>NSE · Equity</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: text }}>{ui.price}</div>
            <div style={{ fontSize: 11, color: GREEN }}>{ui.change}</div>
          </div>
        </div>
        {info && <Explainer {...info} />}
      </Card>
    );
  }

  // --- BUY / SELL ---
  if (ui.type === 'buysell') {
    const sideInfo = {
      BUY:  { icon: '📈', color: INDIGO, title: 'BUY (Long)',  body: 'You expect the price to go UP. You profit when it rises above your entry. Risk is limited to your stop loss.' },
      SELL: { icon: '📉', color: RED,    title: 'SELL (Short)', body: 'You expect the price to go DOWN. Profit when it falls. Short-selling equity requires margin and carries higher risk.' },
    };
    const info = selectedSide ? sideInfo[selectedSide] : null;
    const activeSide = selectedSide ?? ui.active;
    return (
      <Card>
        <HintLabel text="tap BUY or SELL to understand each side" />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: text }}>{ui.stock}</div>
            <div style={{ fontSize: 10, color: sub }}>Reliance Industries Ltd</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: text }}>{ui.price}</div>
            <div style={{ fontSize: 11, color: GREEN }}>{ui.change}</div>
          </div>
        </div>
        <div style={{ display: 'flex', borderRadius: 10, overflow: 'hidden', border: `1px solid ${border}`, background: inputBg }}>
          {['BUY', 'SELL'].map(s => {
            const isActive = s === activeSide;
            const c = s === 'BUY' ? INDIGO : RED;
            return (
              <div key={s} onClick={() => setSelectedSide(prev => prev === s ? null : s)}
                style={{ flex: 1, padding: '10px 4px', textAlign: 'center', background: isActive ? c : 'transparent', cursor: 'pointer', transition: 'all 0.18s', userSelect: 'none' }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: isActive ? '#fff' : sub }}>{s}</div>
              </div>
            );
          })}
        </div>
        {info && <Explainer {...info} />}
      </Card>
    );
  }

  // --- PRODUCT TYPE ---
  if (ui.type === 'producttype') {
    const productInfo = {
      INTRADAY: { icon: '⚡', color: PURPLE, title: 'Intraday', badge: 'same day', body: 'Position must be closed by 3:20 PM the same day. If unfilled, the limit order expires. Higher leverage available but no overnight positions allowed.' },
      SWING:    { icon: '🌙', color: INDIGO,  title: 'Swing / CNC', badge: 'hold overnight', body: 'Order stays open until filled or you cancel it. You can hold for days or weeks. No forced square-off. Ideal for limit orders where you\'re waiting for price to come to you.' },
    };
    const activeProduct = selectedProduct ?? ui.active;
    const info = productInfo[activeProduct];
    return (
      <Card>
        <HintLabel text="tap to see what each product type means" />
        <div style={{ display: 'flex', borderRadius: 10, overflow: 'hidden', border: `1px solid ${border}`, background: inputBg, marginBottom: info ? 0 : 4 }}>
          {[{ id: 'INTRADAY', label: 'Intraday', note: 'Same day' }, { id: 'SWING', label: 'Swing / CNC', note: 'Hold overnight' }].map(o => {
            const isActive = o.id === activeProduct;
            const c = o.id === 'INTRADAY' ? PURPLE : INDIGO;
            return (
              <div key={o.id} onClick={() => setSelectedProduct(prev => prev === o.id ? null : o.id)}
                style={{ flex: 1, padding: '9px 4px', textAlign: 'center', background: isActive ? c : 'transparent', cursor: 'pointer', transition: 'all 0.18s', userSelect: 'none' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: isActive ? '#fff' : sub }}>{o.label}</div>
                <div style={{ fontSize: 9, color: isActive ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.22)' }}>{o.note}</div>
              </div>
            );
          })}
        </div>
        {info && <Explainer {...info} />}
      </Card>
    );
  }

  // --- ORDER MODE ---
  if (ui.type === 'ordermode') {
    const modeInfo = {
      MARKET: { icon: '⚡', color: INDIGO, title: 'Market Order', body: 'Executes immediately at the best available price. Fast but unpredictable — in volatile markets you may get a price worse than expected (slippage). Use only when you need instant execution.' },
      LIMIT:  { icon: '🎯', color: INDIGO, title: 'Limit Order', badge: 'recommended', body: 'You set the exact price you\'re willing to pay. Order fills only if the market reaches that price. No slippage, full price control — ideal for disciplined entries.' },
    };
    const activeMode = selectedMode ?? ui.active;
    const info = modeInfo[activeMode];
    return (
      <Card>
        <HintLabel text="tap MARKET or LIMIT to compare" />
        <div style={{ display: 'flex', borderRadius: 10, overflow: 'hidden', border: `1px solid ${border}`, background: inputBg, marginBottom: 10 }}>
          {['MARKET', 'LIMIT'].map(m => {
            const isActive = m === activeMode;
            return (
              <div key={m} onClick={() => setSelectedMode(prev => prev === m ? null : m)}
                style={{ flex: 1, padding: '9px 4px', textAlign: 'center', background: isActive ? INDIGO : 'transparent', cursor: 'pointer', transition: 'all 0.18s', userSelect: 'none' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: isActive ? '#fff' : sub }}>{m}</div>
              </div>
            );
          })}
        </div>
        <div style={{ borderRadius: 10, padding: '10px 14px', background: `${INDIGO}18`, border: `1.5px solid ${INDIGO}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: `0 0 12px ${INDIGO}44` }}>
          <span style={{ fontSize: 12, color: '#fff' }}>Limit Price (₹)</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: INDIGO }}>{ui.limitPrice}</span>
        </div>
        <p style={{ fontSize: 10, color: AMBER, margin: '4px 0 0 4px' }}>Fills only when market reaches ₹{ui.limitPrice}</p>
        {info && <Explainer {...info} />}
      </Card>
    );
  }

  // --- QUANTITY ---
  if (ui.type === 'quantity') {
    const fieldInfo = {
      qty:     { icon: '🔢', color: INDIGO, title: 'Shares', body: `You're buying ${ui.qty} shares of RELIANCE. Each share costs ~₹1,340 at your limit price. TradeReflex auto-calculates capital used so you don't have to do the math.` },
      capital: { icon: '💰', color: GREEN,  title: 'Capital Used', body: `₹${ui.capital?.replace('₹','')} is locked from your daily capital limit when you place this order. For a limit order the capital is reserved immediately, even before it fills.` },
    };
    const info = selectedField ? fieldInfo[selectedField] : null;
    return (
      <Card>
        <HintLabel text="tap quantity or capital to understand" />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 12, color: sub, fontWeight: 600 }}>Quantity</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: inputBg, border: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: text, fontSize: 18, fontWeight: 300, lineHeight: 1 }}>−</span>
            </div>
            <span
              onClick={() => setSelectedField(f => f === 'qty' ? null : 'qty')}
              style={{ fontSize: 20, fontWeight: 800, color: selectedField === 'qty' ? INDIGO : text, cursor: 'pointer', userSelect: 'none', transition: 'color 0.18s' }}
            >{ui.qty}</span>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: `${INDIGO}22`, border: `1px solid ${INDIGO}55`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: INDIGO, fontSize: 18, fontWeight: 300, lineHeight: 1 }}>+</span>
            </div>
          </div>
        </div>
        <div
          onClick={() => setSelectedField(f => f === 'capital' ? null : 'capital')}
          style={{ background: selectedField === 'capital' ? `${GREEN}18` : `${INDIGO}12`, borderRadius: 8, padding: '8px 12px', border: `1px solid ${selectedField === 'capital' ? GREEN : `${INDIGO}25`}`, display: 'flex', justifyContent: 'space-between', cursor: 'pointer', transition: 'all 0.18s', userSelect: 'none' }}
        >
          <span style={{ fontSize: 11, color: sub }}>Capital used</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: selectedField === 'capital' ? GREEN : INDIGO }}>{ui.capital}</span>
        </div>
        {info && <Explainer {...info} />}
      </Card>
    );
  }

  // --- SL / TARGET ---
  if (ui.type === 'sltp') {
    const fieldInfo = {
      sl:     { icon: '🛡️', color: RED,   title: 'Stop Loss', body: 'The maximum you\'re willing to lose. If price falls to ₹1,318, your position auto-exits. Protects capital. Place it below the nearest support — not randomly.' },
      target: { icon: '🎯', color: GREEN, title: 'Target', body: 'Where you expect price to reach. At ₹1,384 you book profit. Combined with SL, this gives you Risk:Reward — ideally 1:2 or better before entering any trade.' },
      rr:     { icon: '⚖️', color: GREEN, title: 'Risk : Reward', body: 'For every ₹1 you risk (SL distance), you\'re aiming to make ₹2 (target distance). A 1:2 ratio means even if you\'re right only 50% of the time, you\'ll be profitable overall.' },
    };
    const info = selectedField ? fieldInfo[selectedField] : null;
    return (
      <Card>
        <HintLabel text="tap SL, Target or R:R to understand each" />
        {[
          { key: 'sl', label: ui.slLabel, value: ui.sl, hl: ui.highlight === 'sl', hint: ui.highlight === 'sl' ? 'Just below support at ₹1,320' : null, activeColor: RED },
          { key: 'target', label: ui.targetLabel, value: ui.target, hl: ui.highlight === 'target', hint: ui.highlight === 'target' ? 'Next resistance zone' : null, activeColor: GREEN },
        ].map(f => f.value ? (
          <div key={f.key} style={{ marginBottom: 8 }}>
            <div
              onClick={() => setSelectedField(p => p === f.key ? null : f.key)}
              style={{ borderRadius: 10, padding: '10px 14px', background: selectedField === f.key ? `${f.activeColor}18` : f.hl ? `${INDIGO}18` : inputBg, border: `1.5px solid ${selectedField === f.key ? f.activeColor : f.hl ? INDIGO : border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', transition: 'all 0.18s', userSelect: 'none', boxShadow: f.hl ? `0 0 12px ${INDIGO}44` : 'none' }}
            >
              <span style={{ fontSize: 12, color: selectedField === f.key ? '#fff' : f.hl ? '#fff' : sub }}>{f.label}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: selectedField === f.key ? f.activeColor : f.hl ? INDIGO : text }}>{f.value}</span>
            </div>
            {f.hint && <p style={{ fontSize: 10, color: AMBER, margin: '3px 0 0 4px' }}>{f.hint}</p>}
          </div>
        ) : null)}
        {ui.rr && (
          <div onClick={() => setSelectedField(p => p === 'rr' ? null : 'rr')}
            style={{ display: 'flex', justifyContent: 'center', marginTop: 6, cursor: 'pointer', userSelect: 'none' }}>
            <div style={{ background: selectedField === 'rr' ? `${GREEN}25` : `${GREEN}15`, border: `1px solid ${selectedField === 'rr' ? GREEN : `${GREEN}40`}`, borderRadius: 8, padding: '6px 16px', transition: 'all 0.18s' }}>
              <span style={{ fontSize: 11, color: sub }}>Risk : Reward  </span>
              <span style={{ fontSize: 13, fontWeight: 800, color: GREEN }}>{ui.rr}</span>
            </div>
          </div>
        )}
        {info && <Explainer {...info} />}
      </Card>
    );
  }

  // --- REASONS ---
  if (ui.type === 'reasons') {
    const active = selectedReason ?? ui.active;
    const explainer = active ? REASON_EXPLAINERS[active] : null;
    return (
      <Card>
        <HintLabel text="tap any chip to see when to use it" />
        <div style={{ fontSize: 10, color: sub, marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Why this Trade? <span style={{ color: RED }}>✱</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: explainer ? 2 : 0 }}>
          {ui.reasons.map(r => {
            const isSelected = r === active;
            const isImpulse = r === 'Impulse';
            const chipColor = isSelected ? (REASON_EXPLAINERS[r]?.color || INDIGO) : null;
            return (
              <div key={r} onClick={() => setSelectedReason(r === selectedReason ? null : r)}
                style={{ padding: '7px 13px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: isSelected ? `${chipColor}20` : isImpulse ? `${RED}10` : inputBg, border: `1.5px solid ${isSelected ? chipColor : isImpulse ? `${RED}40` : border}`, color: isSelected ? chipColor : isImpulse ? RED : sub, boxShadow: isSelected ? `0 0 12px ${chipColor}44` : 'none', transition: 'all 0.18s', userSelect: 'none' }}
              >{r}</div>
            );
          })}
        </div>
        {explainer && (
          <Explainer color={explainer.color} icon={explainer.icon} title={active} body={explainer.example}
            badge={active === 'Impulse' ? 'lowers score' : null} />
        )}
      </Card>
    );
  }

  // --- TAGS + CONFIDENCE ---
  if (ui.type === 'tagsconfidence') {
    const tagInfo = {
      Support:   { icon: '🧱', color: INDIGO,  body: 'Price has bounced from this level before. A limit order placed here has a structural reason — not guesswork.' },
      Trendline: { icon: '📏', color: INDIGO,  body: 'Price is respecting an ascending/descending trendline. Entry near the trendline gives a tight stop and good R:R.' },
      Breakout:  { icon: '🚀', color: GREEN,   body: 'Price broke above a key resistance or consolidation zone with volume. Momentum is in your favour.' },
      'EMA Cross': { icon: '〰️', color: PURPLE, body: 'Short-term EMA crossed above long-term EMA — a classic momentum signal. Common on 15m and 1H charts.' },
      Volume:    { icon: '📊', color: AMBER,   body: 'Unusually high volume confirms the move. Volume without a price move is noise; price with volume is signal.' },
      RSI:       { icon: '⚡', color: RED,     body: 'RSI shows momentum. Below 30 = oversold (potential buy), above 70 = overbought (potential sell). Useful as confluence.' },
    };
    const confidenceInfo = (c) => {
      if (c <= 3)  return { color: RED,    text: 'Low confidence — consider skipping. Uncertainty is a valid reason to stay out.' };
      if (c <= 6)  return { color: AMBER,  text: 'Moderate confidence — setup has merit but not all conditions are aligned. Size down.' };
      if (c <= 8)  return { color: INDIGO, text: 'Good confidence — multiple confluences align. Normal position size is appropriate.' };
      return              { color: GREEN,  text: 'High conviction — everything aligns. This is when you size up within your risk limits.' };
    };
    const confInfo = confidenceInfo(ui.confidence);
    const info = selectedTag ? tagInfo[selectedTag] : null;
    return (
      <Card>
        <div style={{ fontSize: 10, color: sub, marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Technical Tags</div>
        <HintLabel text="tap a tag to learn what it means" />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 14 }}>
          {ui.tags.map(t => {
            const isActive = ui.activeTags.includes(t);
            const isSelected = t === selectedTag;
            const tc = tagInfo[t]?.color || INDIGO;
            return (
              <div key={t} onClick={() => setSelectedTag(p => p === t ? null : t)}
                style={{ padding: '5px 10px', borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer', background: isSelected ? `${tc}22` : isActive ? `${INDIGO}15` : inputBg, border: `1px solid ${isSelected ? tc : isActive ? INDIGO : border}`, color: isSelected ? tc : isActive ? INDIGO : sub, transition: 'all 0.18s', userSelect: 'none' }}
              >{t}</div>
            );
          })}
        </div>
        {info && <Explainer color={info.color} icon={info.icon} title={selectedTag} body={info.body} />}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, marginTop: info ? 12 : 0 }}>
          <span style={{ fontSize: 10, color: sub, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Confidence</span>
          <span style={{ fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 6, background: `${confInfo.color}20`, color: confInfo.color }}>{ui.confidence}/10</span>
        </div>
        <div style={{ position: 'relative', height: 6, background: inputBg, borderRadius: 3 }}>
          <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${(ui.confidence - 1) / 9 * 100}%`, background: confInfo.color, borderRadius: 3 }} />
          <div style={{ position: 'absolute', top: '50%', left: `${(ui.confidence - 1) / 9 * 100}%`, transform: 'translate(-50%,-50%)', width: 14, height: 14, borderRadius: '50%', background: confInfo.color, border: '2px solid #13141f' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, marginBottom: 6 }}>
          <span style={{ fontSize: 9, color: sub }}>Low</span>
          <span style={{ fontSize: 9, color: sub }}>High</span>
        </div>
        <div style={{ borderRadius: 8, padding: '6px 10px', background: `${confInfo.color}10`, border: `1px solid ${confInfo.color}30` }}>
          <p style={{ fontSize: 10, color: confInfo.color, margin: 0, lineHeight: 1.5 }}>{confInfo.text}</p>
        </div>
      </Card>
    );
  }

  // --- PLACE BUTTON ---
  if (ui.type === 'placebutton') {
    const statusInfo = {
      icon: '⏳', color: AMBER, title: 'Pending Order',
      body: 'Your order is live in the exchange but not yet filled. It\'ll fill automatically when RELIANCE trades at ₹1,340. You can cancel anytime from the Orders tab before it fills.',
    };
    return (
      <Card>
        <HintLabel text="tap the status to understand order states" />
        <div style={{ width: '100%', padding: '14px', borderRadius: 12, textAlign: 'center', background: `linear-gradient(135deg, ${INDIGO}, #818cf8)`, fontSize: 15, fontWeight: 800, color: '#fff', boxShadow: `0 4px 20px ${INDIGO}55`, marginBottom: 12 }}>{ui.label}</div>
        <div
          onClick={() => setSelectedField(f => f === 'status' ? null : 'status')}
          style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', padding: '8px 12px', background: selectedField === 'status' ? `${AMBER}18` : `${AMBER}10`, borderRadius: 8, border: `1px solid ${selectedField === 'status' ? AMBER : `${AMBER}30`}`, cursor: 'pointer', transition: 'all 0.18s', userSelect: 'none' }}
        >
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: AMBER, flexShrink: 0 }} />
          <span style={{ fontSize: 11, color: AMBER, fontWeight: 600 }}>{ui.status}</span>
        </div>
        {selectedField === 'status' && <Explainer {...statusInfo} />}
      </Card>
    );
  }

  // --- STRATEGY BUILDER ---
  if (ui.type === 'strategy-builder') {
    const conditionColors = { entry: INDIGO, exit: RED, sizing: GREEN, universe: AMBER };
    const condInfo = {
      'RSI < 30':            { icon: '⚡', color: RED,    body: 'RSI below 30 = oversold. The stock has fallen sharply and may be due a bounce. This is your entry trigger — wait for this condition before buying.' },
      'Price > 20D High':    { icon: '🚀', color: GREEN,  body: 'Price just crossed above the highest point of the last 20 days. Classic breakout signal — momentum is shifting upward with recent price confirming it.' },
      'Volume > 1.5× Avg':  { icon: '📊', color: AMBER,  body: 'Today\'s volume is 1.5× the average. Volume confirms the move — a breakout on low volume is weak. Always combine price signal with volume confirmation.' },
      'RSI > 70':            { icon: '🔴', color: RED,    body: 'RSI above 70 = overbought exit. The rally may be exhausted. This is your exit trigger — the algo sells when this fires, locking in profit automatically.' },
      'SL hit':              { icon: '🛡️', color: RED,    body: 'Algo automatically exits if price falls to your SL level. No manual action needed — the strategy protects capital even when you\'re not watching.' },
      'Target hit':          { icon: '🎯', color: GREEN,  body: 'Auto-books profit when price reaches target. Combined with SL, this gives you a disciplined R:R on every algo trade without emotion.' },
      '1% of capital/trade': { icon: '💰', color: GREEN,  body: 'Each trade risks exactly 1% of your capital. If capital = ₹1L, max loss per trade = ₹1,000. Quantity is calculated automatically from SL distance.' },
      'All NSE stocks':      { icon: '🔍', color: AMBER,  body: 'The algo scans every NSE stock for your conditions. Narrow the universe to a specific sector or index (e.g. NIFTY 50 stocks) for cleaner, more reliable signals.' },
    };
    return (
      <Card>
        <HintLabel text="tap any rule to understand what it does" />
        {ui.sections.map(section => (
          <div key={section.label} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: conditionColors[section.type], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{section.label}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {section.conditions.map(cond => {
                const isSel = selCond === cond;
                const c = conditionColors[section.type];
                return (
                  <div key={cond} onClick={() => setSelCond(p => p === cond ? null : cond)}
                    style={{ padding: '6px 11px', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer', userSelect: 'none', background: isSel ? `${c}22` : inputBg, border: `1.5px solid ${isSel ? c : border}`, color: isSel ? c : sub, transition: 'all 0.18s', boxShadow: isSel ? `0 0 10px ${c}44` : 'none' }}
                  >{cond}</div>
                );
              })}
            </div>
          </div>
        ))}
        {selCond && condInfo[selCond] && (
          <Explainer color={condInfo[selCond].color} icon={condInfo[selCond].icon} title={selCond} body={condInfo[selCond].body} />
        )}
      </Card>
    );
  }

  // --- BACKTEST RESULT ---
  if (ui.type === 'backtest-result') {
    const metricInfo = {
      'Win Rate':       { icon: '🎯', color: INDIGO, body: 'Out of all trades the strategy took, this % were winners. 45% win rate sounds bad — but if average win is 2× average loss, it\'s profitable. Never read win rate alone.' },
      'Avg R:R':        { icon: '⚖️', color: GREEN,  body: 'Average Reward÷Risk per trade. 2.1 means for every ₹1 risked you made ₹2.10 on winners. Combined with 45% win rate: expected value = (0.45×2.1) − (0.55×1) = +0.395 per trade. Positive = edge exists.' },
      'Profit Factor':  { icon: '📈', color: GREEN,  body: 'Total gross profit ÷ total gross loss. 1.8 means for every ₹1 lost, the strategy made ₹1.80. Above 1.5 = good. Above 2.0 = excellent. Below 1.0 = losing strategy.' },
      'Max Drawdown':   { icon: '📉', color: RED,    body: 'The worst peak-to-trough loss during the test period. −18% means at the worst point, the strategy was down 18% from its high. Ask yourself: can I psychologically handle this without abandoning the strategy?' },
      'Total Trades':   { icon: '🔢', color: AMBER,  body: 'More trades = more statistically significant results. Under 30 trades = results are luck, not edge. Over 100 trades = reliable signal. This backtest has 87 trades — reasonably confident.' },
      'Sharpe Ratio':   { icon: '⚡', color: PURPLE, body: 'Return ÷ Volatility. Above 1.0 = good risk-adjusted return. Above 1.5 = excellent. A strategy making 30% returns with wild swings scores lower than one making 20% steadily.' },
    };
    return (
      <Card>
        <div style={{ fontSize: 10, color: sub, marginBottom: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Backtest Result — <span style={{ color: GREEN }}>Edge confirmed</span>
        </div>
        <HintLabel text="tap any metric to understand what it means" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 4 }}>
          {ui.metrics.map(m => {
            const isSel = selMetric === m.label;
            const info = metricInfo[m.label];
            const c = info?.color || INDIGO;
            return (
              <div key={m.label} onClick={() => setSelMetric(p => p === m.label ? null : m.label)}
                style={{ borderRadius: 10, padding: '10px 12px', background: isSel ? `${c}15` : inputBg, border: `1.5px solid ${isSel ? c : border}`, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                <div style={{ fontSize: 9, color: sub, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{m.label}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: m.positive ? GREEN : m.warn ? RED : text }}>{m.value}</div>
              </div>
            );
          })}
        </div>
        {selMetric && metricInfo[selMetric] && (
          <Explainer color={metricInfo[selMetric].color} icon={metricInfo[selMetric].icon} title={selMetric} body={metricInfo[selMetric].body} />
        )}
      </Card>
    );
  }

  // --- ALGO DASHBOARD ---
  if (ui.type === 'algo-dashboard') {
    const statusColors = { Active: GREEN, Paused: AMBER, Stopped: RED };
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 10, color: sub, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>My Strategies</span>
          <div style={{ fontSize: 11, fontWeight: 700, color: GREEN }}>Portfolio P&L: {ui.portfolioPnl}</div>
        </div>
        <HintLabel text="tap a strategy to see its details" />
        {ui.strategies.map(s => {
          const isSel = selAlgo === s.name;
          const sc = statusColors[s.status] || sub;
          return (
            <div key={s.name} onClick={() => setSelAlgo(p => p === s.name ? null : s.name)}
              style={{ borderRadius: 10, padding: '10px 13px', background: isSel ? `${INDIGO}12` : inputBg, border: `1.5px solid ${isSel ? INDIGO : border}`, marginBottom: 8, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: text }}>{s.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: sc }} />
                  <span style={{ fontSize: 10, color: sc, fontWeight: 600 }}>{s.status}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <span style={{ fontSize: 11, color: sub }}>{s.trades} trades</span>
                <span style={{ fontSize: 11, color: sub }}>{s.winRate} win</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: s.pnl.startsWith('+') ? GREEN : RED }}>{s.pnl}</span>
              </div>
              {isSel && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${border}` }}>
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', margin: 0, lineHeight: 1.55 }}>{s.detail}</p>
                </div>
              )}
            </div>
          );
        })}
      </Card>
    );
  }

  // --- ALGO DASHBOARD SUMMARY (intro slide — mirrors the real 4-stat header + strategy list) ---
  if (ui.type === 'algo-dashboard-summary') {
    const stats = ui.stats || [];
    const strategies = ui.strategies || [];
    const statColors = { active: GREEN, capital: INDIGO, pnl: GREEN, trades: AMBER };
    return (
      <Card>
        <HintLabel text="tap any stat or strategy to understand what it tracks" />
        {/* 4-stat grid — mirrors real AlgoFlowsPage dashboard summary */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7, marginBottom: 12 }}>
          {stats.map(s => {
            const isSel = selectedField === s.key;
            const c = statColors[s.key] || INDIGO;
            return (
              <div key={s.key} onClick={() => setSelectedField(f => f === s.key ? null : s.key)}
                style={{ borderRadius: 10, padding: '10px 12px', background: isSel ? `${c}18` : inputBg, border: `1.5px solid ${isSel ? c : border}`, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                <div style={{ fontSize: 9, color: sub, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{s.label}</div>
                <div style={{ fontSize: 17, fontWeight: 800, color: s.pnl ? (s.value.startsWith('+') ? GREEN : RED) : c }}>{s.value}</div>
                {isSel && s.note && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginTop: 6, lineHeight: 1.5 }}>{s.note}</div>}
              </div>
            );
          })}
        </div>
        {/* Strategy list — mirrors the real strategies list panel exactly */}
        <div style={{ fontSize: 9, color: sub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 7 }}>Your Strategies</div>
        {strategies.map(s => {
          const isSel = selAlgo === s.name;
          const sc = s.status === 'ACTIVE' ? GREEN : s.status === 'PAUSED' ? AMBER : RED;
          const disciplineColor = s.discipline >= 85 ? GREEN : s.discipline >= 70 ? INDIGO : s.discipline >= 50 ? AMBER : RED;
          const pnlColor = s.pnl.startsWith('+') ? GREEN : RED;
          return (
            <div key={s.name} onClick={() => setSelAlgo(p => p === s.name ? null : s.name)}
              style={{ padding: '8px 0', borderBottom: `1px solid ${border}`, cursor: 'pointer', userSelect: 'none', background: isSel ? `${INDIGO}08` : 'transparent', transition: 'all 0.18s' }}>
              {/* Row 1: name + status chip + chevron */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
                  background: s.status === 'ACTIVE' ? 'rgba(34,197,94,0.15)' : s.status === 'PAUSED' ? 'rgba(234,179,8,0.15)' : 'rgba(239,68,68,0.15)',
                  color: sc, flexShrink: 0 }}>{s.status}</span>
                <span style={{ fontSize: 11, color: sub, flexShrink: 0 }}>›</span>
              </div>
              {/* Row 2 (indented): capital | P&L */}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: 14, marginBottom: 3 }}>
                <span style={{ fontSize: 11, color: sub }}>{s.capital}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: pnlColor }}>{s.pnl}</span>
              </div>
              {/* Row 3 (indented): trades · stocks | discipline badge */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingLeft: 14 }}>
                <span style={{ fontSize: 11, color: sub }}>{s.trades} trades · {s.stocks} stocks</span>
                {s.discipline != null && (
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 5, background: `${disciplineColor}18`, color: disciplineColor }}>D: {s.discipline}</span>
                )}
              </div>
              {isSel && s.detail && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${border}`, fontSize: 11, color: 'rgba(255,255,255,0.6)', lineHeight: 1.55, paddingLeft: 14 }}>{s.detail}</div>
              )}
            </div>
          );
        })}
      </Card>
    );
  }

  // --- STRATEGY CREATE FORM — Basic Info + Risk Settings (mirrors Create New Strategy modal) ---
  if (ui.type === 'strategy-create-form') {
    const fields = ui.fields || [];
    const riskFields = ui.riskFields || [];
    return (
      <Card>
        <HintLabel text="tap any field to understand what it controls" />
        <div style={{ fontSize: 9, color: INDIGO, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Basic Info</div>
        {fields.map(f => {
          const isSel = selectedField === f.key;
          return (
            <div key={f.key} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, color: sub, fontWeight: 600, marginBottom: 3 }}>{f.label}{f.required && <span style={{ color: RED }}> *</span>}</div>
              <div onClick={() => setSelectedField(k => k === f.key ? null : f.key)}
                style={{ padding: '9px 12px', borderRadius: 9, background: isSel ? `${INDIGO}15` : inputBg, border: `1.5px solid ${isSel ? INDIGO : border}`, fontSize: 12, color: f.value ? text : sub, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                {f.value || f.placeholder}
              </div>
              {isSel && f.note && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginTop: 5, lineHeight: 1.55, padding: '5px 8px', background: `${INDIGO}10`, borderRadius: 7 }}>{f.note}</div>}
            </div>
          );
        })}
        <div style={{ fontSize: 9, color: AMBER, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 12, marginBottom: 8 }}>Risk Settings</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {riskFields.map(f => {
            const isSel = selectedField === f.key;
            return (
              <div key={f.key} onClick={() => setSelectedField(k => k === f.key ? null : f.key)}
                style={{ padding: '9px 11px', borderRadius: 9, background: isSel ? `${AMBER}15` : inputBg, border: `1.5px solid ${isSel ? AMBER : border}`, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                <div style={{ fontSize: 9, color: sub, fontWeight: 600, marginBottom: 3 }}>{f.label}</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: AMBER }}>{f.value}</div>
                {isSel && f.note && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginTop: 6, lineHeight: 1.55 }}>{f.note}</div>}
              </div>
            );
          })}
        </div>
      </Card>
    );
  }

  // --- STRATEGY ENGINE RULES — conditions + entry logic + SL/Target (mirrors Engine Rules section of modal) ---
  if (ui.type === 'strategy-engine-rules') {
    const conditions = ui.conditions || [];
    const engineFields = ui.engineFields || [];
    return (
      <Card>
        <HintLabel text="tap any condition or rule to understand what it does" />
        <div style={{ fontSize: 9, color: INDIGO, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Conditions</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {conditions.map(c => {
            const isSel = selCond === c.name;
            const dirColor = c.dir === 'bullish' ? GREEN : c.dir === 'bearish' ? RED : AMBER;
            return (
              <div key={c.name} onClick={() => setSelCond(p => p === c.name ? null : c.name)}
                style={{ padding: '6px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer', userSelect: 'none', background: isSel ? `${dirColor}20` : inputBg, border: `1.5px solid ${isSel ? dirColor : border}`, color: isSel ? dirColor : sub, transition: 'all 0.18s' }}>
                {c.display}
              </div>
            );
          })}
        </div>
        {selCond && (() => {
          const found = conditions.find(c => c.name === selCond);
          if (!found) return null;
          const dc = found.dir === 'bullish' ? GREEN : found.dir === 'bearish' ? RED : AMBER;
          return <div style={{ marginBottom: 10, padding: '7px 10px', borderRadius: 8, background: `${dc}10`, border: `1px solid ${dc}30`, fontSize: 10, color: 'rgba(255,255,255,0.7)', lineHeight: 1.55 }}>{found.desc}</div>;
        })()}
        <div style={{ fontSize: 9, color: INDIGO, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Engine Rules</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
          {engineFields.map(f => {
            const isSel = selectedField === f.key;
            const c = f.color === 'red' ? RED : f.color === 'green' ? GREEN : INDIGO;
            return (
              <div key={f.key} onClick={() => setSelectedField(k => k === f.key ? null : f.key)}
                style={{ padding: '8px 10px', borderRadius: 9, background: isSel ? `${c}15` : inputBg, border: `1.5px solid ${isSel ? c : border}`, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                <div style={{ fontSize: 9, color: sub, fontWeight: 600, marginBottom: 3 }}>{f.label}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: c }}>{f.value}</div>
                {isSel && f.note && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginTop: 6, lineHeight: 1.5 }}>{f.note}</div>}
              </div>
            );
          })}
        </div>
      </Card>
    );
  }

  // --- STRATEGY DETAIL — mirrors performance header + configuration panel ---
  if (ui.type === 'strategy-detail') {
    const perf = ui.performance || {};
    const config = ui.config || {};
    const stocks = ui.stocks || [];
    const condChips = ui.conditions || [];
    return (
      <Card>
        <HintLabel text="tap any metric or setting to understand what it means" />
        {/* Performance row — mirrors the 4-metric grid in strategy header */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7, marginBottom: 12 }}>
          {[
            { label: 'Win Rate',    value: perf.win_rate,    color: GREEN,  note: 'Percentage of trades that closed profitably. 45% win rate can still be profitable if average win > average loss — never read win rate alone.' },
            { label: 'ROI',         value: perf.roi,         color: perf.roi?.startsWith('+') ? GREEN : RED, note: 'Return on the capital allocated to this strategy. Calculated as total P&L ÷ capital allocated × 100.' },
            { label: 'W / L',       value: perf.wl,          color: text,   note: 'Winning trades / losing trades. Useful for spotting streaks. 5+ consecutive losses = pause and investigate.' },
            { label: 'Open Trades', value: perf.open_trades, color: AMBER,  note: 'Positions the algo has entered but not yet exited. These are live paper trades tracked by the engine.' },
          ].map(m => {
            const isSel = selMetric === m.label;
            return (
              <div key={m.label} onClick={() => setSelMetric(p => p === m.label ? null : m.label)}
                style={{ padding: '9px 11px', borderRadius: 9, background: isSel ? `${m.color}15` : inputBg, border: `1.5px solid ${isSel ? m.color : border}`, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                <div style={{ fontSize: 9, color: sub, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{m.label}</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: m.color }}>{m.value}</div>
                {isSel && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginTop: 6, lineHeight: 1.5 }}>{m.note}</div>}
              </div>
            );
          })}
        </div>
        {/* Configuration — mirrors the Configuration panel */}
        <div style={{ fontSize: 9, color: INDIGO, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 7 }}>Configuration</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 10 }}>
          {Object.entries(config).map(([label, value]) => (
            <div key={label} style={{ padding: '7px 10px', borderRadius: 8, background: inputBg }}>
              <div style={{ fontSize: 9, color: sub, fontWeight: 600, marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: text }}>{value}</div>
            </div>
          ))}
        </div>
        {/* Stocks */}
        {stocks.length > 0 && (
          <>
            <div style={{ fontSize: 9, color: sub, fontWeight: 600, marginBottom: 5 }}>Stocks ({stocks.length})</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
              {stocks.map(s => <span key={s} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, background: inputBg, color: text }}>{s}</span>)}
            </div>
          </>
        )}
        {/* Conditions chips */}
        {condChips.length > 0 && (
          <>
            <div style={{ fontSize: 9, color: sub, fontWeight: 600, marginBottom: 5 }}>Conditions ({condChips.length})</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {condChips.map(c => <span key={c} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, background: `${INDIGO}20`, color: INDIGO }}>{c}</span>)}
            </div>
          </>
        )}
      </Card>
    );
  }

  // --- STRATEGY DECISIONS — mirrors Recent Decisions + Execution Log panels ---
  if (ui.type === 'strategy-decisions') {
    const decisions = ui.decisions || [];
    const logs = ui.logs || [];
    return (
      <Card>
        <HintLabel text="tap any decision or log entry to understand what happened" />
        <div style={{ fontSize: 9, color: INDIGO, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 7 }}>Recent Decisions — per symbol · last 24h</div>
        {decisions.map(d => {
          const isSel = selectedField === d.symbol;
          const dc = d.decision === 'triggered' ? GREEN : d.decision === 'blocked' ? RED : AMBER;
          return (
            <div key={d.symbol} onClick={() => setSelectedField(k => k === d.symbol ? null : d.symbol)}
              style={{ padding: '8px 11px', borderRadius: 9, background: isSel ? `${dc}10` : inputBg, border: `1.5px solid ${isSel ? dc : border}`, marginBottom: 7, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: text }}>{d.symbol}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: `${dc}20`, color: dc }}>{d.decision}</span>
                </div>
                <span style={{ fontSize: 10, color: d.cooldown ? AMBER : GREEN, background: d.cooldown ? 'rgba(234,179,8,0.1)' : 'rgba(34,197,94,0.1)', padding: '2px 7px', borderRadius: 20 }}>{d.cooldown ? `${d.cooldown} cooldown` : 'ready'}</span>
              </div>
              <div style={{ fontSize: 11, color: sub, paddingLeft: 2 }}>{d.reason}</div>
              {isSel && d.conditions && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                  {d.conditions.met?.map(c => <span key={c} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 5, background: 'rgba(34,197,94,0.1)', color: GREEN }}>✓ {c}</span>)}
                  {d.conditions.failed?.map(c => <span key={c} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 5, background: 'rgba(239,68,68,0.1)', color: RED }}>✗ {c}</span>)}
                </div>
              )}
            </div>
          );
        })}
        <div style={{ fontSize: 9, color: INDIGO, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 7, marginTop: 4 }}>Execution Log</div>
        {logs.map((l, i) => {
          const lc = l.action === 'TRADE_TRIGGERED' ? GREEN : l.action === 'CONDITIONS_NOT_MET' ? AMBER : RED;
          const isSel = selectedTag === String(i);
          return (
            <div key={i} onClick={() => setSelectedTag(p => p === String(i) ? null : String(i))}
              style={{ display: 'flex', gap: 8, padding: '7px 0', borderBottom: `1px solid ${border}`, cursor: 'pointer', userSelect: 'none' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: lc, marginTop: 4, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: lc }}>{l.action.replace(/_/g, ' ')}</span>
                  <span style={{ fontSize: 10, color: sub }}>{l.time}</span>
                </div>
                <div style={{ fontSize: 11, color: sub, marginTop: 1 }}>{l.symbol} · {l.reason}</div>
                {isSel && l.detail && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginTop: 5, lineHeight: 1.5 }}>{l.detail}</div>}
              </div>
            </div>
          );
        })}
      </Card>
    );
  }

  // --- ALGO DISCIPLINE — mirrors the per-strategy Discipline Score panel ---
  if (ui.type === 'algo-discipline') {
    const score = ui.score ?? 85;
    const level = ui.level ?? 'EXCELLENT';
    const scoreColor = score >= 85 ? GREEN : score >= 70 ? INDIGO : score >= 50 ? AMBER : RED;
    const stats = ui.stats || {};
    const events = ui.events || [];
    return (
      <Card>
        <HintLabel text="tap any event to understand how it affects your algo discipline score" />
        <div style={{ fontSize: 9, color: sub, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>7-day algo interference score</div>
        {/* Score row — mirrors the circle + level + stats grid */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <div style={{ position: 'relative', width: 52, height: 52, flexShrink: 0 }}>
            <svg viewBox="0 0 36 36" style={{ width: 52, height: 52, transform: 'rotate(-90deg)' }}>
              <circle cx="18" cy="18" r="14" fill="none" stroke={border} strokeWidth="3" />
              <circle cx="18" cy="18" r="14" fill="none" stroke={scoreColor} strokeWidth="3"
                strokeDasharray={`${(score / 100) * 87.96} 87.96`} strokeLinecap="round" />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: scoreColor }}>{score}</div>
          </div>
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 9px', borderRadius: 6, background: `${scoreColor}18`, color: scoreColor }}>{level}</span>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 14px', marginTop: 7 }}>
              {[
                { label: 'Panic pauses',     value: stats.panic_pauses    ?? 0, bad: true },
                { label: 'Config changes',   value: stats.config_changes  ?? 0, bad: true },
                { label: 'Manual overrides', value: stats.manual_overrides?? 0, bad: true },
                { label: 'Held through loss',value: stats.held_through_loss?? 0, bad: false },
              ].map(s => (
                <span key={s.label} style={{ fontSize: 10, color: sub }}>
                  {s.label}: <b style={{ color: s.value > 0 ? (s.bad ? RED : GREEN) : text }}>{s.value}</b>
                </span>
              ))}
            </div>
          </div>
        </div>
        {/* Score bar */}
        <div style={{ height: 6, borderRadius: 3, background: border, marginBottom: 10 }}>
          <div style={{ height: '100%', width: `${score}%`, background: scoreColor, borderRadius: 3 }} />
        </div>
        {/* Events */}
        {events.map((ev, i) => {
          const isSel = selectedField === String(i);
          const ec = ev.type === 'PANIC_PAUSE' ? RED : ev.type === 'HELD_THROUGH_LOSS' ? GREEN : AMBER;
          return (
            <div key={i} onClick={() => setSelectedField(k => k === String(i) ? null : String(i))}
              style={{ display: 'flex', gap: 8, padding: '7px 0', borderBottom: `1px solid ${border}`, cursor: 'pointer', userSelect: 'none' }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: ec, flexShrink: 0, minWidth: 28 }}>{ev.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: ec }}>{ev.label}</span>
                  <span style={{ fontSize: 10, color: sub }}>{ev.time}</span>
                </div>
                {isSel && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', marginTop: 5, lineHeight: 1.55 }}>{ev.note}</div>}
              </div>
            </div>
          );
        })}
      </Card>
    );
  }

  // --- PEER BENCHMARK (mirrors real DisciplinePage "How You Compare" section) ---
  if (ui.type === 'peer-benchmark') {
    const { percentile = 62, total_users = 1240, best, weakest } = ui;
    const topPct = 100 - percentile;
    const barColor = percentile >= 70 ? GREEN : percentile >= 40 ? INDIGO : RED;
    const isSel = selectedField === 'bar';
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: text }}>How You Compare</span>
          <span style={{ fontSize: 10, color: sub, background: inputBg, borderRadius: 6, padding: '2px 7px' }}>vs {total_users.toLocaleString()} traders · anonymous</span>
        </div>
        {/* Overall percentile bar */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
            <span style={{ fontSize: 11, color: sub }}>Overall Discipline</span>
            <span style={{ fontSize: 14, fontWeight: 800, color: barColor }}>Top {topPct}%</span>
          </div>
          <div onClick={() => setSelectedField(f => f === 'bar' ? null : 'bar')}
            style={{ cursor: 'pointer', userSelect: 'none' }}>
            <div style={{ height: 8, borderRadius: 4, background: inputBg, position: 'relative', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${percentile}%`, background: `linear-gradient(to right, ${barColor}99, ${barColor})`, borderRadius: 4, transition: 'width 0.4s' }} />
            </div>
          </div>
          <div style={{ fontSize: 10, color: sub, marginTop: 4 }}>Better than {percentile}% of traders on this platform</div>
          {isSel && (
            <div style={{ marginTop: 6, padding: '7px 10px', borderRadius: 8, background: `${barColor}10`, border: `1px solid ${barColor}30`, fontSize: 10, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55 }}>
              Your percentile is calculated anonymously across all TradeReflex users who have at least 10 trades. A higher percentile means more consistent rule-following — not necessarily higher P&L.
            </div>
          )}
        </div>
        {/* Strength / Needs Work 2-col grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {best && (
            <div style={{ padding: '10px 12px', borderRadius: 10, background: `${GREEN}10`, border: `1px solid ${GREEN}28` }}>
              <div style={{ fontSize: 9, color: GREEN, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Your Strength</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: text, marginBottom: 3 }}>{best.label}</div>
              <div style={{ fontSize: 12, fontWeight: 800, color: GREEN }}>Top {100 - best.percentile}%</div>
            </div>
          )}
          {weakest && (
            <div style={{ padding: '10px 12px', borderRadius: 10, background: `${RED}10`, border: `1px solid ${RED}28` }}>
              <div style={{ fontSize: 9, color: RED, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Needs Work</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: text, marginBottom: 3 }}>{weakest.label}</div>
              <div style={{ fontSize: 12, fontWeight: 800, color: RED }}>Bottom {weakest.percentile}%</div>
            </div>
          )}
        </div>
      </Card>
    );
  }

  // --- MARKET OVERVIEW ---
  if (ui.type === 'market-overview') {
    const idx = ui.indices || [];
    const moverInfo = {
      'RELIANCE':  { sector: 'Energy', note: 'Volume 2.1× avg — high conviction move' },
      'INFY':      { sector: 'IT', note: 'FII buying — tech sector rotation' },
      'HDFCBANK':  { sector: 'Banking', note: 'Breakout above resistance at ₹1,678' },
      'BAJFINANCE':{ sector: 'NBFC', note: 'Result day — high volatility expected' },
      'ONGC':      { sector: 'Energy', note: 'Tracking Crude oil price move' },
    };
    return (
      <Card>
        <HintLabel text="tap an index or stock to understand what to look for" />
        {idx.map(ix => {
          const isSel = selectedField === ix.label;
          const c = ix.change >= 0 ? GREEN : RED;
          const bullBear = ix.change >= 0
            ? { icon: '📈', body: `${ix.label} is up ${ix.change}% — market favours longs today. Look for breakout setups and pullbacks to support in leading sectors. Avoid shorting against this trend unless a specific setup justifies it.` }
            : { icon: '📉', body: `${ix.label} is down ${Math.abs(ix.change)}% — market favours caution. Look for short setups or range fades. Avoid adding new longs until the index stabilises above key support.` };
          return (
            <div key={ix.label} onClick={() => setSelectedField(f => f === ix.label ? null : ix.label)}
              style={{ borderRadius: 10, padding: '10px 13px', background: isSel ? `${c}12` : inputBg, border: `1.5px solid ${isSel ? c : border}`, marginBottom: 8, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: text }}>{ix.label}</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: c }}>{ix.change >= 0 ? '+' : ''}{ix.change}%</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, color: sub }}>{ix.level}</span>
                <span style={{ fontSize: 10, color: sub }}>Vol: {ix.vol}</span>
              </div>
              {isSel && <Explainer color={c} icon={bullBear.icon} title={ix.label + ' signal'} body={bullBear.body} />}
            </div>
          );
        })}
        {ui.movers && (
          <div style={{ marginTop: 4 }}>
            <div style={{ fontSize: 9, color: sub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Top Movers</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {ui.movers.map(m => {
                const isSel = selectedTag === m.name;
                const c = m.pct >= 0 ? GREEN : RED;
                const info = moverInfo[m.name];
                return (
                  <div key={m.name} onClick={() => setSelectedTag(t => t === m.name ? null : m.name)}
                    style={{ padding: '5px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer', userSelect: 'none', background: isSel ? `${c}18` : inputBg, border: `1.5px solid ${isSel ? c : border}`, color: isSel ? c : sub, transition: 'all 0.18s' }}>
                    {m.name} <span style={{ color: c, fontWeight: 800 }}>{m.pct >= 0 ? '+' : ''}{m.pct}%</span>
                  </div>
                );
              })}
            </div>
            {selectedTag && moverInfo[selectedTag] && (
              <Explainer color={GREEN} icon="💡" title={selectedTag + ' · ' + moverInfo[selectedTag].sector} body={moverInfo[selectedTag].note} />
            )}
          </div>
        )}
      </Card>
    );
  }

  // --- GLOBAL CUES ---
  if (ui.type === 'cues-card') {
    const cueInfo = {
      'SGX Nifty':   { icon: '🎯', body: 'SGX Nifty is the real-time predictor of where NIFTY 50 will open. Gap up >0.5% = bullish open. Gap down >0.5% = wait for first 15 minutes. Gap >1.5% = volatile session, reduce size.' },
      'S&P 500':     { icon: '🇺🇸', body: 'US markets set the overnight tone. Strong US close (+1%+) = positive Indian open. Sharp US sell-off (−1.5%+) almost always drags India lower. Check the reason — a specific company miss matters less than broad fear.' },
      'Nasdaq':      { icon: '💻', body: 'Nasdaq leads tech sentiment globally. IT stocks (INFY, TCS, Wipro) follow Nasdaq very closely. If Nasdaq is down 2%+ overnight, expect IT sector weakness in India regardless of local news.' },
      'FII Flow':    { icon: '🌐', body: 'FII (Foreign Institutional Investors) net buying or selling drives market direction. Net FII buying = smart money entering India = favour longs. FII selling = distribution phase = reduce longs, watch support levels.' },
      'DII Flow':    { icon: '🏦', body: 'DII (Domestic Institutional Investors — mutual funds, LIC) often buy when FII sells, providing a buffer. FII selling + DII buying = support holding, market may be choppy but not crash. Both selling = avoid new longs.' },
      'Crude Oil':   { icon: '🛢️', body: 'India imports 85% of its crude. Crude rising sharply (+2%+) = inflation pressure, bearish for markets and especially aviation/paint/chemical stocks. Rising crude also weakens the rupee, pressuring FII outflows.' },
      'DXY (Dollar)':{ icon: '💵', body: 'A rising Dollar Index (DXY) weakens the Indian rupee, which triggers FII outflows as their INR returns shrink in dollar terms. DXY rising = FII selling pressure on India = negative headwind for all markets.' },
    };
    return (
      <Card>
        <HintLabel text="tap any cue to understand how it affects Indian markets" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {ui.cues.map(c => {
            const isSel = selectedField === c.label;
            const vc = c.direction === 'up' ? GREEN : c.direction === 'down' ? RED : AMBER;
            const info = cueInfo[c.label];
            return (
              <div key={c.label} onClick={() => setSelectedField(f => f === c.label ? null : c.label)}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 12px', borderRadius: 9, background: isSel ? `${vc}12` : inputBg, border: `1.5px solid ${isSel ? vc : border}`, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: text }}>{c.label}</div>
                  {isSel && info && <div style={{ fontSize: 11, color: vc, marginTop: 6, lineHeight: 1.5 }}>{info.icon} {info.body}</div>}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: vc }}>{c.value}</div>
                  <div style={{ fontSize: 9, color: vc, fontWeight: 600, textTransform: 'uppercase' }}>{c.direction === 'up' ? '▲ Bullish' : c.direction === 'down' ? '▼ Bearish' : '→ Neutral'}</div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    );
  }

  // --- SCORE ARC ---
  if (ui.type === 'score-arc') {
    const tiers = [
      { label: 'Minimum',   range: '0–34',  cap: '₹50K/day',  color: RED },
      { label: 'Restricted',range: '35–49', cap: '₹75K/day',  color: '#f97316' },
      { label: 'Standard',  range: '50–64', cap: '₹1L/day',   color: AMBER },
      { label: 'Advanced',  range: '65–79', cap: '₹1.5L/day', color: INDIGO },
      { label: 'Elite',     range: '80+',   cap: '₹2L/day',   color: GREEN },
    ];
    const score = ui.score || 62;
    const pct = score / 100;
    const activeTier = tiers.find(t => {
      if (t.range === '80+') return score >= 80;
      const [lo, hi] = t.range.split('–').map(Number);
      return score >= lo && score <= hi;
    }) || tiers[2];
    const componentInfo = {
      'Stop Loss Discipline':    { icon: '🛡️', color: GREEN,  body: 'Do you always set a stop loss and keep it in place? Weighted 20% — the highest of all components. Missing SL even once drops your score significantly. This is non-negotiable.' },
      'Risk-Reward Planning':    { icon: '🎯', color: INDIGO, body: 'Are your trades planned with a target that justifies the risk? Weighted 15%. Entering without a defined target = unplanned trade. Always set target before entering.' },
      'Strategy Consistency':    { icon: '🧠', color: INDIGO, body: 'Are you trading the same repeatable setups or jumping between random ideas? Weighted 15%. Consistent strategy use = learning compounds. Random trades = no edge accumulation.' },
      'Emotional Control':       { icon: '⚠️', color: AMBER,  body: 'Did you take revenge trades after losses, or overtrade after wins? Weighted 10%. Emotional trades (e.g. immediately re-entering after SL hit) are flagged and penalised.' },
      'Trade Frequency':         { icon: '🕐', color: AMBER,  body: 'Did you take too many or too few trades in a session? Weighted 10%. Overtrading after losses is the most common discipline failure — the system detects it automatically.' },
      'Confidence Alignment':    { icon: '⚡', color: PURPLE, body: 'Does your stated confidence (1–10) match actual outcomes over time? Weighted 10%. Consistently tagging trades 9/10 but losing = miscalibrated confidence. The system tracks this drift.' },
      'Journal Discipline':      { icon: '📒', color: INDIGO, body: 'Did you write a journal note for your trades? Weighted 5%. Journaling forces reflection and is how professionals audit their own performance over time.' },
      'SL Violations':           { icon: '🚫', color: RED,    body: 'Did you move your SL further away after it was set, or close without hitting SL? Weighted 5%. Moving SL to "give it room" is the most expensive discipline failure in trading.' },
      'Risk Profile Adherence':  { icon: '📊', color: GREEN,  body: 'Did your position sizes stay within your risk profile limit? Weighted 10%. Sizing 3× your normal size on a "high conviction" trade is exactly what blows accounts. Stay within limits.' },
    };
    return (
      <Card>
        <div style={{ textAlign: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: sub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Discipline Score</div>
          <div style={{ fontSize: 42, fontWeight: 900, color: activeTier.color, lineHeight: 1 }}>{score}</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: activeTier.color, marginTop: 4 }}>{activeTier.label} Tier · {activeTier.cap}</div>
          <div style={{ position: 'relative', height: 8, background: inputBg, borderRadius: 4, margin: '10px 0 4px' }}>
            <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${pct * 100}%`, background: `linear-gradient(90deg, ${RED}, ${AMBER}, ${GREEN})`, borderRadius: 4 }} />
            <div style={{ position: 'absolute', top: '50%', left: `${pct * 100}%`, transform: 'translate(-50%,-50%)', width: 14, height: 14, borderRadius: '50%', background: activeTier.color, border: '2px solid #13141f' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: sub }}>
            <span>0</span><span>35</span><span>50</span><span>65</span><span>80</span><span>100</span>
          </div>
        </div>
        <HintLabel text="tap any component to understand how it is scored" />
        {ui.components && ui.components.map(comp => {
          const isSel = selectedField === comp.label;
          const info = componentInfo[comp.label];
          const c = info?.color || INDIGO;
          const w = comp.value / 100;
          return (
            <div key={comp.label} onClick={() => setSelectedField(f => f === comp.label ? null : comp.label)}
              style={{ borderRadius: 9, padding: '8px 11px', background: isSel ? `${c}15` : inputBg, border: `1.5px solid ${isSel ? c : border}`, marginBottom: 7, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: isSel ? c : text }}>{comp.label}</span>
                <span style={{ fontSize: 11, fontWeight: 800, color: c }}>{comp.value}%</span>
              </div>
              <div style={{ height: 4, background: border, borderRadius: 2 }}>
                <div style={{ height: '100%', width: `${w * 100}%`, background: c, borderRadius: 2 }} />
              </div>
              {isSel && info && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 8, lineHeight: 1.5 }}>{info.icon} {info.body}</div>}
            </div>
          );
        })}
      </Card>
    );
  }

  // --- DISCIPLINE SCORE CARD — mirrors DisciplinePage Main Score Card + "How You Compare" peer benchmark ---
  if (ui.type === 'discipline-score-card') {
    const { score = 72, profile_type = 'Baseline Trader', profile_color = INDIGO, percentile = 58, total_users = 1240, best, weakest } = ui;
    const tiers = [
      { label: 'Impulsive',     range: [0,   34],  cap: '₹50K/day',  color: RED },
      { label: 'Unstructured',  range: [35,  49],  cap: '₹75K/day',  color: '#f97316' },
      { label: 'Developing',    range: [50,  64],  cap: '₹1L/day',   color: AMBER },
      { label: 'Consistent',    range: [65,  79],  cap: '₹1.5L/day', color: INDIGO },
      { label: 'Disciplined',   range: [80, 100],  cap: '₹2L/day',   color: GREEN },
    ];
    const activeTier = tiers.find(t => score >= t.range[0] && score <= t.range[1]) || tiers[2];
    const scoreColor = activeTier.color;
    const topPct = 100 - percentile;
    const barColor = percentile >= 70 ? GREEN : percentile >= 40 ? INDIGO : RED;
    return (
      <Card>
        {/* Score row — matches DisciplinePage layout */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: sub }}>Discipline Score</div>
            <div style={{ fontSize: 38, fontWeight: 900, color: scoreColor, lineHeight: 1 }}>
              {score}<span style={{ fontSize: 15, color: sub }}>/100</span>
            </div>
          </div>
          {/* Half-arc SVG — matches real page */}
          <svg viewBox="0 0 100 55" width={88} height={52}>
            <path d="M 10 50 A 40 40 0 0 1 90 50" fill="none" stroke={border} strokeWidth={10} strokeLinecap="round" />
            <path d="M 10 50 A 40 40 0 0 1 90 50" fill="none" stroke={scoreColor} strokeWidth={10}
              strokeLinecap="round" strokeDasharray={`${(score / 100) * 125.66} 125.66`} />
          </svg>
        </div>
        {/* 5-band spectrum bar */}
        <div style={{ position: 'relative', height: 8, borderRadius: 4, background: 'linear-gradient(to right, #ef4444 0%, #f97316 20%, #f59e0b 40%, #6366f1 60%, #16C784 100%)', marginBottom: 4 }}>
          <div style={{ position: 'absolute', top: '50%', left: `${Math.min(Math.max(score, 2), 97)}%`, transform: 'translate(-50%,-50%)', width: 14, height: 14, borderRadius: '50%', background: '#fff', border: `2px solid ${scoreColor}`, boxShadow: `0 0 6px ${scoreColor}` }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: sub, marginBottom: 10 }}>
          {tiers.map(t => <span key={t.label}>{t.label}</span>)}
        </div>
        {/* Profile label + tier cap */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: inputBg, borderRadius: 9, marginBottom: 12, borderTop: `1px solid ${border}` }}>
          <span style={{ fontSize: 11, color: sub }}>Behavioral Profile</span>
          <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: `${scoreColor}20`, color: scoreColor }}>{profile_type}</span>
        </div>
        {/* Peer benchmark — "How You Compare" */}
        <div style={{ fontSize: 11, fontWeight: 700, color: text, marginBottom: 8 }}>How You Compare</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
          <span style={{ fontSize: 11, color: sub }}>Overall Discipline</span>
          <span style={{ fontSize: 13, fontWeight: 800, color: barColor }}>Top {topPct}%</span>
        </div>
        <div style={{ height: 7, borderRadius: 4, background: inputBg, marginBottom: 5 }}>
          <div style={{ height: '100%', width: `${percentile}%`, borderRadius: 4, background: `linear-gradient(to right, ${barColor}90, ${barColor})` }} />
        </div>
        <div style={{ fontSize: 10, color: sub, marginBottom: 10 }}>Better than {percentile}% of traders on this platform · vs {total_users.toLocaleString()} traders</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {best && (
            <div style={{ padding: '8px 10px', borderRadius: 9, background: 'rgba(22,199,132,0.08)', border: '1px solid rgba(22,199,132,0.2)' }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: GREEN, marginBottom: 3 }}>YOUR STRENGTH</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: text }}>{best.label}</div>
              <div style={{ fontSize: 12, fontWeight: 800, color: GREEN }}>Top {100 - best.percentile}%</div>
            </div>
          )}
          {weakest && (
            <div style={{ padding: '8px 10px', borderRadius: 9, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: RED, marginBottom: 3 }}>NEEDS WORK</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: text }}>{weakest.label}</div>
              <div style={{ fontSize: 12, fontWeight: 800, color: RED }}>Bottom {weakest.percentile}%</div>
            </div>
          )}
        </div>
      </Card>
    );
  }

  // --- DISCIPLINE REGIME — mirrors DisciplinePage "Discipline by Market Regime" + "Trade Statistics" ---
  if (ui.type === 'discipline-regime') {
    const { regime_discipline = [], trade_stats = null } = ui;
    const regimeColor = r => r === 'BULLISH' ? GREEN : r === 'BEARISH' ? RED : AMBER;
    return (
      <Card>
        {/* Trade Statistics — quick 3-number summary */}
        {trade_stats && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: text, marginBottom: 8 }}>Trade Statistics</div>
            <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center', padding: '10px 0', background: inputBg, borderRadius: 10 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: text }}>{trade_stats.total}</div>
                <div style={{ fontSize: 11, color: sub }}>Total</div>
              </div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: GREEN }}>{trade_stats.wins}</div>
                <div style={{ fontSize: 11, color: sub }}>Wins</div>
              </div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: RED }}>{trade_stats.losses}</div>
                <div style={{ fontSize: 11, color: sub }}>Losses</div>
              </div>
            </div>
          </div>
        )}
        {/* Discipline by Market Regime */}
        <div style={{ fontSize: 11, fontWeight: 700, color: text, marginBottom: 4 }}>Discipline by Market Regime</div>
        <div style={{ fontSize: 10, color: sub, marginBottom: 8 }}>Your avg discipline score & P&L when the market is in each regime</div>
        {regime_discipline.map(r => {
          const rc = regimeColor(r.regime);
          const pnlPos = r.total_pnl >= 0;
          const pc = pnlPos ? GREEN : RED;
          const sc = r.avg_score >= 70 ? GREEN : r.avg_score >= 45 ? AMBER : RED;
          return (
            <div key={r.regime} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 11px', borderRadius: 9, background: inputBg, marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 5, background: `${rc}20`, color: rc }}>{r.regime}</span>
                <span style={{ fontSize: 11, color: sub }}>{r.days}d</span>
              </div>
              <div style={{ display: 'flex', gap: 16 }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 10, color: sub }}>Score</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: sc }}>{r.avg_score}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 10, color: sub }}>P&L</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: pc }}>{pnlPos ? '+' : '-'}₹{Math.abs(r.total_pnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
                </div>
              </div>
            </div>
          );
        })}
      </Card>
    );
  }

  // --- DISCIPLINE BEHAVIOR METRICS — mirrors DisciplinePage "Trader Personality" + "Behavior Metrics" ---
  if (ui.type === 'discipline-behavior') {
    const { profile_type = 'Baseline Trader', profile_score = 72, confidence = 81, signals = [], metrics = [] } = ui;
    const profileColor = profile_score >= 70 ? GREEN : profile_score >= 45 ? AMBER : RED;
    const r = 22, circ = 2 * Math.PI * r;
    return (
      <Card>
        {/* Trader Personality header — matches DisciplinePage layout exactly */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: inputBg, borderRadius: 12, marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ position: 'relative', width: 52, height: 52, flexShrink: 0 }}>
              <svg width={52} height={52} style={{ transform: 'rotate(-90deg)' }}>
                <circle cx={26} cy={26} r={r} fill="none" stroke={border} strokeWidth={4} />
                <circle cx={26} cy={26} r={r} fill="none" stroke={profileColor} strokeWidth={4}
                  strokeDasharray={`${(profile_score / 100) * circ} ${circ}`} strokeLinecap="round" />
              </svg>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 900, color: profileColor }}>🧠</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: sub }}>Trader Personality</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: text }}>{profile_type}</div>
              <div style={{ fontSize: 11, color: sub }}>{confidence}% confidence</div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: profileColor }}>{profile_score}</div>
            <div style={{ fontSize: 11, color: sub }}>Score</div>
          </div>
        </div>
        {/* Why this label — signals */}
        {signals.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: text, marginBottom: 6 }}>Why This Label</div>
            {signals.map((sig, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, padding: '7px 10px', background: inputBg, borderRadius: 8, marginBottom: 5 }}>
                <span style={{ fontSize: 11, color: INDIGO, flexShrink: 0, marginTop: 1 }}>ℹ</span>
                <span style={{ fontSize: 11, color: sub, lineHeight: 1.5 }}>{sig}</span>
              </div>
            ))}
          </div>
        )}
        {/* Behavior Metrics 2×3 grid — tappable */}
        <div style={{ fontSize: 11, fontWeight: 700, color: text, marginBottom: 6 }}>Behavior Metrics</div>
        <HintLabel text="tap any metric to understand what it measures" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {metrics.map(m => {
            const isSel = selMetric === m.label;
            const mc = m.status === 'good' ? GREEN : m.status === 'warn' ? AMBER : m.status === 'bad' ? RED : text;
            return (
              <div key={m.label} onClick={() => setSelMetric(p => p === m.label ? null : m.label)}
                style={{ padding: '8px 10px', borderRadius: 9, background: isSel ? `${mc}15` : inputBg, border: `1.5px solid ${isSel ? mc : border}`, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                <div style={{ fontSize: 11, color: sub, marginBottom: 2 }}>{m.label}</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: mc }}>{m.value}</div>
                {isSel && m.note && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 6, lineHeight: 1.5 }}>{m.note}</div>}
              </div>
            );
          })}
        </div>
      </Card>
    );
  }

  // --- DISCIPLINE PNL IMPACT — mirrors DisciplinePage "What's Costing / Making You Money" ---
  if (ui.type === 'discipline-pnl-impact') {
    const { total_trades = 30, buckets = [] } = ui;
    const maxAbs = Math.max(...buckets.map(b => Math.abs(b.pnl)), 1);
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: text }}>What's Costing / Making You Money</span>
          <span style={{ fontSize: 10, background: inputBg, borderRadius: 6, padding: '2px 7px', color: sub }}>{total_trades} trades</span>
        </div>
        <HintLabel text="tap a row to see individual trades" />
        {buckets.map(bucket => {
          const isPos = bucket.pnl >= 0;
          const barColor = isPos ? GREEN : RED;
          const barW = Math.round((Math.abs(bucket.pnl) / maxAbs) * 100);
          const isSel = selectedField === bucket.key;
          return (
            <div key={bucket.key} style={{ borderRadius: 9, overflow: 'hidden', border: `1.5px solid ${barColor}30`, marginBottom: 7 }}>
              <div onClick={() => setSelectedField(f => f === bucket.key ? null : bucket.key)}
                style={{ padding: '9px 11px', background: `${barColor}08`, cursor: 'pointer', userSelect: 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                  <span style={{ fontSize: 11, color: sub }}>{bucket.label} <span style={{ opacity: 0.6 }}>({bucket.count})</span></span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 800, color: barColor }}>{isPos ? '+' : '-'}₹{Math.abs(bucket.pnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                    <span style={{ fontSize: 10, color: sub }}>{isSel ? '▲' : '▼'}</span>
                  </div>
                </div>
                <div style={{ height: 5, background: border, borderRadius: 3 }}>
                  <div style={{ height: '100%', width: `${barW}%`, background: barColor, borderRadius: 3 }} />
                </div>
              </div>
              {isSel && bucket.trades && (
                <div style={{ borderTop: `1px solid ${border}50` }}>
                  {bucket.trades.map((t, i) => {
                    const tc = t.pnl >= 0 ? GREEN : RED;
                    return (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 11px', background: inputBg, borderBottom: `1px solid ${border}30` }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: text }}>{t.symbol}</div>
                          <div style={{ fontSize: 10, color: sub }}>{t.exit_reason} · Conf {t.confidence}/10</div>
                          {t.lucky && <div style={{ fontSize: 10, color: AMBER, marginTop: 2 }}>⚠ Wrong approach, got lucky</div>}
                          {t.unlucky && <div style={{ fontSize: 10, color: sub, marginTop: 2 }}>✓ Right approach, bad outcome</div>}
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 800, color: tc }}>{t.pnl >= 0 ? '+' : '-'}₹{Math.abs(t.pnl).toLocaleString('en-IN')}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </Card>
    );
  }

  // --- DISCIPLINE INSIGHTS — mirrors DisciplinePage "P&L by Time of Day" + "Does Your Confidence Predict Outcome?" ---
  if (ui.type === 'discipline-insights') {
    const { time_of_day = [], confidence_tiers = [], confidence_insight = '' } = ui;
    const maxTimeAbs = Math.max(...time_of_day.map(h => Math.abs(h.avg_pnl)), 1);
    const bestHour   = [...time_of_day].sort((a, b) => b.avg_pnl - a.avg_pnl)[0];
    const worstHour  = [...time_of_day].sort((a, b) => a.avg_pnl - b.avg_pnl)[0];
    const tierColors = { low: INDIGO, mid: AMBER, high: GREEN };
    return (
      <Card>
        {/* P&L by Time of Day */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: text }}>P&L by Time of Day</span>
          <span style={{ fontSize: 13, color: sub }}>🕐</span>
        </div>
        {/* Best / Worst hour chips */}
        {bestHour && worstHour && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 10 }}>
            <div style={{ padding: '8px 10px', borderRadius: 9, background: 'rgba(22,199,132,0.08)', border: '1px solid rgba(22,199,132,0.2)', textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: sub, marginBottom: 2 }}>Best Hour</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: GREEN }}>{bestHour.label}</div>
              <div style={{ fontSize: 11, color: GREEN }}>avg +₹{Math.abs(bestHour.avg_pnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
            </div>
            <div style={{ padding: '8px 10px', borderRadius: 9, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: sub, marginBottom: 2 }}>Worst Hour</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: RED }}>{worstHour.label}</div>
              <div style={{ fontSize: 11, color: RED }}>avg -₹{Math.abs(worstHour.avg_pnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
            </div>
          </div>
        )}
        {/* Horizontal bar chart per hour */}
        <div style={{ marginBottom: 14 }}>
          {time_of_day.map(h => {
            const isPos = h.avg_pnl >= 0;
            const bc = isPos ? GREEN : RED;
            const bw = Math.round((Math.abs(h.avg_pnl) / maxTimeAbs) * 100);
            return (
              <div key={h.hour} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
                <span style={{ fontSize: 11, color: sub, width: 38, textAlign: 'right', flexShrink: 0 }}>{h.label}</span>
                <div style={{ flex: 1, height: 20, borderRadius: 5, background: inputBg, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${bw}%`, background: `${bc}40`, borderRadius: 5 }} />
                  <span style={{ position: 'absolute', left: 6, top: 0, bottom: 0, display: 'flex', alignItems: 'center', fontSize: 10, fontWeight: 600, color: bc }}>
                    {isPos ? '+' : '-'}₹{Math.abs(h.avg_pnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })} avg · {h.trade_count}t
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Confidence Calibration */}
        <div style={{ borderTop: `1px solid ${border}`, paddingTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: text }}>Does Confidence Predict Outcome?</span>
            <span style={{ fontSize: 13, color: sub }}>⚡</span>
          </div>
          {confidence_insight && (
            <div style={{ padding: '7px 10px', borderRadius: 8, background: `${INDIGO}15`, border: `1px solid ${INDIGO}35`, marginBottom: 8 }}>
              <span style={{ fontSize: 11, color: INDIGO, lineHeight: 1.5 }}>{confidence_insight}</span>
            </div>
          )}
          {confidence_tiers.map(t => {
            const isPos = t.avg_pnl >= 0;
            const pc = isPos ? GREEN : RED;
            const tc = tierColors[t.key] || INDIGO;
            return (
              <div key={t.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', borderRadius: 9, background: inputBg, marginBottom: 6 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: tc }}>{t.label}</div>
                  <div style={{ fontSize: 10, color: sub }}>{t.count} trades</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: pc }}>{isPos ? '+' : '-'}₹{Math.abs(t.avg_pnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })} avg</div>
                  <div style={{ fontSize: 10, color: sub }}>total {t.total_pnl < 0 ? '-' : '+'}₹{Math.abs(t.total_pnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    );
  }

  // --- DISCIPLINE WHY CHANGED — mirrors DisciplinePage "Why Score Changed" + "Recent Trades Impact" ---
  if (ui.type === 'discipline-why-changed') {
    const { entries = [], recent_trades = [] } = ui;
    return (
      <Card>
        <div style={{ fontSize: 12, fontWeight: 700, color: text, marginBottom: 10 }}>Why Score Changed</div>
        <HintLabel text="tap an entry to see which components moved and why" />
        {entries.map((entry, idx) => {
          const improved = entry.delta > 0;
          const dc = improved ? GREEN : RED;
          const isSel = selectedField === `why-${idx}`;
          return (
            <div key={idx} onClick={() => setSelectedField(f => f === `why-${idx}` ? null : `why-${idx}`)}
              style={{ borderRadius: 9, overflow: 'hidden', border: `1.5px solid ${dc}30`, marginBottom: 7, cursor: 'pointer', userSelect: 'none' }}>
              {/* Row header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 11px', background: `${dc}08` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: `${dc}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 13 }}>
                    {improved ? '▲' : '▼'}
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 14, fontWeight: 800, color: dc }}>{improved ? '+' : ''}{entry.delta}</span>
                      <span style={{ fontSize: 12, color: sub }}>→</span>
                      <span style={{ fontSize: 14, fontWeight: 800, color: text }}>{entry.score}</span>
                      {entry.symbol && <span style={{ fontSize: 11, background: `${INDIGO}20`, color: INDIGO, borderRadius: 5, padding: '1px 6px' }}>{entry.symbol}</span>}
                    </div>
                    <div style={{ fontSize: 11, color: sub }}>{entry.time}</div>
                  </div>
                </div>
                <span style={{ fontSize: 11, color: sub }}>{isSel ? '▲' : '▼'}</span>
              </div>
              {/* Expanded: component changes + reasons */}
              {isSel && (
                <div style={{ padding: '8px 11px', background: inputBg, borderTop: `1px solid ${border}` }}>
                  {entry.component_changes && entry.component_changes.map((c, ci) => {
                    const cc = c.delta > 0 ? GREEN : RED;
                    return (
                      <div key={ci} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 6, marginBottom: 6, borderBottom: `1px solid ${border}30` }}>
                        <span style={{ fontSize: 11, color: sub }}>{c.label}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ fontSize: 11, color: sub }}>{c.before}</span>
                          <span style={{ fontSize: 11, color: sub }}>→</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: cc }}>{c.after} ({c.delta > 0 ? '+' : ''}{c.delta})</span>
                        </div>
                      </div>
                    );
                  })}
                  {entry.reasons && entry.reasons.map((r, ri) => (
                    <div key={ri} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: r.good ? GREEN : RED, flexShrink: 0 }}>{r.good ? '✓' : '✗'}</span>
                      <span style={{ fontSize: 11, color: sub, lineHeight: 1.5 }}>{r.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {/* Recent Trades Impact — matches DisciplinePage section */}
        {recent_trades.length > 0 && (
          <div style={{ marginTop: 14, borderTop: `1px solid ${border}`, paddingTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: text, marginBottom: 8 }}>Recent Trades Impact</div>
            {recent_trades.map((t, i) => {
              const tc = t.pnl >= 0 ? GREEN : RED;
              const isImpulse = t.reason === 'Impulse' || t.reason === 'Tip';
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 9, background: inputBg, marginBottom: 5 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: `${tc}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 13 }}>
                      {t.pnl >= 0 ? '▲' : '▼'}
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: text }}>{t.symbol}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 4, background: isImpulse ? `${RED}20` : `${INDIGO}20`, color: isImpulse ? RED : INDIGO }}>{t.reason}</span>
                        <span style={{ fontSize: 10, color: sub }}>Conf: {t.confidence}/10</span>
                        {t.journal && <span style={{ fontSize: 11, color: sub }}>📝</span>}
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: tc }}>{t.pnl >= 0 ? '+' : '-'}₹{Math.abs(t.pnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    );
  }

  // --- DISCIPLINE COMPONENTS + TREND — mirrors DisciplinePage "Component Details" + "Score Trend" ---
  if (ui.type === 'discipline-components') {
    const { components = [], trend = [] } = ui;
    const COMP_ICONS = { stop_loss: '🛡️', risk_reward: '🎯', strategy: '🧠', emotion: '⚠️', frequency: '🕐', confidence: '⚡', journal: '📒', sl_violation: '🚫', risk_profile: '📊' };
    const COMP_LABELS = { stop_loss: { name: 'Stop Loss Discipline', weight: '20%' }, risk_reward: { name: 'Risk-Reward Planning', weight: '15%' }, strategy: { name: 'Strategy Consistency', weight: '15%' }, emotion: { name: 'Emotional Control', weight: '10%' }, frequency: { name: 'Trade Frequency', weight: '10%' }, confidence: { name: 'Confidence Alignment', weight: '10%' }, journal: { name: 'Journal Discipline', weight: '5%' }, sl_violation: { name: 'SL Violations', weight: '5%' }, risk_profile: { name: 'Risk Profile Adherence', weight: '10%' } };
    // Mini sparkline path from trend data
    const sparkPoints = trend.length > 1
      ? (() => {
          const min = Math.min(...trend.map(t => t.score));
          const max = Math.max(...trend.map(t => t.score), min + 1);
          const W = 200, H = 44;
          const pts = trend.map((t, i) => {
            const x = (i / (trend.length - 1)) * W;
            const y = H - ((t.score - min) / (max - min)) * H;
            return `${x},${y}`;
          });
          return pts.join(' ');
        })()
      : null;
    return (
      <Card>
        {/* Score Trend sparkline */}
        {sparkPoints && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: text, marginBottom: 6 }}>Score Trend</div>
            <div style={{ position: 'relative', background: inputBg, borderRadius: 10, padding: '8px 8px 4px', overflow: 'hidden' }}>
              <svg width="100%" height={50} viewBox={`0 0 200 44`} preserveAspectRatio="none">
                <polyline points={sparkPoints} fill="none" stroke={INDIGO} strokeWidth={2} strokeLinejoin="round" />
                {trend.map((t, i) => {
                  const min = Math.min(...trend.map(x => x.score));
                  const max = Math.max(...trend.map(x => x.score), min + 1);
                  const x = (i / (trend.length - 1)) * 200;
                  const y = 44 - ((t.score - min) / (max - min)) * 44;
                  return <circle key={i} cx={x} cy={y} r={3} fill={INDIGO} />;
                })}
              </svg>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                {trend.map((t, i) => <span key={i} style={{ fontSize: 9, color: sub }}>{t.label}</span>)}
              </div>
            </div>
          </div>
        )}
        {/* Component Details list — tappable */}
        <div style={{ fontSize: 11, fontWeight: 700, color: text, marginBottom: 6 }}>Component Details</div>
        <HintLabel text="tap a component to see its weight and what moves it" />
        {components.map(c => {
          const isSel = selectedField === c.key;
          const sc = c.score >= 70 ? GREEN : c.score >= 45 ? AMBER : RED;
          const meta = COMP_LABELS[c.key] || { name: c.key, weight: '—' };
          const icon = COMP_ICONS[c.key] || '📌';
          return (
            <div key={c.key} onClick={() => setSelectedField(f => f === c.key ? null : c.key)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 11px', borderRadius: 9, background: isSel ? `${sc}12` : inputBg, border: `1.5px solid ${isSel ? sc : border}`, marginBottom: 6, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16 }}>{icon}</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: text }}>{meta.name}</div>
                  <div style={{ fontSize: 10, color: sub }}>Weight: {meta.weight}</div>
                  {isSel && c.note && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 5, lineHeight: 1.5 }}>{c.note}</div>}
                </div>
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, color: sc, flexShrink: 0, marginLeft: 8 }}>{c.score}</div>
            </div>
          );
        })}
      </Card>
    );
  }

  // --- ANALYTICS GRID ---
  if (ui.type === 'analytics-grid') {
    const metricInfo = {
      'Total P&L':   { icon: '💰', color: GREEN,  body: 'Total profit or loss across all your trades. But this number alone is misleading — you need to see HOW you made it. A trader who makes ₹10K on 3 big wins but loses on 20 others has a fragile edge.' },
      'Win Rate':    { icon: '🎯', color: INDIGO, body: 'Percentage of trades that were profitable. A 40% win rate can still be very profitable if your average win is 2.5× your average loss. Never read win rate in isolation.' },
      'Avg R:R':     { icon: '⚖️', color: INDIGO, body: 'Average reward ÷ risk ratio across all trades. Above 1.5 = healthy. Below 1.0 = you\'re losing more on losers than you\'re making on winners. This is the most important number to fix first.' },
      'Total Trades':{ icon: '🔢', color: AMBER,  body: 'How many trades you\'ve placed. Under 30 trades = sample size is too small for any pattern to be reliable. Patterns become meaningful above 50 trades.' },
      'Best Setup':  { icon: '🏆', color: GREEN,  body: 'Your highest win-rate trade type. This is your edge — the setup where your skill consistently shows up. Focus more capital and attention on this setup.' },
      'Avg Drawdown':{ icon: '📉', color: RED,    body: 'Average loss on losing trades. If this is larger than your average win, you cannot be profitable long term no matter how high your win rate. Tighten SL placement.' },
    };
    return (
      <Card>
        <div style={{ fontSize: 10, color: sub, marginBottom: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Performance · <span style={{ color: AMBER }}>Last 30 days</span>
        </div>
        <HintLabel text="tap any metric to understand what it means" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 4 }}>
          {ui.metrics.map(m => {
            const isSel = selMetric === m.label;
            const info = metricInfo[m.label];
            const c = info?.color || INDIGO;
            return (
              <div key={m.label} onClick={() => setSelMetric(p => p === m.label ? null : m.label)}
                style={{ borderRadius: 10, padding: '10px 12px', background: isSel ? `${c}15` : inputBg, border: `1.5px solid ${isSel ? c : border}`, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                <div style={{ fontSize: 9, color: sub, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{m.label}</div>
                <div style={{ fontSize: m.label === 'Best Setup' ? 11 : 16, fontWeight: 800, color: m.positive ? GREEN : m.warn ? RED : text }}>{m.value}</div>
              </div>
            );
          })}
        </div>
        {selMetric && metricInfo[selMetric] && (
          <Explainer color={metricInfo[selMetric].color} icon={metricInfo[selMetric].icon} title={selMetric} body={metricInfo[selMetric].body} />
        )}
        {ui.showCurve && (
          <div style={{ marginTop: 8, padding: '8px 10px', background: inputBg, borderRadius: 8, border: `1px solid ${border}` }}>
            <div style={{ fontSize: 9, color: sub, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Equity Curve</div>
            <svg viewBox="0 0 200 50" style={{ width: '100%', height: 50 }}>
              <polyline points="0,45 25,40 50,38 75,30 100,32 125,22 150,18 175,12 200,8"
                fill="none" stroke={GREEN} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <polyline points="0,45 25,40 50,38 75,30 100,32 125,22 150,18 175,12 200,8"
                fill="url(#grad)" stroke="none" opacity="0.2" />
              <defs><linearGradient id="grad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={GREEN} /><stop offset="100%" stopColor="transparent" /></linearGradient></defs>
            </svg>
            <div style={{ fontSize: 9, color: sub, textAlign: 'center', marginTop: 2 }}>Smooth upward slope = consistent edge. Spikes + crashes = erratic.</div>
          </div>
        )}
      </Card>
    );
  }

  // --- REGIME CARD ---
  if (ui.type === 'regime-card') {
    const regimeInfo = {
      'Bullish Trend':   { color: GREEN,  icon: '📈', strategies: ['Breakouts', 'Pullback to support', 'Momentum longs'], avoid: 'Shorting against trend' },
      'Bearish Trend':   { color: RED,    icon: '📉', strategies: ['Resistance rejections', 'Breakdown shorts', 'Put options'], avoid: 'New longs on bounces' },
      'Sideways/Range':  { color: AMBER,  icon: '↔️', strategies: ['Range fades', 'Sell resistance / Buy support', 'Iron condors'], avoid: 'Breakout trades — most will fail' },
      'High Volatility': { color: PURPLE, icon: '⚡', strategies: ['Smaller position sizes', 'Wait for clear setup', 'Straddles/strangles'], avoid: 'Chasing moves — gap risk is high' },
    };
    const breadthInfo = {
      advancing: { icon: '📊', body: 'Advancing stocks > declining stocks. Broad participation = healthy regime. When most stocks are moving with the trend, the move is more sustainable and individual stock picks have higher success rates.' },
      declining:  { icon: '📊', body: 'More stocks declining than advancing. Even if the index is up, narrow breadth = weak rally driven by a few heavyweights. This often precedes a reversal — be cautious with new longs.' },
    };
    const regime = ui.regime || 'Bullish Trend';
    const info = regimeInfo[regime] || regimeInfo['Bullish Trend'];
    const isBreadthSel = selectedField === 'breadth';
    const breadthDir = ui.advancing > ui.declining ? 'advancing' : 'declining';
    return (
      <Card>
        <HintLabel text="tap the regime or breadth to understand what to do" />
        <div onClick={() => setSelectedField(f => f === 'regime' ? null : 'regime')}
          style={{ borderRadius: 11, padding: '12px 14px', background: selectedField === 'regime' ? `${info.color}18` : `${info.color}10`, border: `2px solid ${info.color}${selectedField === 'regime' ? '' : '55'}`, cursor: 'pointer', userSelect: 'none', marginBottom: 10, transition: 'all 0.18s' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 10, color: sub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 }}>Current Regime</div>
              <div style={{ fontSize: 15, fontWeight: 900, color: info.color }}>{info.icon} {regime}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9, color: sub, textTransform: 'uppercase', marginBottom: 2 }}>Strength</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: info.color }}>{ui.strength || 'Moderate'}</div>
            </div>
          </div>
          {selectedField === 'regime' && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${info.color}30` }}>
              <div style={{ fontSize: 10, color: GREEN, fontWeight: 700, marginBottom: 4 }}>✓ Works in this regime:</div>
              {info.strategies.map(s => <div key={s} style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginBottom: 2 }}>• {s}</div>)}
              <div style={{ fontSize: 10, color: RED, fontWeight: 700, marginTop: 6, marginBottom: 4 }}>✗ Avoid:</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>• {info.avoid}</div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 7, marginBottom: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 9, color: sub, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Advancing</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: GREEN }}>{ui.advancing || 312}</div>
          </div>
          <div style={{ width: 1, background: border }} />
          <div style={{ flex: 1, textAlign: 'right' }}>
            <div style={{ fontSize: 9, color: sub, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Declining</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: RED }}>{ui.declining || 188}</div>
          </div>
        </div>
        <div onClick={() => setSelectedField(f => f === 'breadth' ? null : 'breadth')}
          style={{ padding: '7px 11px', borderRadius: 8, background: isBreadthSel ? `${GREEN}12` : `${GREEN}08`, border: `1px solid ${isBreadthSel ? GREEN : `${GREEN}30`}`, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
          <span style={{ fontSize: 10, color: GREEN, fontWeight: 600 }}>Market Breadth: {breadthDir === 'advancing' ? '✓ Strong — broad participation' : '⚠ Narrow — selective moves only'}</span>
          {isBreadthSel && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 6, lineHeight: 1.5 }}>{breadthInfo[breadthDir].icon} {breadthInfo[breadthDir].body}</div>}
        </div>
      </Card>
    );
  }

  // --- OVERFITTING CARD ---
  if (ui.type === 'overfit-card') {
    const signs = ui.signs || [];
    const fixes = ui.fixes || [];
    return (
      <Card>
        <div style={{ fontSize: 10, color: RED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>⚠ Overfitting Warning Signs</div>
        <HintLabel text="tap any sign to understand why it is dangerous" />
        {signs.map(s => {
          const isSel = selectedTag === s.label;
          return (
            <div key={s.label} onClick={() => setSelectedTag(t => t === s.label ? null : s.label)}
              style={{ borderRadius: 9, padding: '8px 11px', background: isSel ? `${RED}15` : inputBg, border: `1.5px solid ${isSel ? RED : border}`, marginBottom: 7, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: isSel ? RED : text }}>⚠ {s.label}</div>
              {isSel && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 6, lineHeight: 1.5 }}>{s.body}</div>}
            </div>
          );
        })}
        <div style={{ fontSize: 10, color: GREEN, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginTop: 10, marginBottom: 6 }}>✓ The Fix: Walk-Forward Test</div>
        {fixes.map(f => (
          <div key={f} style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}>• {f}</div>
        ))}
      </Card>
    );
  }

  // --- LIVE SIM CARD ---
  if (ui.type === 'live-sim') {
    const stages = ui.stages || [];
    return (
      <Card>
        <div style={{ fontSize: 10, color: INDIGO, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>Graduation Path to Live Trading</div>
        <HintLabel text="tap a stage to see what to check before moving forward" />
        {stages.map((s, i) => {
          const isSel = selectedField === s.label;
          const c = s.done ? GREEN : i === stages.findIndex(x => !x.done) ? AMBER : border;
          return (
            <div key={s.label} onClick={() => setSelectedField(f => f === s.label ? null : s.label)}
              style={{ display: 'flex', gap: 10, marginBottom: 10, cursor: 'pointer', userSelect: 'none' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: c + '22', border: `2px solid ${c}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: c, fontWeight: 800, flexShrink: 0 }}>{s.done ? '✓' : i + 1}</div>
                {i < stages.length - 1 && <div style={{ width: 2, flex: 1, background: border, marginTop: 3 }} />}
              </div>
              <div style={{ flex: 1, paddingBottom: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: s.done ? GREEN : text, marginBottom: 2 }}>{s.label}</div>
                <div style={{ fontSize: 10, color: sub }}>{s.check}</div>
                {isSel && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 6, lineHeight: 1.5, padding: '6px 8px', background: `${c}10`, borderRadius: 7 }}>{s.detail}</div>}
              </div>
            </div>
          );
        })}
      </Card>
    );
  }

  // --- LIMITS PANEL ---
  if (ui.type === 'limits-panel') {
    const limits = ui.limits || [];
    return (
      <Card>
        <div style={{ fontSize: 10, color: sub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Today's Limits Panel</div>
        <HintLabel text="tap any limit to understand where it comes from" />
        {limits.map(l => {
          const isSel = selectedField === l.label;
          const c = l.color || INDIGO;
          return (
            <div key={l.label} onClick={() => setSelectedField(f => f === l.label ? null : l.label)}
              style={{ borderRadius: 9, padding: '9px 12px', background: isSel ? `${c}15` : inputBg, border: `1.5px solid ${isSel ? c : border}`, marginBottom: 7, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isSel ? 6 : 0 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: text }}>{l.label}</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: c }}>{l.value}</span>
              </div>
              {isSel && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>{l.detail}</div>}
            </div>
          );
        })}
      </Card>
    );
  }

  // --- DAY PLAN ---
  if (ui.type === 'day-plan') {
    const slotInfo = {
      'Account Size':    { icon: '💰', color: INDIGO, body: 'Your total active capital for trading today. This is the base number all calculations derive from. If you deposited ₹2L but want to use only ₹1L today, enter ₹1L.' },
      'Trades Today':    { icon: '🔢', color: AMBER,  body: 'How many trades do you plan to take today? Fewer trades = more focus per trade. More trades = more opportunities but more friction and emotional fatigue. Most good traders do 1–3 quality trades per day.' },
      'Risk Per Trade':  { icon: '⚖️', color: RED,    body: 'What % of your account are you willing to lose on a single trade? 1% is the professional standard. At ₹1L capital, 1% = ₹1,000 max loss per trade. This number, combined with your SL distance, determines quantity.' },
      'Capital/Trade':   { icon: '📊', color: GREEN,  body: 'Your total capital divided by number of trades. This is the maximum you can deploy per trade — not the risk amount. If Risk Per Trade is 1% of ₹1L = ₹1,000, and your SL is 20 points, you can buy 50 shares at any price.' },
    };
    return (
      <Card>
        <div style={{ fontSize: 10, color: INDIGO, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Day Plan Setup</div>
        <HintLabel text="tap any field to understand why it matters" />
        {ui.fields.map(f => {
          const isSel = selectedField === f.label;
          const info = slotInfo[f.label];
          const c = info?.color || INDIGO;
          return (
            <div key={f.label} onClick={() => setSelectedField(ff => ff === f.label ? null : f.label)}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 12px', borderRadius: 9, background: isSel ? `${c}15` : inputBg, border: `1.5px solid ${isSel ? c : border}`, marginBottom: 7, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s', flexWrap: 'wrap', gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: text }}>{f.label}</span>
              <span style={{ fontSize: 13, fontWeight: 800, color: c }}>{f.value}</span>
              {isSel && info && <div style={{ width: '100%', fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5, marginTop: 4 }}>{info.icon} {info.body}</div>}
            </div>
          );
        })}
        <div style={{ padding: '8px 11px', background: `${GREEN}10`, borderRadius: 8, border: `1px solid ${GREEN}30`, marginTop: 4 }}>
          <span style={{ fontSize: 11, color: GREEN, fontWeight: 600 }}>✓ Plan first → then trade. Never the other way around.</span>
        </div>
      </Card>
    );
  }

  // --- ACT SCENARIO ---
  if (ui.type === 'act-scenario') {
    const ORANGE = '#f97316';
    // Candlestick data: [open, close, high, low] normalised to 0–100
    const candles = [
      [40, 45, 48, 37], [45, 42, 47, 40], [42, 55, 57, 41],
      [55, 52, 58, 50], [52, 60, 63, 51], [60, 58, 64, 55],
      [58, 70, 72, 57],
    ];
    const W = 200, H = 60, cw = 20, gap = 8;
    const scaleY = v => H - (v / 80) * H;

    const decisionInfo = {
      Buy:  { color: GREEN,  icon: '📈', body: 'You are predicting the stock will go up from here. For this to be a good Buy, you need: a clear pattern (e.g. breakout, pullback to support), volume confirming the move, and a defined SL below recent structure.' },
      Sell: { color: RED,    icon: '📉', body: 'You are predicting the stock will fall from here. For this to be a good Sell/Short, you need: price rejecting resistance, bearish candle pattern, and volume increasing on the down move.' },
      Skip: { color: AMBER,  icon: '⏭️', body: 'No clear setup = no trade. Skipping is a valid and often correct decision. In ACT, a well-reasoned Skip scores higher than a poorly-reasoned Buy or Sell. Not every chart needs a decision.' },
    };

    const contextInfo = {
      'Volume':  { icon: '📊', color: AMBER,  body: 'Volume is 2.3× the 20-day average. High volume on an upward move = strong buying conviction. Breakouts on high volume are more reliable — price is less likely to reverse back.' },
      'Regime':  { icon: '📈', color: GREEN,  body: 'Market is in a Bullish Trend regime. This means long setups have higher success rates today. In a bullish regime, pullbacks to support tend to hold and continue upward.' },
      'Sector':  { icon: '🏭', color: INDIGO, body: 'IT sector is leading today with +1.8% gain. This stock is an IT company — trading in the direction of your sector adds tailwind. Sector momentum + individual setup = stronger edge.' },
      'Pattern': { icon: '📐', color: PURPLE, body: 'The chart shows a Cup & Handle pattern — a bullish continuation setup. Price consolidated in a U-shape, then formed a small handle (flag), and is now breaking out. Classic high-probability setup.' },
    };

    const scoreInfo = {
      'Pattern ID':    { icon: '📐', color: INDIGO, body: 'Did you correctly identify the chart pattern before deciding? Recognising patterns consistently is a learnable skill — ACT tracks which patterns you identify correctly vs miss over time.' },
      'SL Placement':  { icon: '🛡️', color: GREEN,  body: 'Was your suggested stop loss at a logical level? Good SL = just below the pattern\'s key support (e.g. below the handle of a Cup & Handle). A logical SL gives the trade room to breathe while capping your risk.' },
      'R:R Quality':   { icon: '⚖️', color: AMBER,  body: 'Did you target at least 2× your risk? If SL is 20 points away, your target should be at least 40 points. Trades with R:R below 1:1.5 are penalised in ACT scoring — they don\'t survive long-term.' },
      'Decision Made': { icon: '🎯', color: ORANGE, body: 'Did you make a clear call — Buy, Sell, or Skip — with a reason? Indecision is penalised. A well-reasoned wrong decision scores better than an unexplained correct one.' },
    };

    const historyInfo = {
      Breakout:  { icon: '🚀', color: GREEN, body: 'Your breakout identification is strong — 72% accuracy. This is your best setup. When you see a breakout with volume confirmation, trust it and size appropriately.' },
      Pullback:  { icon: '📉', color: AMBER, body: 'You identify pullbacks correctly only 44% of the time. Common mistake: entering too early before the pullback stabilises. Wait for a bullish candle confirmation at the support level.' },
      Reversal:  { icon: '🔄', color: RED,   body: 'Reversal setups are your weakest area — 28% accuracy. These are genuinely harder to identify. Consider skipping reversal setups until your pattern recognition improves through more ACT practice.' },
    };

    if (ui.mode === 'decision') {
      const picked = selAlgo;
      return (
        <Card>
          <div style={{ fontSize: 10, color: sub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>
            RELIANCE · NSE · <span style={{ color: ORANGE }}>Decision point</span>
          </div>
          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H, marginBottom: 8 }}>
            {candles.map(([o, c, h, l], i) => {
              const x = i * (cw + gap) + 10;
              const isUp = c >= o;
              const cc = isUp ? GREEN : RED;
              return (
                <g key={i}>
                  <line x1={x + cw/2} y1={scaleY(h)} x2={x + cw/2} y2={scaleY(l)} stroke={cc} strokeWidth={1.5} />
                  <rect x={x} y={scaleY(Math.max(o,c))} width={cw} height={Math.max(2, Math.abs(scaleY(o)-scaleY(c)))} fill={cc} rx={2} />
                </g>
              );
            })}
            <line x1={0} y1={scaleY(72)} x2={W} y2={scaleY(72)} stroke={AMBER} strokeWidth={1} strokeDasharray="4 3" />
            <text x={W - 2} y={scaleY(72) - 3} fontSize={8} fill={AMBER} textAnchor="end">Resistance</text>
          </svg>
          <HintLabel text="what is your call? tap Buy, Sell, or Skip" />
          <div style={{ display: 'flex', gap: 7, marginBottom: 8 }}>
            {['Buy', 'Sell', 'Skip'].map(d => {
              const info = decisionInfo[d];
              const isSel = picked === d;
              return (
                <div key={d} onClick={() => setSelAlgo(p => p === d ? null : d)}
                  style={{ flex: 1, padding: '10px 0', borderRadius: 10, textAlign: 'center', fontSize: 13, fontWeight: 800, cursor: 'pointer', userSelect: 'none', background: isSel ? `${info.color}22` : inputBg, border: `2px solid ${isSel ? info.color : border}`, color: isSel ? info.color : sub, transition: 'all 0.18s', boxShadow: isSel ? `0 0 12px ${info.color}44` : 'none' }}>
                  {d}
                </div>
              );
            })}
          </div>
          {picked && decisionInfo[picked] && (
            <Explainer color={decisionInfo[picked].color} icon={decisionInfo[picked].icon} title={`${picked} — what makes this valid?`} body={decisionInfo[picked].body} />
          )}
        </Card>
      );
    }

    if (ui.mode === 'context') {
      return (
        <Card>
          <div style={{ fontSize: 10, color: sub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>
            INFY · Context Panel <span style={{ color: ORANGE }}>— read before deciding</span>
          </div>
          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H, marginBottom: 10 }}>
            {candles.map(([o, c, h, l], i) => {
              const x = i * (cw + gap) + 10;
              const isUp = c >= o;
              const cc = isUp ? GREEN : RED;
              return (
                <g key={i}>
                  <line x1={x + cw/2} y1={scaleY(h)} x2={x + cw/2} y2={scaleY(l)} stroke={cc} strokeWidth={1.5} />
                  <rect x={x} y={scaleY(Math.max(o,c))} width={cw} height={Math.max(2, Math.abs(scaleY(o)-scaleY(c)))} fill={cc} rx={2} />
                </g>
              );
            })}
          </svg>
          <HintLabel text="tap each context item to understand why it matters" />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {['Volume', 'Regime', 'Sector', 'Pattern'].map(k => {
              const info = contextInfo[k];
              const isSel = selectedField === k;
              return (
                <div key={k} onClick={() => setSelectedField(f => f === k ? null : k)}
                  style={{ padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer', userSelect: 'none', background: isSel ? `${info.color}20` : inputBg, border: `1.5px solid ${isSel ? info.color : border}`, color: isSel ? info.color : sub, transition: 'all 0.18s' }}>
                  {info.icon} {k}
                </div>
              );
            })}
          </div>
          {selectedField && contextInfo[selectedField] && (
            <Explainer color={contextInfo[selectedField].color} icon={contextInfo[selectedField].icon} title={selectedField} body={contextInfo[selectedField].body} />
          )}
        </Card>
      );
    }

    if (ui.mode === 'score') {
      const components = [
        { label: 'Pattern ID',   score: 8, max: 10 },
        { label: 'SL Placement', score: 7, max: 10 },
        { label: 'R:R Quality',  score: 6, max: 10 },
        { label: 'Decision Made',score: 5, max: 10 },
      ];
      const total = components.reduce((s, c) => s + c.score, 0);
      const totalMax = components.reduce((s, c) => s + c.max, 0);
      const grade = total >= 32 ? { label: 'Excellent', color: GREEN } : total >= 24 ? { label: 'Good', color: INDIGO } : total >= 16 ? { label: 'Needs Work', color: AMBER } : { label: 'Poor Process', color: RED };
      return (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 10, color: sub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>ACT Score Breakdown</div>
            <div style={{ fontSize: 13, fontWeight: 900, color: grade.color }}>{total}/{totalMax} · {grade.label}</div>
          </div>
          <HintLabel text="tap each component to understand how it is scored" />
          {components.map(comp => {
            const isSel = selectedField === comp.label;
            const info = scoreInfo[comp.label];
            const c = info?.color || ORANGE;
            const pct = comp.score / comp.max;
            return (
              <div key={comp.label} onClick={() => setSelectedField(f => f === comp.label ? null : comp.label)}
                style={{ borderRadius: 9, padding: '8px 11px', background: isSel ? `${c}15` : inputBg, border: `1.5px solid ${isSel ? c : border}`, marginBottom: 7, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: isSel ? c : text }}>{comp.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: c }}>{comp.score}/{comp.max}</span>
                </div>
                <div style={{ height: 4, background: border, borderRadius: 2 }}>
                  <div style={{ height: '100%', width: `${pct * 100}%`, background: c, borderRadius: 2 }} />
                </div>
                {isSel && info && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 8, lineHeight: 1.5 }}>{info.icon} {info.body}</div>}
              </div>
            );
          })}
        </Card>
      );
    }

    if (ui.mode === 'checklist') {
      const checks = [
        { id: 'trend',   label: 'What is the trend?',           hint: 'Look at the last 20 candles. Are they making higher highs and higher lows (uptrend) or lower lows (downtrend)? Only trade in the direction of the trend.', options: ['Uptrend ↑', 'Downtrend ↓', 'Sideways →'], good: 'Uptrend ↑' },
        { id: 'pattern', label: 'Is there a clear pattern?',    hint: 'Look for: Breakout above resistance, Pullback to support, Bullish engulfing candle, or Cup & Handle. If you cannot name the pattern, Skip — "it looks good" is not a pattern.', options: ['Breakout', 'Pullback', 'Engulfing', 'No pattern'], good: 'Breakout' },
        { id: 'volume',  label: 'Does volume confirm the move?',hint: 'Look at the volume bars below the chart. A valid breakout needs volume 1.5× or more above average. Low volume = weak move = Skip or Wait.', options: ['High (>1.5×)', 'Normal (1–1.5×)', 'Low (<1×)'], good: 'High (>1.5×)' },
        { id: 'sl',      label: 'Where is your Stop Loss?',     hint: 'Your SL must be at a logical structure level — below the last swing low, below the pattern base, or below the previous day low. Never place SL randomly. If no clean SL level exists, the trade does not qualify.', options: ['Below swing low', 'Below pattern base', 'Prev day low', 'No clean level'], good: 'Below swing low' },
        { id: 'rr',      label: 'Is R:R at least 1:2?',        hint: 'Calculate: (Target − Entry) ÷ (Entry − SL). If the answer is below 2.0, the trade does not meet the minimum quality bar. Skip it. A trade with 1:1.2 R:R will not survive long-term even with a 60% win rate.', options: ['Yes (≥2.0)', 'Borderline (1.5–2.0)', 'No (<1.5)'], good: 'Yes (≥2.0)' },
      ];
      return (
        <Card>
          <div style={{ fontSize: 10, color: '#f97316', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Pre-Trade Checklist — ask these 5 questions</div>
          <HintLabel text="tap any question to understand what to look for" />
          {checks.map((c, i) => {
            const isSel = selectedField === c.id;
            return (
              <div key={c.id} onClick={() => setSelectedField(f => f === c.id ? null : c.id)}
                style={{ borderRadius: 10, padding: '9px 12px', background: isSel ? `${INDIGO}15` : inputBg, border: `1.5px solid ${isSel ? INDIGO : border}`, marginBottom: 7, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', background: `${INDIGO}20`, border: `1.5px solid ${INDIGO}60`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: INDIGO, flexShrink: 0, marginTop: 1 }}>{i + 1}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: isSel ? INDIGO : text, marginBottom: isSel ? 8 : 0 }}>{c.label}</div>
                    {isSel && (
                      <>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55, marginBottom: 8 }}>{c.hint}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                          {c.options.map(opt => (
                            <div key={opt} style={{ padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: opt === c.good ? `${GREEN}20` : `${RED}12`, border: `1px solid ${opt === c.good ? GREEN : `${RED}30`}`, color: opt === c.good ? GREEN : 'rgba(255,255,255,0.4)' }}>
                              {opt === c.good ? '✓ ' : '✗ '}{opt}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </Card>
      );
    }

    if (ui.mode === 'fill-form') {
      const ORANGE = '#f97316';
      const ENTER_TAGS = ['Breakout with volume', 'Trend aligned', 'Near support', 'RSI in range', 'MACD crossover', 'Scanner signal'];
      const SKIP_TAGS  = ['Volume too low', 'Near resistance', 'RSI overbought', 'No clean SL level', 'Poor R:R'];
      const WAIT_TAGS  = ['Waiting for pullback', 'Waiting for volume', 'Market unclear'];
      const decisions = ['Enter', 'Wait', 'Skip'];
      const decColors = { Enter: GREEN, Wait: AMBER, Skip: RED };
      // Use selAlgo for decision, selCond for active tag, selectedField for conviction
      const picked = selAlgo || 'Enter';
      const tags = picked === 'Enter' ? ENTER_TAGS : picked === 'Skip' ? SKIP_TAGS : WAIT_TAGS;
      const tagInfo = {
        'Breakout with volume': 'Price crossed above a key resistance level AND volume is above 1.5× average. Both must be true — price without volume is a false breakout.',
        'Trend aligned':        'The stock is in an uptrend (higher highs, higher lows over 20+ candles). You are trading with the trend, not against it.',
        'Near support':         'Price is just above a previous swing low or a well-tested horizontal support. The SL sits just below this support — logical and tight.',
        'RSI in range':         'RSI is between 40–65. Not overbought (>75 = reversal risk) and not oversold (<30 = downtrend). Healthy momentum zone.',
        'MACD crossover':       'MACD line crossed above the signal line recently. Momentum is turning bullish — adds confluence to your entry.',
        'Scanner signal':       'A TradeReflex scanner flagged this stock. The scanner has pre-screened for technical criteria — adds a second layer of validation.',
        'Volume too low':       'Volume is below average. A move on low volume has no conviction — it can reverse instantly. Wait for volume to confirm or Skip.',
        'Near resistance':      'Price is approaching a resistance level where sellers previously took over. Risk/reward deteriorates here — better to wait for a breakout.',
        'RSI overbought':       'RSI above 75 means the stock has risen quickly and is statistically likely to pause or pull back before continuing. Not a buy zone.',
        'No clean SL level':    'There is no obvious structural low to place your SL below. Without a logical SL, you cannot calculate R:R — the trade cannot be sized correctly.',
        'Poor R:R':             'Even if the entry is valid, if Target / SL distance is below 1.5×, the trade fails the minimum quality bar. Skip it.',
        'Waiting for pullback': 'The setup exists but price is extended from the entry zone. Wait for a shallow pullback to the support/trigger level for a better entry.',
        'Waiting for volume':   'The pattern is forming but volume has not confirmed yet. Wait for volume to spike before entering — it reduces the odds of a false break.',
        'Market unclear':       'The broader market (NIFTY/BANKNIFTY) is indecisive or volatile. Even a good individual stock setup has lower odds in a choppy market.',
      };
      return (
        <Card>
          <div style={{ fontSize: 10, color: ORANGE, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>How to Fill the ACT Form</div>

          {/* Step 1: Decision */}
          <div style={{ fontSize: 9, color: sub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Step 1 — Your Decision</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            {decisions.map(d => (
              <div key={d} onClick={() => setSelAlgo(p => p === d ? null : d)}
                style={{ flex: 1, padding: '9px 0', borderRadius: 9, textAlign: 'center', fontSize: 12, fontWeight: 800, cursor: 'pointer', userSelect: 'none', background: picked === d ? `${decColors[d]}22` : inputBg, border: `2px solid ${picked === d ? decColors[d] : border}`, color: picked === d ? decColors[d] : sub, transition: 'all 0.18s' }}>
                {d}
              </div>
            ))}
          </div>

          {/* Step 2: Tags */}
          <div style={{ fontSize: 9, color: sub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            Step 2 — Why? <span style={{ color: ORANGE }}>tap tags to understand each reason</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 12 }}>
            {tags.map(t => {
              const isSel = selCond === t;
              const c = decColors[picked];
              return (
                <div key={t} onClick={() => setSelCond(p => p === t ? null : t)}
                  style={{ padding: '5px 10px', borderRadius: 7, fontSize: 10, fontWeight: 700, cursor: 'pointer', userSelect: 'none', background: isSel ? `${c}20` : inputBg, border: `1.5px solid ${isSel ? c : border}`, color: isSel ? c : sub, transition: 'all 0.18s' }}>
                  {t}
                </div>
              );
            })}
          </div>
          {selCond && tagInfo[selCond] && (
            <div style={{ padding: '8px 10px', borderRadius: 8, background: `${decColors[picked]}10`, border: `1px solid ${decColors[picked]}30`, marginBottom: 12, fontSize: 11, color: 'rgba(255,255,255,0.7)', lineHeight: 1.55 }}>
              💡 {tagInfo[selCond]}
            </div>
          )}

          {/* Step 3: SL + Target (only for Enter) */}
          {picked === 'Enter' && (
            <>
              <div style={{ fontSize: 9, color: sub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Step 3 — Set SL & Target</div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                {[
                  { label: 'Entry',  value: '₹1,362', color: INDIGO },
                  { label: 'SL',     value: '₹1,340', color: RED },
                  { label: 'Target', value: '₹1,406', color: GREEN },
                ].map(f => (
                  <div key={f.label} style={{ flex: 1, borderRadius: 8, padding: '8px 10px', background: inputBg, border: `1.5px solid ${f.color}50` }}>
                    <div style={{ fontSize: 9, color: sub, fontWeight: 700, textTransform: 'uppercase', marginBottom: 3 }}>{f.label}</div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: f.color }}>{f.value}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                <div style={{ flex: 1, padding: '6px 10px', borderRadius: 7, background: `${GREEN}10`, border: `1px solid ${GREEN}30`, fontSize: 10, color: GREEN, fontWeight: 700 }}>
                  R:R = 2.0× ✓
                </div>
                <div style={{ flex: 1, padding: '6px 10px', borderRadius: 7, background: `${INDIGO}10`, border: `1px solid ${INDIGO}30`, fontSize: 10, color: INDIGO, fontWeight: 700 }}>
                  Qty = 18 shares (₹25K)
                </div>
              </div>
            </>
          )}

          {/* Step 4: Conviction */}
          <div style={{ fontSize: 9, color: sub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Step {picked === 'Enter' ? 4 : 3} — Conviction</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {['LOW', 'MEDIUM', 'HIGH'].map(c => {
              const cc = c === 'LOW' ? RED : c === 'MEDIUM' ? AMBER : GREEN;
              const isSel = selectedField === c;
              const convInfo = {
                LOW:    'You see a setup but it doesn\'t feel clean. Maybe volume is borderline, or the pattern is ambiguous. Enter smaller or consider skipping.',
                MEDIUM: 'The setup meets your criteria. Reasonable entry — standard size. Most good trades are MEDIUM conviction, not HIGH.',
                HIGH:   'Everything aligns — trend, pattern, volume, R:R, sector. These are your best trades. You can size up within your risk profile.',
              };
              return (
                <div key={c} onClick={() => setSelectedField(f => f === c ? null : c)}
                  style={{ flex: 1, borderRadius: 9, padding: '8px 4px', textAlign: 'center', background: isSel ? `${cc}20` : inputBg, border: `1.5px solid ${isSel ? cc : border}`, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: isSel ? cc : sub }}>{c}</div>
                  {isSel && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginTop: 6, lineHeight: 1.4, padding: '0 4px', textAlign: 'left' }}>{convInfo[c]}</div>}
                </div>
              );
            })}
          </div>
        </Card>
      );
    }

    if (ui.mode === 'history') {
      const records = [
        { stock: 'RELIANCE', setup: 'Breakout', decision: 'Buy',  outcome: 'Correct', score: 34, scoreMax: 40 },
        { stock: 'HDFCBANK', setup: 'Pullback',  decision: 'Skip', outcome: 'Wrong',   score: 18, scoreMax: 40 },
        { stock: 'TCS',      setup: 'Reversal',  decision: 'Sell', outcome: 'Correct', score: 28, scoreMax: 40 },
      ];
      return (
        <Card>
          <div style={{ fontSize: 10, color: sub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Recent ACT History</div>
          <HintLabel text="tap a setup type to see your accuracy on it" />
          {records.map(r => {
            const isSel = selCond === r.stock;
            const outcomeColor = r.outcome === 'Correct' ? GREEN : RED;
            const pct = r.score / r.scoreMax;
            return (
              <div key={r.stock} onClick={() => setSelCond(p => p === r.stock ? null : r.stock)}
                style={{ borderRadius: 10, padding: '9px 12px', background: isSel ? `${outcomeColor}12` : inputBg, border: `1.5px solid ${isSel ? outcomeColor : border}`, marginBottom: 7, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <div>
                    <span style={{ fontSize: 12, fontWeight: 800, color: text }}>{r.stock}</span>
                    <span style={{ fontSize: 10, color: sub, marginLeft: 7 }}>{r.setup} · {r.decision}</span>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: outcomeColor }}>{r.outcome}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ flex: 1, height: 4, background: border, borderRadius: 2 }}>
                    <div style={{ height: '100%', width: `${pct * 100}%`, background: outcomeColor, borderRadius: 2 }} />
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 700, color: outcomeColor, flexShrink: 0 }}>{r.score}/{r.scoreMax}</span>
                </div>
                {isSel && historyInfo[r.setup] && (
                  <Explainer color={historyInfo[r.setup].color} icon={historyInfo[r.setup].icon} title={r.setup + ' — your accuracy'} body={historyInfo[r.setup].body} />
                )}
              </div>
            );
          })}
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            {Object.entries(historyInfo).map(([setup, info]) => (
              <div key={setup} style={{ flex: 1, padding: '5px 4px', borderRadius: 7, background: `${info.color}12`, border: `1px solid ${info.color}30`, textAlign: 'center' }}>
                <div style={{ fontSize: 9, color: sub, fontWeight: 600 }}>{setup}</div>
                <div style={{ fontSize: 11, fontWeight: 800, color: info.color }}>{setup === 'Breakout' ? '72%' : setup === 'Pullback' ? '44%' : '28%'}</div>
              </div>
            ))}
          </div>
        </Card>
      );
    }

    if (ui.mode === 'targets') {
      const targets = [
        { label: 'Swing Scenarios',    done: 3, total: 5, color: INDIGO, detail: 'Swing ACT scenarios use daily/weekly charts. You hold the imaginary position for days. Focus on: weekly trend direction, key support/resistance, and sector momentum. Complete 5 per day to build pattern recognition.' },
        { label: 'Intraday Scenarios', done: 1, total: 5, color: ORANGE, detail: 'Intraday ACT scenarios use 5-min or 15-min charts. You hold for minutes to hours. Focus on: opening range breakout, 9:30 AM momentum, and volume spikes. These are harder — more noise, shorter window.' },
      ];
      return (
        <Card>
          <div style={{ fontSize: 10, color: sub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>Today's ACT Targets</div>
          <HintLabel text="tap a target bar to understand what the scenarios train" />
          {targets.map(t => {
            const isSel = selectedField === t.label;
            const pct = t.done / t.total;
            return (
              <div key={t.label} onClick={() => setSelectedField(f => f === t.label ? null : t.label)}
                style={{ borderRadius: 10, padding: '10px 12px', background: isSel ? `${t.color}15` : inputBg, border: `1.5px solid ${isSel ? t.color : border}`, marginBottom: 8, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: text }}>{t.label}</span>
                  <span style={{ fontSize: 11, fontWeight: 800, color: t.color }}>{t.done}/{t.total} done</span>
                </div>
                <div style={{ height: 6, background: border, borderRadius: 3 }}>
                  <div style={{ height: '100%', width: `${pct * 100}%`, background: t.color, borderRadius: 3, transition: 'width 0.4s ease' }} />
                </div>
                {isSel && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 8, lineHeight: 1.55 }}>{t.detail}</div>}
              </div>
            );
          })}
          <div style={{ padding: '7px 10px', background: `${ORANGE}10`, borderRadius: 8, border: `1px solid ${ORANGE}30`, marginTop: 4 }}>
            <span style={{ fontSize: 11, color: ORANGE, fontWeight: 600 }}>📅 Do this every day — consistency beats intensity.</span>
          </div>
        </Card>
      );
    }

    return null;
  }

  // --- CHAT PREVIEW — mirrors Nyx AI Coach conversation interface ---
  if (ui.type === 'chat-preview') {
    const msgs = ui.messages || [];
    const prompts = ui.prompts || [];
    const showMarketCtx = !!ui.show_market_ctx;
    const PROMPT_NOTES = {
      'Review my portfolio':       'Nyx pulls your full position list, capital allocation, unrealised P&L, and sector exposure — then highlights what\'s over-concentrated or underperforming relative to your historical edge.',
      'Analyze my recent trades':  'Nyx reads your last 10–20 trades: win rate, average R:R, hold time, time-of-day bias, and whether you\'re following your SL discipline. It identifies the one or two patterns costing you the most.',
      'Why am I losing money?':    'Nyx cross-references your trade history with your discipline score timeline to pinpoint the exact behaviour driving losses — SL violations, overtrading windows, setup mismatches, or post-loss revenge trades.',
      'How to improve win rate?':  'Nyx identifies your highest win-rate setup type, the time window where you perform best, and the regime conditions where your edge works — then gives specific, actionable changes rather than generic advice.',
      'Show my best setup':        'Nyx filters your trade history by setup type and calculates win rate, average R:R, and average hold time for each — then names your top-performing pattern and explains when it works.',
      'What am I doing wrong?':    'Nyx looks for the most consistent negative pattern in your trades: which sessions you overstay, which setups you mistime, and where your actual behaviour diverges from your stated risk profile.',
      'Review my breakout trades': 'Nyx filters all breakout trades and calculates your accuracy, average entry vs breakout level, and which volume conditions correlate with your wins vs losses on this setup.',
      'Am I overtrading?':         'Nyx checks your average trades per day, how win rate changes after trade 3+, and whether your P&L turns negative in the afternoon — classic signs of overtrading that your raw trade count alone won\'t show.',
      'Analyze my last 3 trades':  'Nyx looks at entry timing, SL placement, hold duration, and exit vs target for each of the last 3 trades — then finds the common thread if all 3 lost or underperformed.',
      'Why do I exit early?':      'Nyx compares your average exit point as a % of target across winning vs losing trades. If you exit winners at 35% of target but hold losers to full SL, that\'s profit anxiety — Nyx names it and gives a fix.',
      'Am I revenge trading?':     'Nyx checks if your trade frequency spikes within 30–90 minutes after a loss, and whether win rate drops in those sessions. If yes, it\'s revenge trading — Nyx shows you the exact pattern and suggests a cooldown rule.',
      'Got it':                    'Acknowledging a nudge tells Nyx you\'ve read it. The nudge history is tracked — if the same pattern triggers again within the week, Nyx escalates the coaching message.',
      'Why this pattern?':         'Nyx explains the psychological mechanism behind the nudge — for example why traders hold losers longer than winners, or why win rate drops after 3 PM. It references your specific numbers, not generic trading theory.',
      'Show my timing data':       'Nyx shows a breakdown of your win rate and average P&L by time block: 9:15–10:30, 10:30–12:00, 12:00–14:00, 14:00–15:30. Most traders have a clear peak window they\'re not fully exploiting.',
    };
    return (
      <Card>
        {/* Nyx header — mirrors real AIPage header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>🌙</div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: text }}>Nyx AI</div>
            <div style={{ fontSize: 10, color: sub }}>Discipline · Risk · Psychology</div>
          </div>
        </div>
        {/* Suggested prompts — tappable, each shows what Nyx does with it */}
        {prompts.length > 0 && (
          <>
            <HintLabel text="tap a prompt to see what Nyx does with it" />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
              {prompts.map(p => {
                const isSel = selectedTag === p;
                return (
                  <div key={p} onClick={() => setSelectedTag(t => t === p ? null : p)}
                    style={{ padding: '5px 10px', borderRadius: 20, fontSize: 10, fontWeight: 600, cursor: 'pointer', userSelect: 'none', background: isSel ? `${INDIGO}25` : inputBg, border: `1px solid ${isSel ? INDIGO : border}`, color: isSel ? INDIGO : sub, transition: 'all 0.18s' }}>
                    ✦ {p}
                  </div>
                );
              })}
            </div>
            {prompts.map(p => selectedTag === p && PROMPT_NOTES[p] && (
              <div key={p} style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 10, lineHeight: 1.55, padding: '7px 10px', background: `${INDIGO}10`, borderRadius: 8, border: `1px solid ${INDIGO}25` }}>
                <strong style={{ color: INDIGO }}>"{p}"</strong> — {PROMPT_NOTES[p]}
              </div>
            ))}
          </>
        )}
        {/* Chat messages */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {msgs.map((m, i) => {
            const isUser = m.role === 'user';
            return (
              <div key={i} style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', gap: 7 }}>
                {!isUser && (
                  <div style={{ width: 22, height: 22, borderRadius: 6, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, flexShrink: 0, marginTop: 2 }}>🌙</div>
                )}
                <div style={{ maxWidth: '82%', padding: '8px 11px', borderRadius: isUser ? '12px 12px 2px 12px' : '2px 12px 12px 12px', background: isUser ? `${INDIGO}25` : inputBg, border: `1px solid ${isUser ? `${INDIGO}40` : border}`, fontSize: 11, color: isUser ? '#c7d2fe' : 'rgba(255,255,255,0.75)', lineHeight: 1.55 }}>
                  {m.text}
                </div>
              </div>
            );
          })}
        </div>
        {/* Market Context toggle — shown on slide 4 only */}
        {showMarketCtx && (
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 12px', borderRadius: 20, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none', cursor: 'default' }}
              onClick={() => setSelectedField(f => f === 'mkt-ctx' ? null : 'mkt-ctx')}>
              <span style={{ fontSize: 10 }}>⚡</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>Market Context: ON</span>
            </div>
            <span style={{ fontSize: 10, color: PURPLE }}>Using personal + 90-day regime data</span>
          </div>
        )}
        {showMarketCtx && selectedField === 'mkt-ctx' && (
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 8, lineHeight: 1.55, padding: '7px 10px', background: `${PURPLE}10`, borderRadius: 8, border: `1px solid ${PURPLE}25` }}>
            When Market Context is ON, Nyx adds 90-day NSE regime data to its answers — useful when asking about market conditions, sector trends, or whether your setup fits the current environment. OFF by default since personal data alone is usually sufficient.
          </div>
        )}
        {/* Quota ring + input row — mirrors real AIPage bottom bar */}
        <div style={{ display: 'flex', gap: 7, marginTop: 10, padding: '7px 10px', background: inputBg, borderRadius: 10, border: `1px solid ${showMarketCtx ? `${INDIGO}50` : border}`, alignItems: 'center' }}
          onClick={() => setSelectedField(f => f === 'quota' ? null : 'quota')}>
          <span style={{ flex: 1, fontSize: 11, color: sub }}>Ask Nyx about your trading...</span>
          {/* Quota ring SVG — mirrors real QuotaRing component */}
          <svg width="18" height="18" viewBox="0 0 18 18" style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
            <circle cx="9" cy="9" r="7" fill="none" stroke="#374151" strokeWidth="2.5" />
            <circle cx="9" cy="9" r="7" fill="none" stroke="#8b5cf6" strokeWidth="2.5"
              strokeDasharray={43.98} strokeDashoffset={43.98 * 0.35} strokeLinecap="round" />
          </svg>
          <div style={{ padding: '3px 10px', borderRadius: 7, background: `${INDIGO}30`, fontSize: 10, fontWeight: 700, color: INDIGO }}>Send</div>
        </div>
        {selectedField === 'quota' && (
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 7, lineHeight: 1.55, padding: '7px 10px', background: `${PURPLE}10`, borderRadius: 8, border: `1px solid ${PURPLE}25` }}>
            The purple ring next to the input shows your weekly message quota. As you use messages the ring fills — turning amber at 75% and red when full. Full purple ring = unlimited (Elite plan). Quota resets weekly.
          </div>
        )}
      </Card>
    );
  }

  // --- TRADE CARD LIST — mirrors Trade History page trade rows ---
  if (ui.type === 'trade-card-list') {
    const trades = ui.trades || [];
    return (
      <Card>
        <HintLabel text="tap any trade to understand what each field means" />
        <div style={{ fontSize: 9, color: sub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Trade History</div>
        {trades.map((t, i) => {
          const isSel = selectedField === String(i);
          const pnlStr = String(t.pnl || '');
          const pnlColor = pnlStr.startsWith('-') ? RED : GREEN;
          return (
            <div key={i} onClick={() => setSelectedField(f => f === String(i) ? null : f)}
              style={{ padding: '10px 12px', borderRadius: 10, background: isSel ? `${pnlColor}10` : inputBg, border: `1.5px solid ${isSel ? pnlColor : border}`, marginBottom: 7, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: text }}>{t.symbol}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 5, background: t.type === 'BUY' ? `${INDIGO}20` : `${RED}20`, color: t.type === 'BUY' ? INDIGO : RED }}>{t.type}</span>
                  <span style={{ fontSize: 10, color: sub }}>{t.product}</span>
                </div>
                <span style={{ fontSize: 13, fontWeight: 800, color: pnlColor }}>{pnlStr}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 10 }}>
                  <span style={{ fontSize: 10, color: sub }}>Entry {t.entry}</span>
                  {t.exit && <span style={{ fontSize: 10, color: sub }}>Exit {t.exit}</span>}
                  <span style={{ fontSize: 10, color: sub }}>×{t.qty}</span>
                </div>
                <span style={{ fontSize: 10, color: sub }}>{t.date}</span>
              </div>
              {isSel && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${border}` }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 5 }}>
                    {t.tags?.map(tag => <span key={tag} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 5, background: `${INDIGO}15`, color: INDIGO }}>{tag}</span>)}
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>{t.note}</div>
                </div>
              )}
            </div>
          );
        })}
      </Card>
    );
  }

  // --- BEHAVIOR PROFILE — mirrors Trade Performance page (behavioral health score ring + metrics) ---
  if (ui.type === 'behavior-profile') {
    const score = ui.score ?? 68;
    const profile = ui.profile || 'Disciplined Executor';
    const metrics = ui.metrics || [];
    const scoreColor = score >= 70 ? GREEN : score >= 45 ? AMBER : RED;
    const r = 20, circ = 2 * Math.PI * r;
    return (
      <Card>
        <HintLabel text="tap any metric to understand what it measures" />
        {/* Profile header — score ring + label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12, padding: '10px 12px', background: inputBg, borderRadius: 12 }}>
          <div style={{ position: 'relative', width: 52, height: 52, flexShrink: 0 }}>
            <svg width={52} height={52} style={{ transform: 'rotate(-90deg)' }}>
              <circle cx={26} cy={26} r={r} fill="none" stroke={border} strokeWidth={4} />
              <circle cx={26} cy={26} r={r} fill="none" stroke={scoreColor} strokeWidth={4}
                strokeDasharray={`${(score / 100) * circ} ${circ}`} strokeLinecap="round" />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 900, color: scoreColor }}>{score}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: sub, fontWeight: 600, textTransform: 'uppercase', marginBottom: 3 }}>Trader Type</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: text }}>{profile}</div>
            <div style={{ fontSize: 10, color: sub, marginTop: 2 }}>Based on your last {ui.trade_count || 34} trades</div>
          </div>
        </div>
        {/* Behavior metrics */}
        {metrics.map(m => {
          const isSel = selMetric === m.label;
          const normalized = m.score <= 10 ? m.score * 10 : m.score;
          const mc = normalized >= 70 ? GREEN : normalized >= 45 ? AMBER : RED;
          const displayScore = m.score <= 10 ? `${m.score}/10` : `${m.score}/100`;
          return (
            <div key={m.label} onClick={() => setSelMetric(p => p === m.label ? null : m.label)}
              style={{ borderRadius: 9, padding: '8px 11px', background: isSel ? `${mc}12` : inputBg, border: `1.5px solid ${isSel ? mc : border}`, marginBottom: 6, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: isSel ? mc : text }}>{m.label}</span>
                <span style={{ fontSize: 12, fontWeight: 800, color: mc }}>{displayScore}</span>
              </div>
              <div style={{ height: 4, background: border, borderRadius: 2 }}>
                <div style={{ height: '100%', width: `${normalized}%`, background: mc, borderRadius: 2 }} />
              </div>
              {isSel && m.note && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 7, lineHeight: 1.55 }}>{m.note}</div>}
            </div>
          );
        })}
      </Card>
    );
  }

  // --- RISK SCORE CARD — mirrors RiskProfileSection main score card + spectrum ---
  if (ui.type === 'risk-score-card') {
    const { score = 46, profile_type = 'Moderate' } = ui;
    const scoreColor = score >= 66 ? RED : score >= 46 ? '#f97316' : score >= 26 ? INDIGO : GREEN;
    const pct = Math.min(Math.max(score, 2), 97);
    const BANDS = [
      { key: 'vc', label: 'V.Cons.',  range: '0–25',  color: GREEN,    note: 'Very Conservative: 0–25. Risk ≤0.5% per trade, max 1 trade/day. Best suited to complete beginners or anyone who cannot absorb more than ₹5,000 in losses per month.' },
      { key: 'co', label: 'Cons.',    range: '26–45',  color: '#84cc16', note: 'Conservative: 26–45. Risk ≤1% per trade, max 2 trades/day. Good for newer traders with limited capital or those who need more practice managing losses before sizing up.' },
      { key: 'mo', label: 'Moderate', range: '46–65',  color: '#f59e0b', note: 'Moderate: 46–65. Risk ≤1.5% per trade, max 3 trades/day. The most common profile — you understand trading basics and can manage a drawdown without abandoning your plan.' },
      { key: 'ag', label: 'Aggr.',    range: '66–80',  color: '#f97316', note: 'Aggressive: 66–80. Risk ≤2% per trade, max 5 trades/day. You have real edge in a specific setup, consistent SL discipline, and enough capital to absorb a 5-trade losing streak.' },
      { key: 'va', label: 'V.Aggr.',  range: '81–100', color: RED,       note: 'Very Aggressive: 81–100. Risk ≤2.5% per trade, up to 8 trades/day. Reserved for experienced traders with verified historical edge and very high behavioral discipline scores.' },
    ];
    return (
      <Card>
        <div style={{ textAlign: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: sub, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Your Risk Score</div>
          <div onClick={() => setSelectedField(f => f === 'score-main' ? null : 'score-main')}
            style={{ cursor: 'pointer', userSelect: 'none' }}>
            <div style={{ fontSize: 52, fontWeight: 900, color: scoreColor, lineHeight: 1 }}>{score}</div>
            <div style={{ fontSize: 12, color: sub, marginBottom: 8 }}>out of 100</div>
            <span style={{ display: 'inline-block', fontSize: 13, fontWeight: 700, padding: '3px 14px', borderRadius: 20, background: `${scoreColor}20`, color: scoreColor }}>{profile_type}</span>
          </div>
          {selectedField === 'score-main' && (
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 10, lineHeight: 1.5, textAlign: 'left', padding: '8px 10px', background: `${scoreColor}10`, borderRadius: 8, border: `1px solid ${scoreColor}30` }}>
              Score {score} places you at <strong style={{ color: scoreColor }}>{profile_type}</strong>. This determines your recommended risk per trade, max trades per day, and which instruments you should be trading. Re-answer the 13 questions every 3 months as your knowledge and experience grow.
            </div>
          )}
        </div>
        {/* 5-band gradient spectrum — tap each band label for explanation */}
        <div style={{ position: 'relative', height: 8, borderRadius: 4, background: 'linear-gradient(to right, #16C784 0%, #84cc16 25%, #f59e0b 50%, #f97316 75%, #ef4444 100%)', marginBottom: 8 }}>
          <div style={{ position: 'absolute', top: '50%', left: `${pct}%`, transform: 'translate(-50%,-50%)', width: 16, height: 16, borderRadius: '50%', background: '#fff', border: `2px solid ${scoreColor}`, boxShadow: `0 0 6px ${scoreColor}` }} />
        </div>
        <HintLabel text="tap a band label to see what that profile means" />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {BANDS.map(b => {
            const isSel = selectedField === b.key;
            return (
              <span key={b.key} onClick={() => setSelectedField(f => f === b.key ? null : b.key)}
                style={{ fontSize: 10, fontWeight: isSel ? 800 : 500, color: isSel ? b.color : sub, cursor: 'pointer', userSelect: 'none', padding: '2px 1px', borderBottom: isSel ? `2px solid ${b.color}` : '2px solid transparent', transition: 'all 0.15s' }}>
                {b.label}
              </span>
            );
          })}
        </div>
        {BANDS.map(b => selectedField === b.key && (
          <div key={b.key} style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 10, lineHeight: 1.5, padding: '8px 10px', background: `${b.color}10`, borderRadius: 8, border: `1px solid ${b.color}30` }}>
            <strong style={{ color: b.color }}>{b.label} ({b.range})</strong> — {b.note}
          </div>
        ))}
      </Card>
    );
  }

  // --- RISK BREAKDOWN — mirrors RiskProfileSection "Score Breakdown" (3 sub-component bars) ---
  if (ui.type === 'risk-breakdown') {
    const { capacity = 18, knowledge = 12, behavior = 24 } = ui;
    const bars = [
      { label: 'Financial Capacity', val: capacity, max: 30, color: INDIGO },
      { label: 'Knowledge',          val: knowledge, max: 20, color: '#a855f7' },
      { label: 'Behavior',           val: behavior,  max: 50, color: '#f97316' },
    ];
    return (
      <Card>
        <div style={{ fontSize: 11, fontWeight: 700, color: sub, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Score Breakdown</div>
        <HintLabel text="tap a component to understand what drives it" />
        {bars.map(b => {
          const isSel = selectedField === b.label;
          const NOTES = {
            'Financial Capacity': 'Based on your income, savings, and stated loss tolerance. Can you absorb a ₹25,000 drawdown without stress? This caps at 30 pts — 30% of your total score.',
            'Knowledge':          'Your options, futures, and technical analysis experience. Increases as you complete Learning Path levels and trade real instruments. Caps at 20 pts.',
            'Behavior':           'Heaviest component at 50 pts. Measures emotional control under loss — your questionnaire answers plus actual trade history both feed this. The hardest to improve but most impactful.',
          };
          return (
            <div key={b.label} onClick={() => setSelectedField(f => f === b.label ? null : b.label)}
              style={{ marginBottom: 12, cursor: 'pointer', userSelect: 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 5 }}>
                <span style={{ color: isSel ? b.color : text }}>{b.label}</span>
                <span style={{ fontWeight: 700, color: b.color }}>{b.val} <span style={{ color: sub }}>/ {b.max}</span></span>
              </div>
              <div style={{ height: 8, borderRadius: 4, background: border }}>
                <div style={{ height: '100%', width: `${(b.val / b.max) * 100}%`, borderRadius: 4, background: b.color, transition: 'width 0.4s' }} />
              </div>
              {isSel && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 7, lineHeight: 1.5 }}>{NOTES[b.label]}</div>}
            </div>
          );
        })}
      </Card>
    );
  }

  // --- RISK RECOMMENDATIONS — mirrors RiskProfileSection 3-column recommendations grid ---
  if (ui.type === 'risk-recommendations') {
    const { risk_pct = '1.5%', style = 'Swing', max_trades = 3, adherence_score = 83, trades_checked = 42, within_limit = 38, violations = 4, breaches = [] } = ui;
    const adhColor = adherence_score >= 70 ? GREEN : adherence_score >= 40 ? '#f97316' : RED;
    const RECS = [
      { key: 'risk-pct', label: 'Risk / Trade', value: risk_pct, color: GREEN,     note: `At ${risk_pct} risk per trade with ₹1L capital = ₹1,500 max loss on any single trade. This is calibrated to your Financial Capacity score. If you're consistently hitting SL, the problem is setup quality — not this number.` },
      { key: 'style',    label: 'Style',        value: style,    color: INDIGO,    note: `${style} trading is recommended based on your risk profile score and behavioral history. Swing means holding positions overnight. This is only appropriate if you have a real stop-loss set — not a mental stop you\'ll move when price goes against you.` },
      { key: 'max-day',  label: 'Max / Day',    value: String(max_trades), color: '#f97316', note: `${max_trades} trades per day maximum. More trades = more decisions = more chances for emotional errors. Quality beats quantity. Plan all ${max_trades} slots before market open — don't place them reactively.` },
    ];
    const ADHNOTES = {
      'Trades Checked': 'The number of your recent trades that the system evaluated against your risk limits. Every trade is checked — this is automatic, not a manual review.',
      'Within Limit':   `${within_limit} of ${trades_checked} trades stayed within your ${risk_pct} risk limit. High within-limit count = your actual behaviour matches your stated profile. This improves your Behavior score.`,
      'Violations':     `${violations} trades exceeded your ${risk_pct} risk limit. Each violation lowers your Behavior score and your Discipline Score. Review these trades to understand what caused you to oversize.`,
    };
    return (
      <Card>
        <HintLabel text="tap any recommendation or stat to understand it" />
        {/* 3-column recommendations — exact match to real page */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 7, marginBottom: 10 }}>
          {RECS.map(({ key, label, value, color, note }) => {
            const isSel = selectedField === key;
            return (
              <div key={key} onClick={() => setSelectedField(f => f === key ? null : key)}
                style={{ textAlign: 'center', padding: '10px 6px', borderRadius: 10, background: isSel ? `${color}18` : inputBg, border: `1.5px solid ${isSel ? color : border}`, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                <div style={{ fontSize: 11, color: sub, marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 14, fontWeight: 800, color }}>{value}</div>
              </div>
            );
          })}
        </div>
        {RECS.map(({ key, color, note }) => selectedField === key && (
          <div key={key} style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 10, lineHeight: 1.55, padding: '8px 10px', background: `${color}10`, borderRadius: 8, border: `1px solid ${color}30` }}>{note}</div>
        ))}
        {/* Risk Adherence — matches real section layout */}
        <div style={{ borderTop: `1px solid ${border}`, paddingTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: sub, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Risk Adherence</span>
            <span style={{ fontSize: 13 }}>{violations === 0 ? '🛡️' : '⚠️'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 5 }}>
            <span style={{ color: text }}>Following your risk limit</span>
            <span style={{ fontWeight: 700, color: adhColor }}>{adherence_score}/100</span>
          </div>
          <div style={{ height: 8, borderRadius: 4, background: border, marginBottom: 10 }}>
            <div style={{ height: '100%', width: `${adherence_score}%`, borderRadius: 4, background: adhColor }} />
          </div>
          {/* Stats 3-column — each tappable */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: violations > 0 ? 10 : 0 }}>
            {[
              { label: 'Trades Checked', value: trades_checked, color: text },
              { label: 'Within Limit',   value: within_limit,   color: GREEN },
              { label: 'Violations',     value: violations,     color: violations > 0 ? '#f97316' : sub },
            ].map(({ label, value, color }) => {
              const isSel = selectedField === `adh-${label}`;
              return (
                <div key={label} onClick={() => setSelectedField(f => f === `adh-${label}` ? null : `adh-${label}`)}
                  style={{ textAlign: 'center', padding: '8px 4px', borderRadius: 9, background: isSel ? `${color}15` : inputBg, border: `1.5px solid ${isSel ? color : 'transparent'}`, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                  <div style={{ fontSize: 17, fontWeight: 800, color }}>{value}</div>
                  <div style={{ fontSize: 10, color: sub, marginTop: 2 }}>{label}</div>
                </div>
              );
            })}
          </div>
          {['Trades Checked', 'Within Limit', 'Violations'].map(lbl => selectedField === `adh-${lbl}` && (
            <div key={lbl} style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 8, lineHeight: 1.55, padding: '7px 10px', background: `${INDIGO}10`, borderRadius: 8, border: `1px solid ${INDIGO}25` }}>{ADHNOTES[lbl]}</div>
          ))}
          {/* Breach list — each row tappable */}
          {breaches.length > 0 && (
            <div style={{ borderTop: `1px solid ${border}`, paddingTop: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: sub, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Breaches — tap to understand</div>
              {breaches.map((v, i) => {
                const vc = v.severity === 'severe' ? RED : '#f97316';
                const bKey = `breach-${i}`;
                const isSel = selectedField === bKey;
                return (
                  <div key={i}>
                    <div onClick={() => setSelectedField(f => f === bKey ? null : bKey)}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px', borderRadius: 8, background: isSel ? `${vc}20` : `${vc}12`, border: `1.5px solid ${isSel ? vc : `${vc}30`}`, marginBottom: 5, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                      <div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: text }}>{v.symbol}</span>
                        <span style={{ fontSize: 11, color: sub, marginLeft: 7 }}>{v.date}</span>
                      </div>
                      <div>
                        <span style={{ fontSize: 13, fontWeight: 800, color: vc }}>{v.actual_pct}%</span>
                        <span style={{ fontSize: 11, color: sub, marginLeft: 4 }}>/ {v.limit_pct}% limit</span>
                      </div>
                    </div>
                    {isSel && (
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 8, lineHeight: 1.55, padding: '7px 10px', background: `${vc}10`, borderRadius: 8, border: `1px solid ${vc}25` }}>
                        This trade risked {v.actual_pct}% of capital — {((v.actual_pct - v.limit_pct) / v.limit_pct * 100).toFixed(0)}% more than your {v.limit_pct}% limit. Common cause: entering with a SL that's too wide, or sizing in without checking the calculator first. Fix: always size via the Calculator before placing the order.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>
    );
  }

  // --- RISK QUESTIONNAIRE — mirrors RiskProfileSection 1-question-per-screen flow ---
  if (ui.type === 'risk-questionnaire') {
    const { category = 'Financial Capacity', step = '1 of 5', question = '', options = [], selected_key = null } = ui;
    return (
      <Card>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: INDIGO, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>{category} · {step}</div>
          <div style={{ height: 4, borderRadius: 2, background: border }}>
            <div style={{ height: '100%', borderRadius: 2, background: INDIGO, width: `${(parseInt(step) / parseInt(step.split(' of ')[1])) * 100}%` }} />
          </div>
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: text, marginBottom: 12, lineHeight: 1.5 }}>{question}</div>
        <HintLabel text="tap an option to see what it signals about your risk capacity" />
        {options.map(opt => {
          const isSel = selectedField === opt.key || selected_key === opt.key;
          return (
            <div key={opt.key} onClick={() => setSelectedField(f => f === opt.key ? null : opt.key)}
              style={{ padding: '10px 12px', borderRadius: 10, background: isSel ? `${INDIGO}18` : inputBg, border: `1.5px solid ${isSel ? INDIGO : border}`, marginBottom: 7, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: isSel ? INDIGO : text, marginBottom: isSel && opt.note ? 5 : 0 }}>{opt.label}</div>
              {isSel && opt.note && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>{opt.note}</div>}
            </div>
          );
        })}
      </Card>
    );
  }

  // --- CALCULATOR PLAN — mirrors PositionSizeCalculator Plan Setup section ---
  if (ui.type === 'calculator-plan') {
    const { capital = 150000, num_trades = 5, risk_pct = 2, allocs = [20, 20, 20, 20, 20] } = ui;
    const risk_budget = Math.round(capital * risk_pct / 100);
    const total_risk  = risk_pct * num_trades;
    const cap_trade   = Math.floor(capital / num_trades);
    const allocTotal  = allocs.slice(0, num_trades).reduce((s, a) => s + a, 0);
    const CHIPS = [
      { key: 'chip-rb',  label: 'Risk budget', value: `₹${risk_budget.toLocaleString('en-IN')}`, color: RED,   note: `Risk budget = ${risk_pct}% × ₹${capital.toLocaleString('en-IN')} = ₹${risk_budget.toLocaleString('en-IN')}. This is the maximum rupee amount you can lose on a single trade. Never size a position where your SL would cost you more than this.` },
      { key: 'chip-tr',  label: 'Total risk',  value: `${total_risk}%`,                           color: AMBER, note: `Total risk = ${risk_pct}% × ${num_trades} trades = ${total_risk}%. If all ${num_trades} trades hit their stop losses in one day, you lose ${total_risk}% of capital. Check this number before confirming your plan — is a ${total_risk}% drawdown day something you can absorb without changing behaviour?` },
      { key: 'chip-ct',  label: 'Cap / trade', value: `₹${cap_trade.toLocaleString('en-IN')}`,   color: INDIGO, note: `Capital per trade = ₹${capital.toLocaleString('en-IN')} ÷ ${num_trades} trades = ₹${cap_trade.toLocaleString('en-IN')}. This is how much capital each slot gets. The actual shares you buy depend on entry price — the Calculator converts this into exact quantity.` },
    ];
    const FIELD_NOTES = {
      'capital':   `Account size is your total active capital for today — the number all calculations derive from. If you deposited ₹2L but only want to trade ₹1.5L today, enter ₹1.5L here. This is your risk base.`,
      'trades':    `The number of trades you plan to take today. Fewer trades = more focus per trade. Good traders do 1–3 quality setups. Don't plan 5 if you've only found 2 strong setups — leave slots empty.`,
      'risk-pct':  `${risk_pct}% risk per trade is auto-filled from your Risk Profile. This is the max % of capital you\'re willing to lose on one trade. Don't override this without a specific reason — it's calibrated to your financial capacity.`,
    };
    return (
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: `${INDIGO}10`, borderRadius: 8, marginBottom: 10, border: `1px solid ${border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, color: INDIGO }}>⚡</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: sub, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Plan Setup</span>
          </div>
          <span style={{ fontSize: 10, color: sub }}>{risk_pct}% risk/trade · profile ✓</span>
        </div>
        <HintLabel text="tap any field or metric to understand what it means" />
        {/* Account size + Trades today row — tappable */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
          {[
            { fkey: 'capital', label: 'Account size · daily limit', display: `₹${capital.toLocaleString('en-IN')}`, accent: INDIGO },
            { fkey: 'trades',  label: 'Trades today',               display: String(num_trades),                    accent: INDIGO },
          ].map(({ fkey, label, display, accent }) => {
            const isSel = selectedField === fkey;
            return (
              <div key={fkey} onClick={() => setSelectedField(f => f === fkey ? null : fkey)} style={{ cursor: 'pointer', userSelect: 'none' }}>
                <div style={{ fontSize: 10, color: sub, marginBottom: 4 }}>{label}</div>
                <div style={{ padding: '7px 10px', borderRadius: 8, background: isSel ? `${accent}18` : inputBg, border: `1.5px solid ${isSel ? accent : `${accent}40`}`, fontSize: 12, fontWeight: 800, color: isSel ? accent : text, transition: 'all 0.18s', textAlign: 'center' }}>{display}</div>
              </div>
            );
          })}
        </div>
        {['capital', 'trades'].map(k => selectedField === k && (
          <div key={k} style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 10, lineHeight: 1.55, padding: '7px 10px', background: `${INDIGO}10`, borderRadius: 8, border: `1px solid ${INDIGO}25` }}>{FIELD_NOTES[k]}</div>
        ))}
        {/* Risk % stepper — tappable */}
        <div style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: sub, marginBottom: 4 }}>
            <span>Risk % per trade</span>
            <span style={{ color: GREEN }}>profile ✓</span>
          </div>
          <div onClick={() => setSelectedField(f => f === 'risk-pct' ? null : 'risk-pct')}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, background: selectedField === 'risk-pct' ? `${INDIGO}18` : inputBg, border: `1.5px solid ${selectedField === 'risk-pct' ? INDIGO : border}`, cursor: 'pointer', transition: 'all 0.18s' }}>
            <span style={{ width: 24, height: 24, borderRadius: 6, background: `${INDIGO}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: INDIGO }}>−</span>
            <span style={{ flex: 1, textAlign: 'center', fontSize: 16, fontWeight: 900, color: INDIGO }}>{risk_pct}%</span>
            <span style={{ width: 24, height: 24, borderRadius: 6, background: `${INDIGO}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: INDIGO }}>+</span>
          </div>
          {selectedField === 'risk-pct' && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 6, lineHeight: 1.55, padding: '7px 10px', background: `${INDIGO}10`, borderRadius: 8, border: `1px solid ${INDIGO}25` }}>{FIELD_NOTES['risk-pct']}</div>}
        </div>
        {/* 3 metric chips — each tappable */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 10 }}>
          {CHIPS.map(({ key, label, value, color }) => {
            const isSel = selectedField === key;
            return (
              <div key={key} onClick={() => setSelectedField(f => f === key ? null : key)}
                style={{ textAlign: 'center', padding: '7px 4px', borderRadius: 8, background: isSel ? `${color}18` : inputBg, border: `1.5px solid ${isSel ? color : 'transparent'}`, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                <div style={{ fontSize: 11, fontWeight: 800, color }}>{value}</div>
                <div style={{ fontSize: 9, color: sub, marginTop: 2 }}>{label}</div>
              </div>
            );
          })}
        </div>
        {CHIPS.map(({ key, color, note }) => selectedField === key && (
          <div key={key} style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 10, lineHeight: 1.55, padding: '7px 10px', background: `${color}10`, borderRadius: 8, border: `1px solid ${color}25` }}>{note}</div>
        ))}
        {/* Allocation bars per trade — each row tappable */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: sub, marginBottom: 5 }}>
            <span>Allocation per trade (tap a slot)</span>
            <span style={{ color: allocTotal === 100 ? GREEN : RED }}>{allocTotal}% {allocTotal === 100 ? '✓' : '⚠'}</span>
          </div>
          {allocs.slice(0, num_trades).map((p, i) => {
            const aKey = `alloc-${i}`;
            const isSel = selectedField === aKey;
            return (
              <div key={i}>
                <div onClick={() => setSelectedField(f => f === aKey ? null : aKey)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, padding: '3px 4px', borderRadius: 6, background: isSel ? `${INDIGO}12` : 'transparent', cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: isSel ? INDIGO : sub, width: 14, textAlign: 'center', flexShrink: 0 }}>{i + 1}</span>
                  <div style={{ flex: 1, height: 6, borderRadius: 3, background: border, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${p}%`, background: isSel ? `${INDIGO}` : `${INDIGO}99`, borderRadius: 3, transition: 'width 0.4s' }} />
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 700, color: isSel ? INDIGO : text, width: 26, textAlign: 'right', flexShrink: 0 }}>{p}%</span>
                  <span style={{ fontSize: 10, color: sub, width: 54, textAlign: 'right', flexShrink: 0 }}>₹{Math.floor(capital * p / 100).toLocaleString('en-IN')}</span>
                </div>
                {isSel && (
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 6, lineHeight: 1.5, padding: '6px 10px', background: `${INDIGO}10`, borderRadius: 7, border: `1px solid ${INDIGO}25` }}>
                    Trade slot {i + 1}: {p}% of ₹{capital.toLocaleString('en-IN')} = ₹{Math.floor(capital * p / 100).toLocaleString('en-IN')} allocated. Once you enter Entry and SL for this slot, the Calculator converts this capital into exact share quantity automatically — no manual maths.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    );
  }

  // --- CALCULATOR EQUITY SLOT — mirrors PositionSizeCalculator Equity trade slot ---
  if (ui.type === 'calculator-equity-slot') {
    const { symbol = 'RELIANCE', entry = 2840, sl_pct = 1.5, target_pct = 3.0, capital = 30000, risk_budget = 3000 } = ui;
    const sl_price   = Math.round(entry * (1 - sl_pct / 100));
    const tgt_price  = Math.round(entry * (1 + target_pct / 100));
    const max_loss   = Math.round(risk_budget);
    const qty        = Math.floor(risk_budget / (entry - sl_price));
    const max_profit = Math.round(qty * (tgt_price - entry));
    const rr         = (target_pct / sl_pct).toFixed(1);
    const rrColor    = parseFloat(rr) >= 2 ? GREEN : parseFloat(rr) >= 1.5 ? AMBER : RED;
    const EQ_NOTES = {
      'Shares (auto-calculated)': `${qty} shares = ₹${risk_budget.toLocaleString('en-IN')} risk budget ÷ (₹${entry} entry − ₹${sl_price} SL) = ₹${entry - sl_price} per share risk. You never guess quantity — the Calculator always sizes it from your risk budget and SL distance.`,
      'Capital deployed':         `${qty} shares × ₹${entry} = ₹${(qty * entry).toLocaleString('en-IN')}. This is less than your ₹${capital.toLocaleString('en-IN')} slot allocation — you only deploy what the sizing math requires. Unused capital stays in the slot.`,
      'Max loss (SL hit)':        `If price drops to ₹${sl_price} (your SL), you lose ${qty} shares × ₹${entry - sl_price} = ₹${max_loss.toLocaleString('en-IN')}. This equals your risk budget exactly. The SL is not a mental stop — it must be placed as an order before you take the trade.`,
      'Max profit (target hit)':  `If price rises to ₹${tgt_price} (your target), you gain ${qty} shares × ₹${tgt_price - entry} = ₹${max_profit.toLocaleString('en-IN')}. This is your intended reward. Before entering, check: is this realistic? Does price have open space to reach ₹${tgt_price}?`,
      'R:R ratio':                `R:R = reward ÷ risk = ₹${max_profit.toLocaleString('en-IN')} ÷ ₹${max_loss.toLocaleString('en-IN')} = 1:${rr}. ${parseFloat(rr) >= 2 ? 'Good — this trade makes sense mathematically.' : parseFloat(rr) >= 1.5 ? 'Acceptable, but aim for 1:2+ on most trades.' : 'Below 1:1.5 — this trade does not make mathematical sense. Widen your target or tighten your SL before entering.'}`,
    };
    return (
      <Card>
        {/* Slot header — matches real collapsed/expanded header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 11px', borderRadius: 9, background: inputBg, border: `1px solid ${border}`, marginBottom: 10 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: text }}>Trade 1</span>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: `${INDIGO}20`, color: INDIGO }}>EQ</span>
            </div>
            <div style={{ fontSize: 10, color: sub, marginTop: 2 }}>₹{capital.toLocaleString('en-IN')} allocated · ₹{risk_budget.toLocaleString('en-IN')} risk</div>
          </div>
          <div style={{ fontSize: 11, fontWeight: 800, color: GREEN }}>+₹{max_profit.toLocaleString('en-IN')}</div>
        </div>
        {/* Stock + Entry — tappable */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
          {[
            { fkey: 'eq-stock', label: 'Stock', val: symbol, note: `${symbol} is the instrument for this trade slot. You search and select from all NSE-listed equities. The Calculator pulls the current (sim) price automatically.` },
            { fkey: 'eq-entry', label: 'Entry Price (₹)', val: `₹${entry.toLocaleString('en-IN')}`, note: `Entry at ₹${entry} is the price at which you plan to buy. This drives the auto-calculation — change the entry and quantity recalculates instantly to keep max loss at exactly ₹${risk_budget.toLocaleString('en-IN')}.` },
          ].map(({ fkey, label, val, note }) => {
            const isSel = selectedField === fkey;
            return (
              <div key={fkey} onClick={() => setSelectedField(f => f === fkey ? null : fkey)}
                style={{ padding: '9px 11px', borderRadius: 9, background: isSel ? `${INDIGO}18` : inputBg, border: `1.5px solid ${isSel ? INDIGO : border}`, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                <div style={{ fontSize: 10, color: sub, marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: 13, fontWeight: 800, color: isSel ? INDIGO : text }}>{val}</div>
                {isSel && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 5, lineHeight: 1.5 }}>{note}</div>}
              </div>
            );
          })}
        </div>
        {/* SL + Target — tappable */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
          {[
            { fkey: 'eq-sl',  label: 'STOP LOSS', pct: sl_pct,     price: sl_price, bg: RED,   note: `SL at ${sl_pct}% below entry = ₹${sl_price}. If price reaches ₹${sl_price}, your position exits automatically. This is not a mental stop — place it as an actual order. Never move your SL further away from entry once the trade is live.` },
            { fkey: 'eq-tgt', label: 'TARGET',    pct: target_pct, price: tgt_price, bg: GREEN, note: `Target at ${target_pct}% above entry = ₹${tgt_price}. This is where you plan to exit the winning side. Check: does price have clear space to reach ₹${tgt_price} without hitting a major resistance level?` },
          ].map(({ fkey, label, pct, price, bg, note }) => {
            const isSel = selectedField === fkey;
            return (
              <div key={fkey} onClick={() => setSelectedField(f => f === fkey ? null : fkey)}
                style={{ padding: '9px 11px', borderRadius: 9, background: isSel ? `${bg}22` : `${bg}10`, border: `1.5px solid ${isSel ? bg : `${bg}35`}`, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: bg, marginBottom: 4 }}>{label}</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 16, fontWeight: 900, color: bg }}>{pct}%</span>
                  <span style={{ fontSize: 10, color: sub }}>₹{price.toLocaleString('en-IN')}</span>
                </div>
                {isSel && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 5, lineHeight: 1.5 }}>{note}</div>}
              </div>
            );
          })}
        </div>
        {/* Auto-calculated breakdown table — each row tappable */}
        <HintLabel text="tap any calculated value to understand how it's derived" />
        <div style={{ borderRadius: 9, background: inputBg, padding: '9px 11px' }}>
          {[
            { key: 'Shares (auto-calculated)', value: `${qty} shares`,                               color: text  },
            { key: 'Capital deployed',         value: `₹${(qty * entry).toLocaleString('en-IN')}`,   color: text  },
            { key: 'Max loss (SL hit)',         value: `-₹${max_loss.toLocaleString('en-IN')}`,       color: RED   },
            { key: 'Max profit (target hit)',   value: `+₹${max_profit.toLocaleString('en-IN')}`,     color: GREEN },
            { key: 'R:R ratio',                value: `1:${rr}`,                                     color: rrColor },
          ].map(({ key, value, color }) => {
            const isSel = selectedField === key;
            return (
              <div key={key} onClick={() => setSelectedField(f => f === key ? null : key)}
                style={{ paddingBottom: 6, marginBottom: 6, borderBottom: `1px solid ${border}30`, cursor: 'pointer', userSelect: 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: isSel ? color : sub }}>{key}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color }}>{value}</span>
                </div>
                {isSel && EQ_NOTES[key] && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 5, lineHeight: 1.5 }}>{EQ_NOTES[key]}</div>}
              </div>
            );
          })}
        </div>
      </Card>
    );
  }

  // --- CALCULATOR FO SLOT — mirrors PositionSizeCalculator F&O trade slot ---
  if (ui.type === 'calculator-fo-slot') {
    const { underlying = 'NIFTY', instrument = 'Options', direction = 'BUY', option_type = 'CE', lots = 1, lot_size = 75, strike = '24000', expiry = '29 May', premium = 180, sl_premium = 120, target_premium = 280, margin = 13500 } = ui;
    const max_loss   = Math.round((premium - sl_premium) * lot_size * lots);
    const max_profit = Math.round((target_premium - premium) * lot_size * lots);
    const rr         = max_loss > 0 ? (max_profit / max_loss).toFixed(1) : '—';
    const dirColor   = direction === 'BUY' ? GREEN : RED;
    const optColor   = option_type === 'CE' ? GREEN : '#f97316';
    const rrColor    = parseFloat(rr) >= 2 ? GREEN : parseFloat(rr) >= 1.5 ? AMBER : RED;
    const FO_NOTES = {
      'underlying':    `${underlying} lot size is ${lot_size} — meaning 1 lot = exposure to ${lot_size} units of the index. You can't buy partial lots. For ${underlying}, minimum trade = 1 lot × ${lot_size} = ₹${(premium * lot_size).toLocaleString('en-IN')} premium at current price.`,
      'instrument':    `Options: you pay premium upfront (₹${premium}/unit), max loss = premium paid (₹${(premium * lot_size * lots).toLocaleString('en-IN')}). Futures: no premium, but full margin required and unlimited loss. For learning, always start with Options BUY — your max loss is capped.`,
      'direction':     `BUY means you're paying premium — your max loss is fixed at ₹${max_loss.toLocaleString('en-IN')} (premium paid). SELL means you're collecting premium but have theoretically unlimited loss and requires writing margin. Never sell options without fully understanding the risk.`,
      'option-type':   `CE (Call Option): profits when ${underlying} goes UP above the strike (${strike}). PE (Put Option): profits when ${underlying} goes DOWN below the strike. You're buying a ${option_type} here — you need ${underlying} to move ${option_type === 'CE' ? 'above' : 'below'} ${strike} before expiry.`,
      'strike-expiry': `Strike ${strike} ${option_type} expiring ${expiry}. The strike you pick determines how much ${underlying} needs to move for you to profit. ATM (at-the-money) strikes cost more but have the highest delta. OTM strikes are cheaper but expire worthless more often.`,
      'entry-prem':    `Entry premium ₹${premium}/unit. You pay this upfront. Total premium paid = ₹${premium} × ${lot_size} × ${lots} lot = ₹${(premium * lot_size * lots).toLocaleString('en-IN')}. This is your maximum possible loss on this trade — fully capped.`,
      'sl-prem':       `SL at ₹${sl_premium} premium. If the option price drops to ₹${sl_premium}, exit. Max loss if SL hit = (₹${premium} − ₹${sl_premium}) × ${lot_size} × ${lots} = ₹${max_loss.toLocaleString('en-IN')}. A premium-based SL is more reliable than a price-level SL for options.`,
      'tgt-prem':      `Target at ₹${target_premium} premium. Max profit if target hit = (₹${target_premium} − ₹${premium}) × ${lot_size} × ${lots} = ₹${max_profit.toLocaleString('en-IN')}. Consider partial exits at 50% of target — options decay fast and don't always reach full target.`,
      'fo-rr':         `R:R = ₹${max_profit.toLocaleString('en-IN')} profit ÷ ₹${max_loss.toLocaleString('en-IN')} risk = 1:${rr}. ${parseFloat(rr) >= 2 ? 'Good — the trade pays well for the risk.' : 'Below 1:2 — consider adjusting your target or picking a different strike to improve this ratio.'}`,
    };
    return (
      <Card>
        {/* Slot header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 11px', borderRadius: 9, background: inputBg, border: `1px solid ${border}`, marginBottom: 10 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: text }}>Trade 1</span>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: `${PURPLE}20`, color: PURPLE }}>F&O</span>
            </div>
            <div style={{ fontSize: 10, color: sub, marginTop: 2 }}>{lots} lot · ₹{margin.toLocaleString('en-IN')} margin</div>
          </div>
          <div style={{ fontSize: 11, fontWeight: 800, color: GREEN }}>+₹{max_profit.toLocaleString('en-IN')}</div>
        </div>
        <HintLabel text="tap any field to understand what it means in F&O" />
        {/* Underlying + type toggles — tappable */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: selectedField === 'underlying' || selectedField === 'instrument' ? 0 : 10 }}>
          <div onClick={() => setSelectedField(f => f === 'underlying' ? null : 'underlying')}
            style={{ padding: '8px 10px', borderRadius: 9, background: selectedField === 'underlying' ? `${INDIGO}18` : inputBg, border: `1.5px solid ${selectedField === 'underlying' ? INDIGO : border}`, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
            <div style={{ fontSize: 10, color: sub, marginBottom: 3 }}>Underlying</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: selectedField === 'underlying' ? INDIGO : text }}>{underlying} <span style={{ fontSize: 10, color: sub }}>lot: {lot_size}</span></div>
          </div>
          <div onClick={() => setSelectedField(f => f === 'instrument' ? null : 'instrument')}
            style={{ padding: '8px 10px', borderRadius: 9, background: selectedField === 'instrument' ? `${INDIGO}18` : inputBg, border: `1.5px solid ${selectedField === 'instrument' ? INDIGO : border}`, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
            <div style={{ fontSize: 10, color: sub, marginBottom: 3 }}>Type</div>
            <div style={{ display: 'flex', gap: 5 }}>
              {['Options', 'Futures'].map(t => (
                <span key={t} style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: t === instrument ? `${INDIGO}20` : 'transparent', color: t === instrument ? INDIGO : sub, border: `1px solid ${t === instrument ? INDIGO : 'transparent'}` }}>{t}</span>
              ))}
            </div>
          </div>
        </div>
        {(selectedField === 'underlying' || selectedField === 'instrument') && (
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 10, lineHeight: 1.55, padding: '7px 10px', background: `${INDIGO}10`, borderRadius: 8, border: `1px solid ${INDIGO}25` }}>{FO_NOTES[selectedField]}</div>
        )}
        {/* Direction + Option type — tappable */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: selectedField === 'direction' || selectedField === 'option-type' ? 0 : 10 }}>
          <div>
            <div style={{ fontSize: 10, color: sub, marginBottom: 4 }}>Direction (tap)</div>
            <div style={{ display: 'flex', gap: 5 }}>
              {['BUY', 'SELL'].map(d => (
                <span key={d} onClick={() => setSelectedField(f => f === 'direction' ? null : 'direction')}
                  style={{ flex: 1, textAlign: 'center', fontSize: 12, fontWeight: 700, padding: '5px 0', borderRadius: 7, background: d === direction ? `${dirColor}20` : inputBg, color: d === direction ? dirColor : sub, border: `1.5px solid ${d === direction ? dirColor : border}`, cursor: 'pointer', userSelect: 'none' }}>{d}</span>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: sub, marginBottom: 4 }}>Option Type (tap)</div>
            <div style={{ display: 'flex', gap: 5 }}>
              {['CE', 'PE'].map(o => (
                <span key={o} onClick={() => setSelectedField(f => f === 'option-type' ? null : 'option-type')}
                  style={{ flex: 1, textAlign: 'center', fontSize: 12, fontWeight: 700, padding: '5px 0', borderRadius: 7, background: o === option_type ? `${optColor}20` : inputBg, color: o === option_type ? optColor : sub, border: `1.5px solid ${o === option_type ? optColor : border}`, cursor: 'pointer', userSelect: 'none' }}>{o}</span>
              ))}
            </div>
          </div>
        </div>
        {(selectedField === 'direction' || selectedField === 'option-type') && (
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 10, lineHeight: 1.55, padding: '7px 10px', background: `${INDIGO}10`, borderRadius: 8, border: `1px solid ${INDIGO}25` }}>{FO_NOTES[selectedField]}</div>
        )}
        {/* Strike + Expiry — tappable */}
        <div onClick={() => setSelectedField(f => f === 'strike-expiry' ? null : 'strike-expiry')}
          style={{ padding: '9px 11px', borderRadius: 9, background: selectedField === 'strike-expiry' ? `${INDIGO}18` : inputBg, border: `1.5px solid ${selectedField === 'strike-expiry' ? INDIGO : border}`, marginBottom: selectedField === 'strike-expiry' ? 0 : 10, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
            <span style={{ fontSize: 11, color: sub }}>Strike</span>
            <span style={{ fontSize: 11, color: sub }}>Expiry</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: selectedField === 'strike-expiry' ? INDIGO : text }}>{underlying} {strike} {option_type}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: INDIGO }}>{expiry}</span>
          </div>
        </div>
        {selectedField === 'strike-expiry' && (
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 10, lineHeight: 1.55, padding: '7px 10px', background: `${INDIGO}10`, borderRadius: 8, border: `1px solid ${INDIGO}25` }}>{FO_NOTES['strike-expiry']}</div>
        )}
        {/* Premium / SL / Target — tappable */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 10 }}>
          {[
            { fkey: 'entry-prem', label: 'Entry Premium', value: `₹${premium}`,        color: text  },
            { fkey: 'sl-prem',    label: 'SL Premium',    value: `₹${sl_premium}`,      color: RED   },
            { fkey: 'tgt-prem',   label: 'Target',        value: `₹${target_premium}`,  color: GREEN },
          ].map(({ fkey, label, value, color }) => {
            const isSel = selectedField === fkey;
            return (
              <div key={fkey} onClick={() => setSelectedField(f => f === fkey ? null : fkey)}
                style={{ padding: '7px 8px', borderRadius: 8, background: isSel ? `${color}18` : inputBg, border: `1.5px solid ${isSel ? color : border}`, textAlign: 'center', cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                <div style={{ fontSize: 9, color: sub, marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: 13, fontWeight: 800, color }}>{value}</div>
                {isSel && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 5, lineHeight: 1.5, textAlign: 'left' }}>{FO_NOTES[fkey]}</div>}
              </div>
            );
          })}
        </div>
        {/* Breakdown — each row tappable */}
        <div style={{ borderRadius: 9, background: inputBg, padding: '9px 11px' }}>
          {[
            { key: 'fo-units',  label: `${lots} lot × ${lot_size}`,  value: `${lots * lot_size} units`,             color: text,    note: `1 lot of ${underlying} = ${lot_size} units. You have ${lots} lot here = ${lots * lot_size} total units. All P&L calculations multiply by ${lots * lot_size}.` },
            { key: 'fo-margin', label: 'Margin required',             value: `₹${margin.toLocaleString('en-IN')}`,  color: text,    note: `This is the collateral blocked by your broker for this F&O position. It is not a cost — it's your own money set aside. It is returned when you close the trade.` },
            { key: 'fo-loss',   label: 'Max loss (SL hit)',           value: `-₹${max_loss.toLocaleString('en-IN')}`, color: RED,   note: `If premium drops from ₹${premium} to ₹${sl_premium} (your SL), loss = ₹${premium - sl_premium} × ${lots * lot_size} = ₹${max_loss.toLocaleString('en-IN')}. This is your capped loss — exit at SL without hesitation.` },
            { key: 'fo-profit', label: 'Max profit (target hit)',     value: `+₹${max_profit.toLocaleString('en-IN')}`, color: GREEN, note: `If premium rises from ₹${premium} to ₹${target_premium} (your target), profit = ₹${target_premium - premium} × ${lots * lot_size} = ₹${max_profit.toLocaleString('en-IN')}. Consider booking 50% at halfway — options can reverse fast.` },
            { key: 'fo-rr',     label: 'R:R',                        value: `1:${rr}`,                              color: rrColor, note: FO_NOTES['fo-rr'] },
          ].map(({ key, label, value, color, note }) => {
            const isSel = selectedField === key;
            return (
              <div key={key} onClick={() => setSelectedField(f => f === key ? null : key)}
                style={{ paddingBottom: 6, marginBottom: 6, borderBottom: `1px solid ${border}30`, cursor: 'pointer', userSelect: 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: isSel ? color : sub }}>{label}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color }}>{value}</span>
                </div>
                {isSel && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 5, lineHeight: 1.5 }}>{note}</div>}
              </div>
            );
          })}
        </div>
      </Card>
    );
  }

  // --- CALCULATOR SUMMARY — mirrors PositionSizeCalculator Plan Summary card ---
  if (ui.type === 'calculator-summary') {
    const { num_trades = 3, num_filled = 3, best_case = 7200, worst_case = 3000, capital = 150000, total_deployed = 90000, total_max_loss = 3000, total_max_profit = 7500, brokerage = 420, rr = 2.4 } = ui;
    const bestPct  = ((best_case  / capital) * 100).toFixed(1);
    const worstPct = ((worst_case / capital) * 100).toFixed(1);
    const rrColor  = rr >= 2 ? GREEN : rr >= 1.5 ? AMBER : RED;
    const rrLabel  = rr >= 2 ? 'Good' : rr >= 1.5 ? 'OK' : 'Poor';
    const SUM_NOTES = {
      'best-case':     `Best case = all ${num_filled} trades hit their targets after brokerage = +₹${best_case.toLocaleString('en-IN')} (+${bestPct}% of account). This is your plan upside. Before trading, ask: is this realistic given today's market conditions and my setup quality?`,
      'worst-case':    `Worst case = all ${num_filled} trades hit their stop losses after brokerage = −₹${worst_case.toLocaleString('en-IN')} (−${worstPct}% of account). This is your maximum damage today. If you cannot absorb −₹${worst_case.toLocaleString('en-IN')} without panic or revenge trading — reduce your risk % or number of trades.`,
      'plan-rr':       `Plan R:R = total potential profit ÷ total potential loss = ₹${total_max_profit.toLocaleString('en-IN')} ÷ ₹${total_max_loss.toLocaleString('en-IN')} = 1:${rr}. ${rrLabel === 'Good' ? 'This plan makes mathematical sense.' : 'Consider reviewing individual trade R:Rs — at least one slot may have a weak setup dragging the plan average down.'}`,
      'deployed':      `Total capital deployed across ${num_filled} trade slots = ₹${total_deployed.toLocaleString('en-IN')} (${((total_deployed / capital) * 100).toFixed(0)}% of your account). Capital not deployed stays idle — this is fine. Never force trades just to use all capital.`,
      'max-loss':      `Gross max loss before charges = ₹${total_max_loss.toLocaleString('en-IN')}. This is the sum of all individual slot stop losses. This number must be something you can absorb without changing your behaviour — if it causes fear or stress now just looking at it, it's too high.`,
      'max-profit':    `Gross max profit before charges = ₹${total_max_profit.toLocaleString('en-IN')}. Net profit after brokerage = ₹${best_case.toLocaleString('en-IN')}. The gap (₹${(total_max_profit - best_case).toLocaleString('en-IN')}) is brokerage + taxes — this is a real cost on every trade.`,
      'brokerage':     `Estimated brokerage + STT + exchange charges for ${num_filled} trades ≈ ₹${brokerage.toLocaleString('en-IN')}. This reduces your net P&L directly. Frequent trading with small targets gets eaten by brokerage — always check that your target covers these costs before entering.`,
    };
    return (
      <Card>
        {/* Summary header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 11px', background: `${INDIGO}10`, borderRadius: 9, marginBottom: 8, border: `1px solid ${border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, color: INDIGO }}>⚡</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: text }}>Plan Summary</span>
          </div>
          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: `${INDIGO}15`, color: INDIGO }}>{num_filled}/{num_trades} trades planned</span>
        </div>
        <HintLabel text="tap best case, worst case, or any stat to understand it" />
        {/* Best / Worst split — each cell tappable */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, border: `1px solid ${selectedField === 'best-case' || selectedField === 'worst-case' ? INDIGO : border}`, borderRadius: 9, overflow: 'hidden', marginBottom: 0 }}>
          <div onClick={() => setSelectedField(f => f === 'best-case' ? null : 'best-case')}
            style={{ padding: '14px 10px', textAlign: 'center', background: selectedField === 'best-case' ? '#16C78428' : 'linear-gradient(135deg, #16C78418 0%, #16C78405 100%)', borderRight: `1px solid ${border}`, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: GREEN, marginBottom: 4 }}>▲ BEST CASE</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: GREEN }}>+₹{best_case.toLocaleString('en-IN')}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: GREEN, marginTop: 2 }}>+{bestPct}%</div>
            <div style={{ fontSize: 9, color: sub, marginTop: 2 }}>all targets hit</div>
          </div>
          <div onClick={() => setSelectedField(f => f === 'worst-case' ? null : 'worst-case')}
            style={{ padding: '14px 10px', textAlign: 'center', background: selectedField === 'worst-case' ? '#EF444428' : 'linear-gradient(135deg, #EF444418 0%, #EF444405 100%)', cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: RED, marginBottom: 4 }}>▼ WORST CASE</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: RED }}>−₹{worst_case.toLocaleString('en-IN')}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: RED, marginTop: 2 }}>−{worstPct}%</div>
            <div style={{ fontSize: 9, color: sub, marginTop: 2 }}>all SLs hit</div>
          </div>
        </div>
        {(selectedField === 'best-case' || selectedField === 'worst-case') && (
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', margin: '8px 0', lineHeight: 1.55, padding: '8px 10px', background: selectedField === 'best-case' ? `${GREEN}10` : `${RED}10`, borderRadius: 8, border: `1px solid ${selectedField === 'best-case' ? `${GREEN}30` : `${RED}30`}` }}>
            {SUM_NOTES[selectedField]}
          </div>
        )}
        {/* R:R bar — tappable */}
        <div onClick={() => setSelectedField(f => f === 'plan-rr' ? null : 'plan-rr')}
          style={{ padding: '10px 11px', borderRadius: 9, background: selectedField === 'plan-rr' ? `${rrColor}12` : `${INDIGO}08`, border: `1.5px solid ${selectedField === 'plan-rr' ? rrColor : border}`, marginTop: 8, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: selectedField === 'plan-rr' ? rrColor : sub }}>Plan R:R</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: rrColor }}>1:{rr} · {rrLabel}</span>
          </div>
          <div style={{ height: 8, borderRadius: 4, background: inputBg }}>
            <div style={{ height: '100%', borderRadius: 4, width: `${Math.min(100, (best_case / (best_case + worst_case)) * 100)}%`, background: 'linear-gradient(90deg, #16C784, #6366F1)' }} />
          </div>
          {selectedField === 'plan-rr' && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 7, lineHeight: 1.5 }}>{SUM_NOTES['plan-rr']}</div>}
        </div>
        {/* Stat list — each row tappable */}
        <div style={{ padding: '10px 11px', borderRadius: 9, background: `${INDIGO}06`, border: `1px solid ${border}`, marginTop: 8 }}>
          {[
            { key: 'deployed',   label: 'Total capital deployed',              value: `₹${total_deployed.toLocaleString('en-IN')}`,   color: text  },
            { key: 'max-loss',   label: 'Total max loss (all SLs hit)',         value: `−₹${total_max_loss.toLocaleString('en-IN')}`,  color: RED   },
            { key: 'max-profit', label: 'Total max profit (all targets)',       value: `+₹${total_max_profit.toLocaleString('en-IN')}`, color: GREEN },
            { key: 'brokerage',  label: `Brokerage + taxes (${num_filled} trades)`, value: `~₹${brokerage.toLocaleString('en-IN')}`, color: sub   },
          ].map(({ key, label, value, color }) => {
            const isSel = selectedField === key;
            return (
              <div key={key} onClick={() => setSelectedField(f => f === key ? null : key)}
                style={{ marginBottom: 6, padding: '4px 6px', borderRadius: 7, background: isSel ? `${color === sub ? INDIGO : color}10` : 'transparent', cursor: 'pointer', userSelect: 'none', transition: 'all 0.15s' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: isSel ? (color === sub ? INDIGO : color) : sub }}>{label}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color }}>{value}</span>
                </div>
                {isSel && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 5, lineHeight: 1.5 }}>{SUM_NOTES[key]}</div>}
              </div>
            );
          })}
        </div>
      </Card>
    );
  }

  // --- RISK PROFILE CARD — mirrors Risk Profile section (profile type + position sizing recommendations) ---
  if (ui.type === 'risk-profile-card') {
    const profileTypes = [
      { type: 'Very Conservative', short: 'V.Cons',  pct: 0.5,  trades: 1, color: GREEN  },
      { type: 'Conservative',      short: 'Cons',    pct: 1.0,  trades: 2, color: INDIGO },
      { type: 'Moderate',          short: 'Mod',     pct: 1.5,  trades: 3, color: INDIGO },
      { type: 'Aggressive',        short: 'Aggr',    pct: 2.0,  trades: 5, color: AMBER  },
      { type: 'Very Aggressive',   short: 'V.Aggr',  pct: 2.5,  trades: 8, color: RED    },
    ];
    const active = ui.active || 'Moderate';
    const limits = ui.limits || [];
    return (
      <Card>
        <HintLabel text="tap a profile type to see its recommended limits" />
        <div style={{ fontSize: 9, color: sub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Risk Profile Type</div>
        <div style={{ display: 'flex', gap: 4, marginBottom: 12, overflowX: 'auto', paddingBottom: 3 }}>
          {profileTypes.map(p => {
            const isActive = p.type === active || selCond === p.type;
            return (
              <div key={p.type} onClick={() => setSelCond(pp => pp === p.type ? null : p.type)}
                style={{ padding: '7px 10px', borderRadius: 9, flexShrink: 0, background: isActive ? `${p.color}20` : inputBg, border: `1.5px solid ${isActive ? p.color : border}`, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s', textAlign: 'center' }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: isActive ? p.color : sub }}>{p.short}</div>
                <div style={{ fontSize: 9, color: isActive ? `${p.color}aa` : 'rgba(255,255,255,0.2)' }}>{p.pct}%</div>
              </div>
            );
          })}
        </div>
        {limits.length > 0 && (
          <>
            <div style={{ fontSize: 9, color: sub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 7 }}>Your Recommended Limits</div>
            {limits.map(l => {
              const isSel = selectedField === l.label;
              const c = l.color || INDIGO;
              return (
                <div key={l.label} onClick={() => setSelectedField(f => f === l.label ? null : f)}
                  style={{ borderRadius: 9, padding: '9px 12px', background: isSel ? `${c}15` : inputBg, border: `1.5px solid ${isSel ? c : border}`, marginBottom: 6, cursor: 'pointer', userSelect: 'none', transition: 'all 0.18s' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isSel ? 6 : 0 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: text }}>{l.label}</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: c }}>{l.value}</span>
                  </div>
                  {isSel && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>{l.detail}</div>}
                </div>
              );
            })}
          </>
        )}
      </Card>
    );
  }

  return null;
}

// ─── Step Sheet ───────────────────────────────────────────────────────────────
function GuideSheet({ node, onClose, navigate }) {
  const { colors } = useTheme();
  const [stepIndex, setStepIndex] = useState(0);
  const swipeRef   = useRef({ startX: 0, startY: 0, swiping: false });
  const steps      = node.steps;
  const totalSteps = steps.length;

  useEffect(() => { setStepIndex(0); }, [node.id]);

  // Pointer-based swipe — one step at a time, no scroll momentum
  const onPointerDown = useCallback((e) => {
    swipeRef.current = { startX: e.clientX, startY: e.clientY, swiping: true };
  }, []);

  const onPointerUp = useCallback((e) => {
    if (!swipeRef.current.swiping) return;
    swipeRef.current.swiping = false;
    const dx = e.clientX - swipeRef.current.startX;
    const dy = e.clientY - swipeRef.current.startY;
    // Only trigger if horizontal swipe is dominant and exceeds threshold
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) setStepIndex(i => Math.min(i + 1, totalSteps - 1));
    else        setStepIndex(i => Math.max(i - 1, 0));
  }, [totalSteps]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        background: 'rgba(0,0,0,0.55)',
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 480,
          height: '72vh',
          borderRadius: '24px 24px 0 0',
          background: colors.cardBg || '#13141a',
          border: `1px solid ${node.color}30`,
          borderBottom: 'none',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          animation: 'slideUp 0.35s cubic-bezier(0.32,0.72,0,1)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px 12px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
            background: `${node.color}22`, border: `1.5px solid ${node.color}55`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontSize: 8, fontWeight: 800, color: node.color, whiteSpace: 'pre', textAlign: 'center', lineHeight: 1.2 }}>
              {node.label}
            </span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 13, fontWeight: 800, color: '#eceef5', margin: 0, lineHeight: 1.2 }}>
              {node.label.replace('\n', ' ')}
            </p>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', margin: '2px 0 0' }}>
              Step {stepIndex + 1} of {totalSteps}
            </p>
          </div>
          <button onClick={onClose} style={{
            width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
            background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'rgba(255,255,255,0.5)',
          }}>
            <X size={16} />
          </button>
        </div>

        {/* Progress dots */}
        <div style={{
          display: 'flex', gap: 5, justifyContent: 'center',
          padding: '10px 20px 0', flexShrink: 0,
        }}>
          {steps.map((_, i) => (
            <button key={i} onClick={() => setStepIndex(i)} style={{
              width: i === stepIndex ? 20 : 6, height: 6,
              borderRadius: 3, border: 'none',
              background: i === stepIndex ? node.color : `${node.color}35`,
              transition: 'all 0.25s ease',
              cursor: 'pointer', padding: 0, flexShrink: 0,
            }} />
          ))}
        </div>

        {/* Step content — controlled swipe, no scroll */}
        <div
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          style={{
            flex: 1, position: 'relative',
            overflow: 'hidden', touchAction: 'pan-y',
            userSelect: 'none',
          }}
        >
          {steps.map((s, i) => {
            const StepIcon   = ICON_MAP[s.icon] || CheckCircle;
            const isLastStep = i === totalSteps - 1;
            const isFirst    = i === 0;
            const offset     = (i - stepIndex) * 100;
            return (
              <div key={i} style={{
                position: 'absolute', inset: 0,
                transform: `translateX(${offset}%)`,
                transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
                overflowY: 'auto', overflowX: 'hidden',
              }}>
              <div style={{
                padding: s.ui ? '18px 20px 20px' : '16px 24px 24px',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center',
                justifyContent: s.ui ? 'flex-start' : 'center',
                gap: s.ui ? 10 : 16,
                minHeight: '100%',
              }}>
                {/* Interactive banner — only on steps with UI */}
                {s.ui && (
                  <div style={{
                    width: '100%',
                    borderRadius: 10,
                    overflow: 'hidden',
                    position: 'relative',
                    padding: '8px 14px',
                    background: `linear-gradient(90deg, ${node.color}22 0%, ${node.color}08 60%, transparent 100%)`,
                    border: `1px solid ${node.color}30`,
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    {/* animated shimmer line on left edge */}
                    <div style={{
                      position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
                      background: `linear-gradient(180deg, transparent, ${node.color}, transparent)`,
                      animation: 'shimmerLine 2s ease-in-out infinite',
                    }} />
                    <div style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: node.color,
                      boxShadow: `0 0 6px ${node.color}`,
                      animation: 'pulse 2s infinite', flexShrink: 0,
                    }} />
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 800, color: node.color, letterSpacing: '0.03em' }}>Interactive</div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 1 }}>Tap the options below to explore and learn</div>
                    </div>
                    <div style={{ marginLeft: 'auto', fontSize: 13, opacity: 0.4 }}>✦</div>
                  </div>
                )}

                {s.ui ? (
                  <div style={{ width: '100%', flexShrink: 0 }}>
                    <StepUI ui={s.ui} color={node.color} />
                  </div>
                ) : (
                  <div style={{
                    width: 72, height: 72, borderRadius: '50%',
                    background: `${node.color}18`, border: `2px solid ${node.color}40`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <StepIcon size={30} color={node.color} />
                  </div>
                )}

                <div style={{ textAlign: 'center', maxWidth: 340 }}>
                  <h3 style={{
                    fontSize: s.ui ? 15 : 18, fontWeight: 900, color: '#eceef5',
                    letterSpacing: '-0.02em', lineHeight: 1.25, marginBottom: 6,
                  }}>
                    {s.title}
                  </h3>
                  <p style={{
                    fontSize: s.ui ? 12 : 14, color: 'rgba(255,255,255,0.55)',
                    lineHeight: 1.6, whiteSpace: 'pre-line',
                  }}>
                    {s.body}
                  </p>
                </div>

                {/* Back + Next / CTA */}
                <div style={{ width: '100%', maxWidth: 320, marginTop: 4, display: 'flex', gap: 8 }}>
                  {!isFirst && (
                    <button
                      onClick={() => setStepIndex(i - 1)}
                      style={{
                        flex: '0 0 auto', padding: '12px 16px', borderRadius: 14,
                        background: 'rgba(255,255,255,0.05)', border: '1.5px solid rgba(255,255,255,0.1)',
                        fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.45)',
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                      }}
                    >
                      <ChevronLeft size={16} /> Back
                    </button>
                  )}
                  {isLastStep && s.cta ? (
                    <button
                      onClick={() => { onClose(); setTimeout(() => navigate(s.cta.path), 250); }}
                      style={{
                        flex: 1, padding: '13px', borderRadius: 14,
                        background: `linear-gradient(135deg, ${node.color} 0%, ${node.color}cc 100%)`,
                        border: 'none', fontSize: 15, fontWeight: 800, color: '#fff',
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      }}
                    >
                      {s.cta.label}
                      <ChevronRight size={18} />
                    </button>
                  ) : !isLastStep ? (
                    <button
                      onClick={() => setStepIndex(i + 1)}
                      style={{
                        flex: 1, padding: '12px', borderRadius: 14,
                        background: `${node.color}18`, border: `1.5px solid ${node.color}40`,
                        fontSize: 14, fontWeight: 700, color: node.color,
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      }}
                    >
                      Next <ChevronRight size={16} />
                    </button>
                  ) : null}
                </div>
              </div>
            </div>);
          })}
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
        @keyframes shimmerLine {
          0%   { opacity: 0.2; transform: scaleY(0.6); }
          50%  { opacity: 1;   transform: scaleY(1); }
          100% { opacity: 0.2; transform: scaleY(0.6); }
        }
        div::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}

// ─── Zone filter chips ────────────────────────────────────────────────────────
function ZoneChips({ activeZone, onZone }) {
  return (
    <div style={{
      display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center',
      padding: '0 20px', marginBottom: 12,
    }}>
      <button
        onClick={() => onZone(null)}
        style={{
          padding: '6px 14px', borderRadius: 20,
          background: activeZone === null ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.04)',
          border: `1.5px solid ${activeZone === null ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)'}`,
          fontSize: 11, fontWeight: 700,
          color: activeZone === null ? '#eceef5' : 'rgba(255,255,255,0.35)',
          cursor: 'pointer', transition: 'all 0.2s',
          boxShadow: activeZone === null ? '0 0 12px rgba(255,255,255,0.08)' : 'none',
        }}
      >
        All
      </button>
      {GUIDE_ZONES.map(z => (
        <button key={z.id} onClick={() => onZone(z.id === activeZone ? null : z.id)} style={{
          padding: '6px 14px', borderRadius: 20,
          background: activeZone === z.id ? `${z.color}20` : 'rgba(255,255,255,0.03)',
          border: `1.5px solid ${activeZone === z.id ? z.color : `${z.color}28`}`,
          fontSize: 11, fontWeight: 700,
          color: activeZone === z.id ? z.color : 'rgba(255,255,255,0.35)',
          cursor: 'pointer', transition: 'all 0.2s',
          boxShadow: activeZone === z.id ? `0 0 14px ${z.color}30` : 'none',
        }}>
          {z.label}
        </button>
      ))}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function GuidePage() {
  const { colors } = useTheme();
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();

  const deepNode = searchParams.get('node');
  const [activeNodeId, setActiveNodeId] = useState(deepNode || null);
  const [activeZone, setActiveZone]     = useState(null);

  const activeNode    = GUIDE_NODES.find(n => n.id === activeNodeId) || null;
  const filteredNodes = activeZone
    ? GUIDE_NODES.filter(n => n.zone === activeZone)
    : GUIDE_NODES;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center',
      paddingTop: 20, paddingBottom: 40,
    }}>
      {/* Page header */}
      <div style={{ width: '100%', maxWidth: 480, padding: '0 20px 16px', textAlign: 'center' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'linear-gradient(135deg, rgba(99,102,241,0.18), rgba(139,92,246,0.12))',
          border: '1px solid rgba(99,102,241,0.35)',
          borderRadius: 20, padding: '5px 14px', marginBottom: 14,
          boxShadow: '0 0 20px rgba(99,102,241,0.15)',
        }}>
          <Map size={11} color="#818cf8" />
          <span style={{ fontSize: 10, fontWeight: 800, color: '#818cf8', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            App Guide
          </span>
        </div>
        <h1 style={{
          fontSize: 26, fontWeight: 900,
          background: 'linear-gradient(135deg, #ffffff 30%, #a5b4fc 70%, #c4b5fd 100%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          letterSpacing: '-0.03em', lineHeight: 1.2, marginBottom: 8,
        }}>
          How to do anything<br />in TradeReflex
        </h1>
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.38)', lineHeight: 1.5, letterSpacing: '0.02em' }}>
          Rotate the orb · tap a node · follow the guide
        </p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 12 }}>
          {[
            { val: GUIDE_NODES.length, label: 'features' },
            { val: GUIDE_ZONES.length, label: 'categories' },
          ].map(({ val, label }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
              <span style={{ fontSize: 18, fontWeight: 800, color: '#a5b4fc', fontVariantNumeric: 'tabular-nums' }}>{val}</span>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Orb */}
      <div style={{ perspective: 1100, perspectiveOrigin: '50% 50%', marginBottom: 16, position: 'relative' }}>
        {/* Background glow bloom */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 420, height: 420, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, rgba(139,92,246,0.06) 40%, transparent 70%)',
          pointerEvents: 'none', zIndex: 0,
        }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <GuideOrb
            onSelect={id => setActiveNodeId(id)}
            activeNodeId={activeNodeId}
          />
        </div>
      </div>

      {/* Divider + filter label */}
      <div style={{ width: '100%', maxWidth: 480, padding: '0 20px', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, height: 1, background: 'linear-gradient(to right, transparent, rgba(99,102,241,0.2), transparent)' }} />
          <span style={{ fontSize: 9, fontWeight: 800, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.1em', textTransform: 'uppercase', flexShrink: 0 }}>Browse by category</span>
          <div style={{ flex: 1, height: 1, background: 'linear-gradient(to left, transparent, rgba(99,102,241,0.2), transparent)' }} />
        </div>
      </div>

      {/* Zone filter */}
      <ZoneChips activeZone={activeZone} onZone={setActiveZone} />

      {/* Node chip list */}
      <div style={{
        width: '100%', maxWidth: 480, padding: '0 20px',
        display: 'flex', flexWrap: 'wrap', gap: 7, justifyContent: 'center',
        marginBottom: 16,
      }}>
        {filteredNodes.map(node => {
          const isActive = activeNodeId === node.id;
          return (
            <button key={node.id} onClick={() => setActiveNodeId(node.id)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '7px 13px', borderRadius: 20,
              background: isActive ? `${node.color}22` : 'rgba(255,255,255,0.03)',
              border: `1.5px solid ${isActive ? node.color : `${node.color}30`}`,
              fontSize: 11, fontWeight: isActive ? 700 : 600,
              color: isActive ? node.color : 'rgba(255,255,255,0.45)',
              cursor: 'pointer', transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
              boxShadow: isActive ? `0 0 16px ${node.color}35, inset 0 0 8px ${node.color}10` : 'none',
            }}>
              <span style={{
                width: 5, height: 5, borderRadius: '50%',
                background: isActive ? node.color : `${node.color}60`,
                flexShrink: 0, transition: 'all 0.2s',
              }} />
              {node.label.replace('\n', ' ')}
            </button>
          );
        })}
      </div>

      {/* Step sheet */}
      {activeNode && (
        <GuideSheet
          node={activeNode}
          onClose={() => setActiveNodeId(null)}
          navigate={navigate}
        />
      )}
    </div>
  );
}
